import React, { useState, useEffect } from "react";
import { auth, provider } from "./firebase";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import {
  saveIncome, saveExpense, updateRecord, deleteRecord,
  subscribeRecords, saveFavorites, getFavorites,
  subscribeNotices, addNotice, updateNotice, deleteNotice,
  addInventoryItem, updateInventoryItem, deleteInventoryItem, subscribeInventory,
  addInquiry, subscribeMyInquiries, subscribeAllInquiries, replyInquiry, deleteInquiry,
  toggleNoticeLike, subscribeNoticeLikes,
  savePresets, subscribePresets,
} from "./db";
import { ARCANE_AREAS, AUTHENTIC_AREAS, GRAND_AUTHENTIC_AREAS, ARCANE_LEVEL_DATA, AUTHENTIC_LEVEL_DATA, GRAND_AUTHENTIC_LEVEL_DATA, calcSymbolCost } from "./symbolData";
import { emptyPresets, presetToBossState, bossStateToPreset } from "./presetData";
import { getBossData, isPatchTransitionDate, getPatchTransitionInfo } from "./bossData";
import { StatsHub } from "./MonthlyReport";
import CharAvatar from "./CharAvatar";
import DataManager from "./DataManager";
import { DROP_ITEMS } from "./dropData";
import { getBossDrops } from "./bossDropData";
import { RING_BOX_DATA, makeRingRecord } from "./ringBoxData";
import { EPIC_DUNGEONS, calcEpicReward, SERAJAR_PRICE } from "./epicData";
import { getNotices, getNoticeById } from "./noticeData";
import { NOTICE_CATEGORIES, getCategoryInfo, cleanTitle, sortNotices } from "./noticeManager";
import { DONATION_INFO } from "./donationData";

const SOL_PRICE  = 7000000;
const MKT_DEPOSIT = 2000; // 경매장 등록 보증금 (등록 1회당 고정)
const calcNetPrice = (price, qty, fee) => Math.floor((+price||0) * (1 - (fee||0)/100)) * (+qty||1);

// ── 캐릭터별 A/B 유형 고려한 수익 계산 ──
function calcIncome(r, favorites) {
  if (!r || r.kind !== "income") return 0;
  const fav = favorites?.find(f => f.nickname === r.nickname);

  let inc = r.totalIncome || 0;

  // 솔에르다 B유형 체크
  const solType      = fav?.solType || "A";
  const solApplyDate = fav?.solApplyDate || "";
  if (solType === "B" && (!solApplyDate || r.date >= solApplyDate)) {
    inc -= (r.totalSol || 0);
  }

  // 물욕템 B유형 체크
  const dropType      = fav?.dropType || "A";
  const dropApplyDate = fav?.dropApplyDate || "";
  if (dropType === "B" && r.type === "drop" && (!dropApplyDate || r.date >= dropApplyDate)) {
    inc -= ((r.meso || 0) + (r.etc || 0));
  }

  return Math.max(0, inc);
}

// 솔에르다만 추출 (통계 유형별용)
function calcSolIncome(r, favorites) {
  if (!r || r.kind !== "income") return 0;
  const fav = favorites?.find(f => f.nickname === r.nickname);
  const solType      = fav?.solType || "A";
  const solApplyDate = fav?.solApplyDate || "";
  if (solType === "B" && (!solApplyDate || r.date >= solApplyDate)) return 0;
  return r.totalSol || 0;
}
const FUNCTION_URL = "https://getcharacterinfo-bw4yxfnaqq-uc.a.run.app";
const DETAIL_URL   = "https://getcharacterdetail-bw4yxfnaqq-uc.a.run.app";

const ADMIN_UID    = "0F3miHlRNMhz7fF4ZTHlfm9JXbJ3";

// 보스명 기준 그룹화 (난이도 선택 UI용)
// ── 보스 데이터: bossData.js에서 관리 ──
// getBossData(date) → 날짜 기준 보스 그룹 반환
// BOSS_PRICE_HISTORY → 가격 변경 이력

// 현재 날짜 기준 기본 BOSS_GROUPS (UI 렌더링용)
const BOSS_GROUPS = getBossData(today());

// 기존 호환용 BOSSES (조회/수정 탭 등에서 보스명 매칭용)
const BOSSES = {
  daily:   BOSS_GROUPS.daily.flatMap(b=>b.diffs.map(d=>({name:`${b.name} (${d.diff})`,crystal:d.crystal}))),
  weekly:  BOSS_GROUPS.weekly.flatMap(b=>b.diffs.map(d=>({name:`${b.name} (${d.diff})`,crystal:d.crystal}))),
  monthly: BOSS_GROUPS.monthly.flatMap(b=>b.diffs.map(d=>({name:`${b.name} (${d.diff})`,crystal:d.crystal}))),
};

function fmt(v) {
  v = Math.round(v);
  const neg = v < 0;
  const abs = Math.abs(v);
  if (abs === 0) return "0메소";
  const ok  = Math.floor(abs / 100000000);
  const man = Math.floor((abs % 100000000) / 10000);
  const won = abs % 10000;
  let r = "";
  if (ok  > 0) r += ok.toLocaleString()  + "억 ";
  if (man > 0) r += man.toLocaleString() + "만 ";
  if (won > 0) r += won.toLocaleString() + "메소";
  if (r.endsWith(" ")) r = r.trimEnd() + "메소";
  return (neg ? "-" : "") + (r.trim() || "0메소");
}
// ── 보스 결정석: 날짜+phaseMap 기준으로 보스 하나의 가격을 해석 ──
// bkey가 phaseMap에 없으면 "after"(패치 후)가 기본값. 그 날 그 보스/난이도 가격이
// 실제로 안 바뀌었으면(changed=false) phase 선택과 무관하게 항상 같은 값을 반환.
function resolveBossPrice(date, sec, bname, diff, phaseMap, bkey) {
  const afterGroups  = getBossData(date, "after");
  const beforeGroups = getBossData(date, "before");
  const afterD  = afterGroups[sec]?.find(b=>b.name===bname)?.diffs.find(d=>d.diff===diff);
  const beforeD = beforeGroups[sec]?.find(b=>b.name===bname)?.diffs.find(d=>d.diff===diff);
  const afterCrystal  = afterD?.crystal  ?? null;
  const beforeCrystal = beforeD?.crystal ?? null;
  const changed = afterCrystal!=null && beforeCrystal!=null && afterCrystal!==beforeCrystal;
  const phase = (phaseMap && phaseMap[bkey]) || "after";
  const crystal = changed ? (phase==="before" ? beforeCrystal : afterCrystal) : (afterCrystal ?? beforeCrystal);
  return { crystal, changed, phase };
}
// 레코드/보스항목의 pricePhase 값을 배지 라벨+색으로 변환 ("mixed"까지 지원)
function phaseBadgeInfo(phase) {
  if (phase==="before") return { label:"🌙 패치 전", short:"🌙패치전", bg:"#EDEDED", fg:"#666" };
  if (phase==="after")  return { label:"🔧 패치 후", short:"🔧패치후", bg:"#FFF4E0", fg:"#854F0B" };
  if (phase==="mixed")  return { label:"🌗 전/후 혼합", short:"🌗혼합", bg:"#EDE7F6", fg:"#534AB7" };
  return null;
}
// 로컬 날짜 기준 YYYY-MM-DD 반환 (UTC 오프셋 버그 수정)
function localDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth()+1).padStart(2,"0");
  const dd = String(d.getDate()).padStart(2,"0");
  return `${y}-${m}-${dd}`;
}
function today()          { return localDateStr(new Date()); }
function thisWeekStart()  {
  const d=new Date(); const day=d.getDay();
  const diff=day>=4?day-4:day+3;
  d.setDate(d.getDate()-diff); d.setHours(0,0,0,0);
  return localDateStr(d);
}
function thisMonthStart() { return today().slice(0,7)+"-01"; }
function thisYearStart()  { return today().slice(0,4)+"-01-01"; }

function useIsMobile() {
  const [m, setM] = useState(window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setM(window.innerWidth < 768);
    window.addEventListener("resize", fn); return () => window.removeEventListener("resize", fn);
  }, []);
  return m;
}

function getTheme(dark) {
  return {
    bg:dark?"#0F0F0F":"#F5F5F5", card:dark?"#1A1A1A":"#FFFFFF",
    border:dark?"#2A2A2A":"#E8E8E8", text:dark?"#F0F0F0":"#1A1A1A",
    sub:dark?"#888888":"#666666", inp:dark?"#222222":"#FFFFFF",
    inpBdr:dark?"#333333":"#E0E0E0", charBg:dark?"#1E1E2E":"#F0EEFF",
    prevBg:dark?"#1E1A2E":"#F5F5FF", dark,
  };
}

// ── 공지 좋아요 버튼 컴포넌트 ──────────────────
function LikeButton({ T, noticeId, uid, dark }) {
  const [count, setCount] = React.useState(0);
  const [liked, setLiked] = React.useState(false);

  React.useEffect(() => {
    const unsub = subscribeNoticeLikes(noticeId, (cnt, uids) => {
      setCount(cnt);
      setLiked(uids.includes(uid));
    });
    return unsub;
  }, [noticeId, uid]);

  const handleLike = async () => {
    await toggleNoticeLike(noticeId, uid);
  };

  return (
    <button onClick={handleLike} style={{
      display:"flex",alignItems:"center",gap:5,
      background:liked?(dark?"#2A1A2E":"#F5F0FF"):"transparent",
      border:`1px solid ${liked?"#534AB7":"#888"}`,
      borderRadius:20,padding:"4px 12px",
      fontSize:12,color:liked?"#534AB7":"#888",
      cursor:"pointer",fontWeight:liked?600:400,
      transition:"all 0.15s"
    }}>
      👍 {count > 0 ? count : ""}
    </button>
  );
}

function Modal({ T, onClose, children, title, wide }) {
  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.55)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:T.card,borderRadius:16,border:`0.5px solid ${T.border}`,width:"100%",maxWidth:wide?900:560,maxHeight:"90vh",display:"flex",flexDirection:"column"}}>
        <div style={{display:"flex",alignItems:"center",padding:"14px 16px",borderBottom:`0.5px solid ${T.border}`,flexShrink:0}}>
          <span style={{flex:1,fontSize:15,fontWeight:500,color:T.text}}>{title}</span>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",fontSize:22,color:T.sub,lineHeight:1}}>×</button>
        </div>
        <div style={{overflowY:"auto",padding:16,flex:1}}>{children}</div>
      </div>
    </div>
  );
}

function potColor(g) {
  return {"레전드리":"#FF9500","유니크":"#FFCC00","에픽":"#A855F7","레어":"#3B82F6"}[g]||"#888";
}

// ── 숫자 입력 컴포넌트 (콤마 + 한글 단위 표시) ──────────
function NumberInput({ value, onChange, placeholder, style, IS }) {
  const [focused, setFocused] = React.useState(false);

  const toKorean = (n) => {
    if (!n || isNaN(n)) return "";
    n = Math.abs(Math.round(+n));
    if (n === 0) return "";
    const ok  = Math.floor(n / 100000000);
    const man = Math.floor((n % 100000000) / 10000);
    const won = n % 10000;
    let r = "";
    if (ok  > 0) r += ok.toLocaleString() + "억 ";
    if (man > 0) r += man.toLocaleString() + "만 ";
    if (won > 0) r += won.toLocaleString();
    return r.trim();
  };

  const displayVal = focused
    ? (value ? (+value).toLocaleString() : "")
    : (value ? (+value).toLocaleString() : "");

  const handleChange = (e) => {
    const raw = e.target.value.replace(/,/g, "");
    if (raw === "" || /^\d+$/.test(raw)) onChange(raw);
  };

  const korean = toKorean(value);

  return (
    <div style={{ position: "relative" }}>
      <input
        type="text"
        inputMode="numeric"
        value={displayVal}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        style={{ ...IS, ...style, paddingRight: korean ? "8px" : undefined }}
      />
      {korean && value && (
        <div style={{
          fontSize: 10,
          color: "#534AB7",
          fontWeight: 500,
          marginTop: 2,
          paddingLeft: 2,
        }}>
          ≈ {korean}메소
        </div>
      )}
    </div>
  );
}
function DateNavPicker({ value, onChange, T, label }) {
  const [showCal, setShowCal] = useState(false);
  const [calYear, setCalYear] = useState(()=>new Date(value||today()).getFullYear());
  const [calMonth, setCalMonth] = useState(()=>new Date(value||today()).getMonth());

  const addDays = (dateStr, delta) => {
    if(!dateStr||dateStr==="") return today();
    const d = new Date(dateStr+"T00:00:00");
    if(isNaN(d.getTime())) return today();
    d.setDate(d.getDate()+delta);
    return localDateStr(d);
  };
  const isToday = value === today();

  // 달력 날짜 배열 생성
  const buildCalDays = (y, m) => {
    const first = new Date(y, m, 1).getDay(); // 0=일
    const last  = new Date(y, m+1, 0).getDate();
    const days  = [];
    for(let i=0; i<first; i++) days.push(null);
    for(let i=1; i<=last; i++) days.push(i);
    return days;
  };
  const calDays = buildCalDays(calYear, calMonth);
  const calDateStr = (d) => `${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;

  const prevMonth = () => { if(calMonth===0){setCalYear(y=>y-1);setCalMonth(11);}else setCalMonth(m=>m-1); };
  const nextMonth = () => { if(calMonth===11){setCalYear(y=>y+1);setCalMonth(0);}else setCalMonth(m=>m+1); };

  const WEEK = ["일","월","화","수","목","금","토"];

  return (
    <div style={{position:"relative"}}>
      {label&&<div style={{fontSize:11,color:T.sub,marginBottom:5,fontWeight:500}}>{label}</div>}
      <div style={{display:"flex",alignItems:"center",gap:6}}>
        {/* 이전 날 */}
        <button onClick={()=>onChange(addDays(value,-1))}
          style={{width:32,height:36,borderRadius:8,border:`0.5px solid ${T.border}`,background:T.card,color:T.text,fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>‹</button>
        {/* 날짜 표시 (클릭 시 달력) */}
        <button onClick={()=>setShowCal(v=>!v)}
          style={{flex:1,height:36,borderRadius:8,border:`0.5px solid ${showCal?"#534AB7":T.border}`,background:T.inp,color:T.text,fontSize:13,fontWeight:600,cursor:"pointer",fontVariantNumeric:"tabular-nums",letterSpacing:"0.5px"}}>
          {value||"날짜 선택"}
        </button>
        {/* 다음 날 */}
        <button onClick={()=>onChange(addDays(value,1))}
          style={{width:32,height:36,borderRadius:8,border:`0.5px solid ${T.border}`,background:T.card,color:T.text,fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>›</button>
        {/* 오늘 버튼 */}
        {!isToday&&(
          <button onClick={()=>onChange(today())}
            style={{height:36,padding:"0 10px",borderRadius:8,border:`0.5px solid #534AB7`,background:"#534AB722",color:"#534AB7",fontSize:12,fontWeight:600,cursor:"pointer",flexShrink:0,whiteSpace:"nowrap"}}>오늘</button>
        )}
      </div>

      {/* 달력 팝업 */}
      {showCal&&(
        <div style={{position:"absolute",top:"calc(100% + 6px)",left:0,right:0,zIndex:300,background:T.card,borderRadius:12,border:`1px solid #534AB7`,boxShadow:"0 8px 24px rgba(0,0,0,0.18)",padding:12,minWidth:260}}>
          {/* 월 네비 */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
            <button onClick={prevMonth} style={{background:"none",border:"none",cursor:"pointer",fontSize:18,color:T.sub,padding:"2px 8px"}}>‹</button>
            <span style={{fontSize:13,fontWeight:600,color:T.text}}>{calYear}년 {calMonth+1}월</span>
            <button onClick={nextMonth} style={{background:"none",border:"none",cursor:"pointer",fontSize:18,color:T.sub,padding:"2px 8px"}}>›</button>
          </div>
          {/* 요일 헤더 */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4}}>
            {WEEK.map((w,i)=>(
              <div key={w} style={{textAlign:"center",fontSize:11,fontWeight:600,color:i===0?"#A32D2D":i===6?"#2563EB":T.sub,padding:"2px 0"}}>{w}</div>
            ))}
          </div>
          {/* 날짜 그리드 */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
            {calDays.map((d,i)=>{
              if(!d) return <div key={`e${i}`}/>;
              const ds = calDateStr(d);
              const isSelected = ds===value;
              const isTod = ds===today();
              const dayOfWeek = (new Date(calYear,calMonth,d).getDay());
              return(
                <button key={d} onClick={()=>{onChange(ds);setShowCal(false);}}
                  style={{
                    height:32,borderRadius:6,border:"none",cursor:"pointer",fontSize:12,fontWeight:isSelected?700:400,
                    background:isSelected?"#534AB7":isTod?"#534AB722":"transparent",
                    color:isSelected?"#fff":isTod?"#534AB7":dayOfWeek===0?"#A32D2D":dayOfWeek===6?"#2563EB":T.text,
                    outline:"none",
                  }}>
                  {d}
                </button>
              );
            })}
          </div>
          {/* 오늘로 버튼 */}
          <button onClick={()=>{onChange(today());setCalYear(new Date().getFullYear());setCalMonth(new Date().getMonth());setShowCal(false);}}
            style={{width:"100%",marginTop:8,padding:"6px 0",borderRadius:8,border:`0.5px solid ${T.border}`,background:"#534AB7",color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer"}}>
            오늘로 이동
          </button>
        </div>
      )}
    </div>
  );
}

function BossSection({ T, isMobile, checkedBosses, setCheckedBosses, bossParty, setBossParty, bossPartyMembers, setBossPartyMembers, bossDiffs, setBossDiffs, records, selChar, incDate, excludeRecordId, isEditMode, originalDate, onLimitToast, onDropChange, bossPricePhaseMap, setBossPricePhaseMap }) {
  const _bossDiffs           = bossDiffs           || {};
  const _bossPartyMembers    = bossPartyMembers    || {};
  const _setBossDiffs        = setBossDiffs        || (()=>{});
  const _setBossPartyMembers = setBossPartyMembers || (()=>{});
  const _phaseMap            = bossPricePhaseMap   || {};
  const _setPhaseMap         = setBossPricePhaseMap|| (()=>{});

  // ── 드롭 아이템 체크 state { bossKey: { itemName: true/false } } ──
  const [bossDropChecks, setBossDropChecks] = React.useState({});
  // ── 반지 상자 선택 { bossKey_itemName: { level, ringName } } ──
  const [ringSelections, setRingSelections] = React.useState({});
  // ── 드롭 섹션 열림 여부 { bossKey: true/false } ──
  const [dropOpen, setDropOpen] = React.useState({});

  // ── 섹션 접기 ──
  const [collapsed, setCollapsed] = React.useState(()=>{
    try { return JSON.parse(localStorage.getItem("bossCollapsed")||"{}"); } catch { return {}; }
  });
  const toggleCollapse = (key) => {
    const next = {...collapsed, [key]:!collapsed[key]};
    setCollapsed(next);
    localStorage.setItem("bossCollapsed", JSON.stringify(next));
  };

  // 드롭 체크 변경 시 부모에 전달
  React.useEffect(() => {
    if (!onDropChange) return;
    const result = [];
    Object.entries(bossDropChecks).forEach(([bossKey, items]) => {
      Object.entries(items).forEach(([itemName, checked]) => {
        if (!checked) return;
        const ringKey = `${bossKey}_${itemName}`;
        const ring = ringSelections[ringKey];
        result.push({ bossKey, itemName, ringLevel: ring?.level||null, ringName: ring?.ringName||null });
      });
    });
    onDropChange(result);
  }, [bossDropChecks, ringSelections]);

  const usedDate = incDate || today();
  // 이 날짜에 daily/weekly/monthly 중 실제로 가격이 바뀌는 게 있는지
  const patchInfo = getPatchTransitionInfo(usedDate);
  const isPatchDay = isPatchTransitionDate(usedDate);
  // 패치 전/후 두 가격표를 항상 같이 준비해둠 (보스마다 개별 선택 가능하도록)
  const afterBossGroups  = getBossData(usedDate, "after");
  const beforeBossGroups = getBossData(usedDate, "before");
  // 기본(단일) 가격표 — 패치와 무관한 항목이나 기존 호환용으로 사용
  const dateBossGroups = afterBossGroups;
  // 수정모달에서 주차 계산은 원본 날짜 기준
  const baseDate = (isEditMode && originalDate) ? originalDate : usedDate;

  // ── incDate 기준 주(목~수) 시작/끝 ──
  const wStart = (()=>{
    const d = new Date(baseDate+"T00:00:00");
    const day = d.getDay();
    const diff = day>=4 ? day-4 : day+3;
    d.setDate(d.getDate()-diff);
    return localDateStr(d);
  })();
  const wEnd = (()=>{
    const d = new Date(wStart+"T00:00:00");
    d.setDate(d.getDate()+6);
    return localDateStr(d);
  })();

  // ── incDate 기준 월 시작/끝 ──
  const mStart = baseDate.slice(0,7)+"-01";
  const mEnd   = (()=>{
    const [y,m] = baseDate.slice(0,7).split("-").map(Number);
    return `${usedDate.slice(0,7)}-${String(new Date(y,m,0).getDate()).padStart(2,"0")}`;
  })();

  // ── 자기 자신 제외한 보스 기록 ──
  const bossRecs = (records||[]).filter(r=>
    r.kind==="income" && r.type==="boss" &&
    r.nickname===(selChar?.nickname||"") &&
    Array.isArray(r.bossList) &&
    r.id !== excludeRecordId
  );

  const baseName = (name) => name.split(" (")[0].trim();

  // ── 해당 주에 DB에 기록된 주간보스 보스명 Set ──
  const weeklyRecordedNames = new Set(
    bossRecs
      .filter(r => r.date>=wStart && r.date<=wEnd)
      .flatMap(r => r.bossList?.filter(b => BOSSES.weekly.some(w=>w.name===b.name))||[])
      .map(b => baseName(b.name))
  );

  // ── 현재 체크된(true인) 주간보스 보스명 Set ──
  const weeklyCheckedNames = new Set(
    Object.entries(checkedBosses)
      .filter(([k,v]) => v===true && k.startsWith("weekly_") && k.split("_").length>=3)
      .map(([k]) => {
        const parts = k.split("_");
        return parts.slice(1,-1).join("_");
      })
  );

  // ── 해당 월에 DB에 기록된 월간보스 수 ──
  const monthlyUsed = bossRecs
    .filter(r => r.date>=mStart && r.date<=mEnd)
    .reduce((s,r) => s+(r.bossList?.filter(b=>BOSSES.monthly.some(m=>m.name===b.name)).length||0), 0);

  // ── 현재 체크된 월간보스 수 ──
  const monthlyCheckedNow = Object.entries(checkedBosses)
    .filter(([k,v]) => v===true && k.startsWith("monthly_")).length;

  const weeklyTotal  = weeklyRecordedNames.size + weeklyCheckedNames.size;
  const monthlyTotal = monthlyUsed + monthlyCheckedNow;

  // ── 보스+난이도 하나의 가격을 "패치 전/후 중 이 보스에 대해 선택된 쪽"으로 계산 ──
  // changed=true면 실제로 그 보스/난이도의 가격이 이 날짜에 바뀐 것 → 개별 선택 UI를 보여줘야 함
  const resolvePrice = (bkey, sec, bname, diff) => {
    const afterGrp  = afterBossGroups[sec]?.find(b=>b.name===bname);
    const beforeGrp = beforeBossGroups[sec]?.find(b=>b.name===bname);
    const afterD  = afterGrp?.diffs.find(d=>d.diff===diff);
    const beforeD = beforeGrp?.diffs.find(d=>d.diff===diff);
    const afterCrystal  = afterD?.crystal  ?? null;
    const beforeCrystal = beforeD?.crystal ?? null;
    const changed = afterCrystal!=null && beforeCrystal!=null && afterCrystal!==beforeCrystal;
    const phase = _phaseMap[bkey] || "after";
    const crystal = changed ? (phase==="before" ? beforeCrystal : afterCrystal) : (afterCrystal ?? beforeCrystal);
    return { crystal, beforeCrystal, afterCrystal, changed, phase };
  };

  // ── 결정석 합산 ──
  const bossTotal = Object.entries(checkedBosses).reduce((sum,[key,checked])=>{
    if(checked!==true) return sum;
    const parts=key.split("_");
    if(parts.length<3) return sum;
    const sec=parts[0]; const diff=parts[parts.length-1]; const bname=parts.slice(1,-1).join("_");
    const { crystal } = resolvePrice(key, sec, bname, diff);
    if(crystal==null) return sum;
    const p=bossParty[key]||1;
    return sum+Math.floor(crystal/p);
  },0);

  const checkedCount = Object.values(checkedBosses).filter(v=>v===true).length;

  const adjustParty = (key, delta) => {
    setBossParty(p=>{ const cur=p[key]||1; return {...p,[key]:Math.max(1,Math.min(6,cur+delta))}; });
    _setBossPartyMembers(p=>{
      const newParty = Math.max(1,Math.min(6,(bossParty[key]||1)+delta));
      const cur = [...(p[key]||[])];
      while(cur.length < newParty-1) cur.push("");
      return {...p,[key]:cur.slice(0,newParty-1)};
    });
  };

  const handleCheck = (bkey, sec, bname) => {
    const isChecked = checkedBosses[bkey]===true;

    // ── 체크 해제는 항상 허용 ──
    if(isChecked) {
      setCheckedBosses(p=>({...p,[bkey]:false}));
      return;
    }

    // ── 수정 모달에서는 DB 기록 기반 제한 없음 (자유 수정 가능) ──
    if(!isEditMode) {
      if(sec==="weekly") {
        if(weeklyRecordedNames.has(bname)) {
          onLimitToast?.(`이번 주에 ${bname}은(는) 이미 기록됐습니다!`); return;
        }
        if(weeklyCheckedNames.has(bname)) {
          onLimitToast?.(`${bname}은(는) 이미 선택되어 있습니다!`); return;
        }
        if(weeklyTotal>=12) {
          onLimitToast?.("이번 주 주간보스 12마리를 모두 채웠습니다!"); return;
        }
      }
      if(sec==="monthly" && monthlyTotal>=1) {
        onLimitToast?.("검은 마법사는 이번 달에 이미 기록됐거나 선택됐습니다!"); return;
      }
    } else {
      // 수정 모달: 같은 보스 타 난이도 중복 선택만 방지
      if(sec==="weekly" && weeklyCheckedNames.has(bname)) {
        onLimitToast?.(`${bname}은(는) 이미 선택되어 있습니다!`); return;
      }
    }

    setCheckedBosses(p=>({...p,[bkey]:true}));
  };

  const SEC_INFO = [
    { key:"daily",   label:"📅 일일 보스", color:"#3B6D11" },
    { key:"weekly",  label:"📆 주간 보스", color:"#534AB7" },
    { key:"monthly", label:"🗓️ 월간 보스", color:"#A32D2D" },
  ];

  return (
    <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16,marginBottom:10}}>
      {isPatchDay && (()=>{
        const changedLabels = [
          patchInfo.daily   && "일간",
          patchInfo.weekly  && "주간",
          patchInfo.monthly && "월간",
        ].filter(Boolean).join("·");
        return (
          <div style={{background:"#FFF4E0",border:"2px solid #E4A800",borderRadius:10,padding:"12px 14px",marginBottom:14}}>
            <div style={{fontSize:15,color:"#854F0B",fontWeight:800,marginBottom:4,lineHeight:1.4}}>
              🚨 오늘은 {changedLabels}보스 결정석 가격이 바뀌는 날이에요!
            </div>
            <div style={{fontSize:13,color:"#854F0B",fontWeight:600,lineHeight:1.5}}>
              가격이 두 개로 뜨는 보스는 <u>보스마다</u> 몇 시에 잡으셨는지 직접 골라주세요.
              (일부는 점검 전, 일부는 점검 후에 잡았어도 문제없어요!)
            </div>
          </div>
        );
      })()}

      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:isEditMode?4:12}}>
        <div style={{fontSize:13,fontWeight:500,color:"#534AB7"}}>⚔️ 보스 결정석</div>
        {checkedCount>0&&(
          <button onClick={()=>{setCheckedBosses({});setBossParty({});_setBossPartyMembers({});_setBossDiffs({});}}
            style={{background:"transparent",border:`0.5px solid ${T.border}`,borderRadius:6,padding:"3px 10px",fontSize:11,color:T.sub,cursor:"pointer"}}>
            전체 해제
          </button>
        )}
      </div>

      {isEditMode&&(
        <div style={{fontSize:11,color:"#534AB7",background:"#EEEDFE",borderRadius:8,padding:"6px 10px",marginBottom:10,lineHeight:1.6}}>
          💡 <strong>기존 기록</strong> 뱃지가 있는 보스가 현재 저장된 내용입니다. 체크를 변경하면 수정됩니다.
        </div>
      )}
      {SEC_INFO.map(({key,label,color})=>{
        const total  = key==="weekly"?weeklyTotal:key==="monthly"?monthlyTotal:null;
        const limit  = key==="weekly"?12:key==="monthly"?1:null;
        const isFull = limit!==null && total>=limit;
        const isCollapsed = !!collapsed[key];

        return(
          <div key={key} style={{marginBottom:12}}>
            {/* 섹션 헤더 */}
            <div onClick={()=>toggleCollapse(key)}
              style={{display:"flex",alignItems:"center",gap:8,marginBottom:isCollapsed?0:8,cursor:"pointer",userSelect:"none"}}>
              <div style={{fontSize:11,fontWeight:600,color:"#fff",padding:"3px 10px",background:color,borderRadius:20}}>{label}</div>
              {selChar&&limit!==null&&(
                <div style={{display:"flex",alignItems:"center",gap:4,padding:"2px 8px",borderRadius:20,
                  border:`0.5px solid ${isFull?"#A32D2D":T.border}`,
                  background:isFull?"#FCEBEB":T.bg}}>
                  <span style={{fontSize:11,fontWeight:700,color:isFull?"#A32D2D":total>0?"#534AB7":T.sub}}>{total}</span>
                  <span style={{fontSize:11,color:T.sub}}>/ {limit}</span>
                  {isFull&&<span style={{fontSize:10,color:"#A32D2D",fontWeight:600}}>✓ 완료</span>}
                </div>
              )}
              <span style={{marginLeft:"auto",fontSize:12,color:T.sub,display:"inline-block",
                transform:isCollapsed?"rotate(-90deg)":"rotate(0deg)",transition:"transform 0.2s"}}>▾</span>
            </div>

            {!isCollapsed&&(
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {dateBossGroups[key].map((boss)=>{
                  const selDiff = _bossDiffs[`${key}_${boss.name}`] || boss.diffs[0].diff;
                  const bkey    = `${key}_${boss.name}_${selDiff}`;
                  const checked = checkedBosses[bkey]===true;
                  const party   = bossParty[bkey]||1;
                  const members = _bossPartyMembers[bkey]||[];
                  const priceInfo = resolvePrice(bkey, key, boss.name, selDiff);
                  const crystal = Math.floor((priceInfo.crystal||0)/party);

                  // ── DB 기록 여부 (해당 기간 내) ──
                  const alreadyInDB = bossRecs.some(r=>{
                    if(key==="weekly"  && (r.date<wStart||r.date>wEnd))   return false;
                    if(key==="monthly" && (r.date<mStart||r.date>mEnd))   return false;
                    if(key==="daily"   && r.date!==usedDate)               return false;
                    return r.bossList?.some(b=>b.name===`${boss.name} (${selDiff})`);
                  });

                  // ── 같은 보스 타 난이도가 DB에 기록됐는지 ──
                  const sameBaseInDB = key==="weekly" && !alreadyInDB && weeklyRecordedNames.has(boss.name);

                  // ── 같은 보스 타 난이도가 현재 체크됐는지 ──
                  const sameBaseNow = key==="weekly" && !checked && weeklyCheckedNames.has(boss.name);

                  // ── 비활성화: 체크 안 됐고 제한에 걸릴 때만 ──
                  // 수정 모달에서는 DB 기록 기반(sameBaseInDB) 차단 없음
                  const isDisabled = !checked && (
                    isEditMode
                      ? (key==="weekly" && sameBaseNow)  // 수정: 현재 선택중인 타 난이도만 차단
                      : ((key==="weekly"  && (isFull||sameBaseInDB||sameBaseNow)) ||
                         (key==="monthly" && isFull))
                  );

                  // 수정 모달에서 원본 기록된 보스 판별
                  const isOriginal = isEditMode && alreadyInDB;
                  const borderColor = checked ? color
                    : isOriginal ? "#534AB7"
                    : alreadyInDB ? "#854F0B44"
                    : (sameBaseInDB||sameBaseNow) ? "#A32D2D33"
                    : T.border;
                  const bgColor = checked ? (T.dark?"#1E1E2E":"#F0EEFF")
                    : isOriginal ? (T.dark?"#1E1E2E":"#F0EEFF")
                    : alreadyInDB ? (T.dark?"#1A150A":"#FFF8EE")
                    : (sameBaseInDB||sameBaseNow) ? (T.dark?"#1A0F0F":"#FFF0F0")
                    : T.card;

                  return(
                    <div key={boss.name} style={{borderRadius:8,border:`0.5px solid ${borderColor}`,
                      background:bgColor,overflow:"hidden",opacity:isDisabled?0.4:1}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px"}}>
                        {/* 체크박스 */}
                        <div onClick={()=>handleCheck(bkey,key,boss.name)}
                          style={{width:18,height:18,borderRadius:4,flexShrink:0,
                            border:`1.5px solid ${checked?color:T.sub}`,
                            background:checked?color:"transparent",
                            display:"flex",alignItems:"center",justifyContent:"center",
                            cursor:isDisabled?"not-allowed":"pointer"}}>
                          {checked&&<span style={{color:"#fff",fontSize:12}}>✓</span>}
                        </div>

                        {/* 보스명 + 뱃지 */}
                        <div style={{fontSize:12,fontWeight:500,color:T.text,minWidth:60,cursor:isDisabled?"not-allowed":"pointer"}}
                          onClick={()=>handleCheck(bkey,key,boss.name)}>
                          {boss.name}
                          {isEditMode&&alreadyInDB&&<span style={{fontSize:9,color:"#fff",background:"#534AB7",borderRadius:4,padding:"1px 5px",marginLeft:4,fontWeight:600}}>기존 기록</span>}
                          {!isEditMode&&alreadyInDB&&<span style={{fontSize:9,color:"#854F0B",background:"#FFF3CD",borderRadius:4,padding:"1px 4px",marginLeft:4}}>기록됨</span>}
                          {sameBaseInDB&&<span style={{fontSize:9,color:"#A32D2D",background:"#FEECEC",borderRadius:4,padding:"1px 4px",marginLeft:4}}>타 난이도 기록됨</span>}
                          {sameBaseNow&&<span style={{fontSize:9,color:"#A32D2D",background:"#FEECEC",borderRadius:4,padding:"1px 4px",marginLeft:4}}>타 난이도 선택중</span>}
                        </div>

                        {/* 난이도 선택 */}
                        {boss.diffs.length>1?(
                          <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                            {boss.diffs.map(d=>(
                              <button key={d.diff}
                                onClick={()=>{
                                  // 난이도 변경 시 기존 체크 해제
                                  setCheckedBosses(p=>{
                                    const next={...p};
                                    boss.diffs.forEach(dd=>{ delete next[`${key}_${boss.name}_${dd.diff}`]; });
                                    return next;
                                  });
                                  _setBossDiffs(p=>({...p,[`${key}_${boss.name}`]:d.diff}));
                                }}
                                style={{padding:"2px 7px",borderRadius:12,fontSize:10,fontWeight:600,cursor:"pointer",
                                  border:`1px solid ${selDiff===d.diff?color:T.border}`,
                                  background:selDiff===d.diff?color:T.card,
                                  color:selDiff===d.diff?"#fff":T.sub}}>
                                {d.diff}
                              </button>
                            ))}
                          </div>
                        ):(
                          <span style={{fontSize:10,color:T.sub,padding:"2px 7px",borderRadius:12,background:T.bg}}>
                            {boss.diffs[0].diff}
                          </span>
                        )}

                        {/* 인원수 */}
                        <div style={{display:"flex",alignItems:"center",gap:4,marginLeft:"auto",flexShrink:0}}>
                          <button onClick={e=>{e.stopPropagation();adjustParty(bkey,-1);}}
                            style={{width:22,height:22,border:`0.5px solid ${T.border}`,borderRadius:4,
                              background:T.card,color:T.text,fontSize:14,cursor:"pointer",
                              display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                          <span style={{fontSize:12,fontWeight:600,color:T.text,minWidth:20,textAlign:"center"}}>{party}인</span>
                          <button onClick={e=>{e.stopPropagation();adjustParty(bkey,1);}}
                            style={{width:22,height:22,border:`0.5px solid ${T.border}`,borderRadius:4,
                              background:T.card,color:T.text,fontSize:14,cursor:"pointer",
                              display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                        </div>

                        {/* 결정석 금액 */}
                        <span style={{fontSize:11,color:checked?color:T.sub,flexShrink:0,minWidth:70,textAlign:"right"}}>
                          {priceInfo.changed && (priceInfo.phase==="before"?"🌙 ":"🔧 ")}{fmt(crystal)}
                        </span>
                      </div>

                      {/* 패치 전/후 가격이 다른 보스: 보스마다 직접 선택 */}
                      {priceInfo.changed&&(
                        <div style={{padding:"0 10px 8px 10px"}}>
                          <div style={{display:"flex",gap:6}}>
                            {[
                              ["before","🌙 점검 전",priceInfo.beforeCrystal],
                              ["after", "🔧 점검 후",priceInfo.afterCrystal],
                            ].map(([val,label,amt])=>{
                              const sel = priceInfo.phase===val;
                              return (
                                <button key={val} onClick={(e)=>{
                                    e.stopPropagation();
                                    _setPhaseMap(p=>({...p,[bkey]:val}));
                                  }}
                                  style={{flex:1,padding:"7px 6px",borderRadius:8,cursor:"pointer",textAlign:"center",
                                    border:`2px solid ${sel?"#E4A800":T.border}`,
                                    background:sel?"#E4A800":T.card,
                                    color:sel?"#fff":T.text}}>
                                  <div style={{fontSize:11,fontWeight:700}}>{sel?"✅ ":""}{label}</div>
                                  <div style={{fontSize:11,fontWeight:600,marginTop:1}}>{fmt(Math.floor(amt/party))}</div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* 파티원 입력 */}
                      {checked===true&&party>1&&(
                        <div style={{padding:"0 10px 8px 38px"}}>
                          <div style={{fontSize:10,color:T.sub,marginBottom:4}}>함께한 용사</div>
                          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                            {Array.from({length:party-1}).map((_,i)=>(
                              <input key={i} type="text"
                                value={members[i]||""}
                                onChange={e=>{
                                  _setBossPartyMembers(p=>{
                                    const cur=[...(p[bkey]||[])];
                                    cur[i]=e.target.value;
                                    return {...p,[bkey]:cur};
                                  });
                                }}
                                placeholder={`용사 ${i+1}`}
                                style={{background:T.inp,border:`0.5px solid ${T.border}`,borderRadius:6,
                                  padding:"4px 8px",color:T.text,fontSize:11,outline:"none",width:90}}/>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 💎 드롭 아이템 */}
                      {checked===true&&!isEditMode&&(()=>{
                        const drops = getBossDrops(boss.name, selDiff);
                        if(!drops.length) return null;
                        const isOpen = dropOpen[bkey];
                        return (
                          <div style={{padding:"0 10px 8px 38px"}}>
                            <button onClick={()=>setDropOpen(p=>({...p,[bkey]:!p[bkey]}))}
                              style={{background:"transparent",border:`0.5px solid ${T.border}`,
                                borderRadius:6,padding:"3px 10px",fontSize:10,color:T.sub,
                                cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
                              💎 드롭 아이템 {isOpen?"▲":"▼"}
                            </button>
                            {isOpen&&(
                              <div style={{marginTop:6,display:"flex",flexDirection:"column",gap:4}}>
                                {drops.map(drop=>{
                                  const isRingBox = drop.name.includes("반지 상자");
                                  const dropChecked = bossDropChecks[bkey]?.[drop.name]||false;
                                  const ringKey = `${bkey}_${drop.name}`;
                                  const ringBox = isRingBox ? RING_BOX_DATA.find(b=>b.name===drop.name) : null;
                                  return (
                                    <div key={drop.name}>
                                      <div onClick={()=>setBossDropChecks(p=>({
                                          ...p,[bkey]:{...(p[bkey]||{}),[drop.name]:!dropChecked}
                                        }))} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",
                                        padding:"4px 6px",borderRadius:6,
                                        background:dropChecked?(T.dark?"#1a2a1a":"#F1FBF1"):T.bg,
                                        border:`0.5px solid ${dropChecked?"#4CAF50":T.border}`}}>
                                        <div style={{width:14,height:14,borderRadius:3,flexShrink:0,
                                          border:`1.5px solid ${dropChecked?"#4CAF50":T.sub}`,
                                          background:dropChecked?"#4CAF50":"transparent",
                                          display:"flex",alignItems:"center",justifyContent:"center"}}>
                                          {dropChecked&&<span style={{color:"#fff",fontSize:9}}>✓</span>}
                                        </div>
                                        {drop.itemIcon?<img src={drop.itemIcon} style={{width:16,height:16}} alt=""/>:null}
                                        <span style={{fontSize:11,color:T.text}}>{drop.name}</span>
                                      </div>
                                      {/* 반지 상자 → 레벨 + 링 종류 선택 */}
                                      {dropChecked&&ringBox&&(
                                        <div style={{marginTop:4,marginLeft:20,padding:"8px 10px",
                                          background:T.card,borderRadius:8,border:`0.5px solid ${T.border}`}}>
                                          <div style={{fontSize:10,color:T.sub,marginBottom:6}}>획득한 반지 정보</div>
                                          <div style={{display:"flex",gap:6,marginBottom:6,flexWrap:"wrap"}}>
                                            {Object.entries(ringBox.levelProb).filter(([,v])=>v!==null).map(([lv])=>(
                                              <button key={lv} onClick={e=>{e.stopPropagation();setRingSelections(p=>({
                                                ...p,[ringKey]:{...(p[ringKey]||{}),level:lv}
                                              }));}}
                                                style={{padding:"3px 10px",borderRadius:10,fontSize:10,
                                                  fontWeight:600,cursor:"pointer",
                                                  border:`1px solid ${ringSelections[ringKey]?.level===lv?"#534AB7":T.border}`,
                                                  background:ringSelections[ringKey]?.level===lv?"#534AB7":"transparent",
                                                  color:ringSelections[ringKey]?.level===lv?"#fff":T.sub}}>
                                                {lv}레벨
                                              </button>
                                            ))}
                                          </div>
                                          {ringSelections[ringKey]?.level&&(
                                            <select value={ringSelections[ringKey]?.ringName||""}
                                              onClick={e=>e.stopPropagation()}
                                              onChange={e=>{e.stopPropagation();setRingSelections(p=>({
                                                ...p,[ringKey]:{...(p[ringKey]||{}),ringName:e.target.value}
                                              }));}}
                                              style={{width:"100%",background:T.inp,border:`0.5px solid ${T.border}`,
                                                borderRadius:6,padding:"5px 8px",color:T.text,fontSize:11,outline:"none"}}>
                                              <option value="">링 종류 선택</option>
                                              {ringBox.rings.map(r=>(
                                                <option key={r.name} value={r.name}>{r.name} ({r.prob.toFixed(2)}%)</option>
                                              ))}
                                            </select>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* 합산 */}
      {checkedCount>0&&(
        <div style={{background:T.prevBg,borderRadius:8,padding:"10px 12px",marginTop:4}}>
          <div style={{fontSize:12,color:T.sub,marginBottom:6}}>선택 ({checkedCount}개)</div>
          {Object.entries(checkedBosses).filter(([,v])=>v===true).map(([k])=>{
            const parts=k.split("_");
            if(parts.length<3) return null;
            const sec=parts[0]; const diff=parts[parts.length-1]; const bname=parts.slice(1,-1).join("_");
            const priceInfo = resolvePrice(k, sec, bname, diff);
            if(priceInfo.crystal==null) return null;
            const p=bossParty[k]||1;
            return(
              <div key={k} style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"3px 0",borderBottom:`0.5px solid ${T.border}`}}>
                <span style={{color:T.text,display:"flex",alignItems:"center",gap:6}}>
                  {bname} ({diff}){p>1?` ${p}인`:""}
                  {priceInfo.changed&&(
                    <span style={{fontSize:10}}>{priceInfo.phase==="before"?"🌙":"🔧"}</span>
                  )}
                </span>
                <span style={{color:"#854F0B",fontWeight:500}}>{fmt(Math.floor(priceInfo.crystal/p))}</span>
              </div>
            );
          })}
          <div style={{display:"flex",justifyContent:"space-between",fontSize:15,fontWeight:500,color:"#854F0B",marginTop:8,paddingTop:6,borderTop:`1px solid ${T.border}`}}>
            <span>결정석 합산</span><span>{fmt(bossTotal)}</span>
          </div>
        </div>
      )}
    </div>
  );
}


function StatsTab({ records, favorites, T, isMobile }) {
  const [selChars, setSelChars] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");
  const [viewType, setViewType] = useState("all");
  const allNicknames = [...new Set(records.map(r=>r.nickname).filter(Boolean))];
  const favNicknames = favorites.map(f=>f.nickname);
  const setRange = (from, to="") => { setDateFrom(from); setDateTo(to||today()); };
  const filtered = records.filter(r=>{
    if (selChars.length>0&&!selChars.includes(r.nickname)) return false;
    if (dateFrom&&r.date<dateFrom) return false;
    if (dateTo&&r.date>dateTo) return false;
    if (viewType==="income"&&r.kind!=="income") return false;
    if (viewType==="expense"&&r.kind!=="expense") return false;
    return true;
  });
  const incRecs=filtered.filter(r=>r.kind==="income");
  const expRecs=filtered.filter(r=>r.kind==="expense");
  const totInc=incRecs.reduce((s,r)=>s+calcIncome(r,favorites),0);
  const totExp=expRecs.reduce((s,r)=>s+(r.meso||0),0);
  const net=totInc-totExp;
  const charMap={};
  filtered.forEach(r=>{
    const n=r.nickname||"기타"; if(!charMap[n]) charMap[n]={inc:0,exp:0};
    if(r.kind==="income") charMap[n].inc+=calcIncome(r,favorites);
    if(r.kind==="expense") charMap[n].exp+=(r.meso||0);
  });
  const charList=Object.entries(charMap).map(([nick,v])=>({nick,inc:v.inc,exp:v.exp,net:v.inc-v.exp})).sort((a,b)=>b.net-a.net);
  const maxAbs=Math.max(...charList.map(c=>Math.abs(c.net)),1);
  const IS={padding:"6px 10px",borderRadius:8,border:`0.5px solid ${T.inpBdr}`,fontSize:12,background:T.inp,color:T.text,fontFamily:"Arial",boxSizing:"border-box"};
  const toggleChar=nick=>setSelChars(p=>p.includes(nick)?p.filter(n=>n!==nick):[...p,nick]);
  return (
    <div>
      <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16,marginBottom:10}}>
        <div style={{fontSize:13,fontWeight:500,color:"#534AB7",marginBottom:12}}>📊 통계 필터</div>
        <div style={{marginBottom:12}}>
          <div style={{fontSize:11,color:T.sub,marginBottom:6,fontWeight:500}}>캐릭터 선택</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            <button onClick={()=>setSelChars([])} style={{padding:"4px 12px",borderRadius:20,fontSize:12,cursor:"pointer",border:`0.5px solid ${selChars.length===0?"#534AB7":T.border}`,background:selChars.length===0?"#534AB7":T.card,color:selChars.length===0?"#fff":T.sub}}>전체</button>
            {allNicknames.map(nick=>(
              <button key={nick} onClick={()=>toggleChar(nick)} style={{padding:"4px 12px",borderRadius:20,fontSize:12,cursor:"pointer",border:`0.5px solid ${selChars.includes(nick)?"#534AB7":T.border}`,background:selChars.includes(nick)?"#534AB7":T.card,color:selChars.includes(nick)?"#fff":T.sub,display:"flex",alignItems:"center",gap:4}}>
                {favNicknames.includes(nick)&&<span style={{fontSize:10}}>⭐</span>}{nick}
              </button>
            ))}
          </div>
        </div>
        <div style={{marginBottom:10}}>
          <div style={{fontSize:11,color:T.sub,marginBottom:6,fontWeight:500}}>기간 선택</div>
          <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:8}}>
            {[["오늘",()=>setRange(today(),today())],["이번 주",()=>setRange(thisWeekStart())],["이번 달",()=>setRange(thisMonthStart())],["올해",()=>setRange(thisYearStart())],["전체",()=>setRange("","")]].map(([l,fn])=>(
              <button key={l} onClick={fn} style={{padding:"4px 12px",borderRadius:20,fontSize:12,cursor:"pointer",border:`0.5px solid ${T.border}`,background:T.card,color:T.sub}}>{l}</button>
            ))}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={{...IS,flex:1,minWidth:130}}/>
            <span style={{fontSize:13,color:T.sub}}>~</span>
            <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={{...IS,flex:1,minWidth:130}}/>
            <button onClick={()=>{setDateFrom("");setDateTo("");}} style={{padding:"5px 12px",borderRadius:8,fontSize:12,cursor:"pointer",border:`0.5px solid ${T.border}`,background:"transparent",color:T.sub}}>초기화</button>
          </div>
          {(dateFrom||dateTo)&&<div style={{fontSize:11,color:"#534AB7",marginTop:4}}>{dateFrom||"처음"} ~ {dateTo||"현재"}</div>}
        </div>
        <div style={{display:"flex",gap:4}}>
          {[["all","전체"],["income","수익"],["expense","지출"]].map(([k,l])=>(
            <button key={k} onClick={()=>setViewType(k)} style={{flex:1,padding:"6px 0",borderRadius:8,fontSize:12,cursor:"pointer",border:`0.5px solid ${T.border}`,background:viewType===k?"#534AB7":T.card,color:viewType===k?"#fff":T.sub}}>{l}</button>
          ))}
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
        <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:14}}>
          <div style={{fontSize:11,color:T.sub,marginBottom:4}}>총 수익</div>
          <div style={{fontSize:isMobile?16:20,fontWeight:500,color:"#3B6D11"}}>{fmt(totInc)}</div>
          <div style={{fontSize:11,color:T.sub,marginTop:4}}>{incRecs.length}건</div>
        </div>
        <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:14}}>
          <div style={{fontSize:11,color:T.sub,marginBottom:4}}>총 지출</div>
          <div style={{fontSize:isMobile?16:20,fontWeight:500,color:"#A32D2D"}}>{fmt(totExp)}</div>
          <div style={{fontSize:11,color:T.sub,marginTop:4}}>{expRecs.length}건</div>
        </div>
      </div>
      <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${net>=0?"#3B6D11":"#A32D2D"}`,padding:16,marginBottom:10}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:11,color:T.sub,marginBottom:4}}>순손익</div>
            <div style={{fontSize:isMobile?20:26,fontWeight:500,color:net>=0?"#3B6D11":"#A32D2D"}}>{net>=0?"+":""}{fmt(net)}</div>
          </div>
          <div style={{fontSize:32}}>{net>=0?"📈":"📉"}</div>
        </div>
        {net<0&&<div style={{marginTop:8,padding:"6px 10px",background:"#FCEBEB",borderRadius:8,fontSize:12,color:"#A32D2D"}}>💸 {fmt(Math.abs(net))} 투자(손해) 상태</div>}
      </div>
      {charList.length>0&&(
        <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16,marginBottom:10}}>
          <div style={{fontSize:13,fontWeight:500,color:"#534AB7",marginBottom:12}}>👤 캐릭터별 순손익</div>
          {charList.map(c=>{
            const pos=c.net>=0; const barW=Math.round(Math.abs(c.net)/maxAbs*100);
            return(<div key={c.nick} style={{marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:13,fontWeight:500,color:T.text}}>{c.nick}</span>
                  {favNicknames.includes(c.nick)&&<span style={{fontSize:10,color:"#854F0B"}}>⭐</span>}
                </div>
                <span style={{fontSize:13,fontWeight:500,color:pos?"#3B6D11":"#A32D2D"}}>{pos?"+":""}{fmt(c.net)}</span>
              </div>
              <div style={{height:8,background:T.prevBg,borderRadius:4,overflow:"hidden"}}>
                <div style={{height:"100%",width:barW+"%",background:pos?"#3B6D11":"#A32D2D",borderRadius:4}}/>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.sub,marginTop:3}}>
                <span>수익 {fmt(c.inc)}</span><span>지출 {fmt(c.exp)}</span>
              </div>
            </div>);
          })}
        </div>
      )}
      {incRecs.length>0&&(
        <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16,marginBottom:10}}>
          <div style={{fontSize:13,fontWeight:500,color:"#534AB7",marginBottom:12}}>💰 수익 유형별</div>
          {[
            ["⚔️ 보스 결정석", incRecs.reduce((s,r)=>s+(r.bossIncome||0),0)],
            ["🏃 사냥 메소",    incRecs.filter(r=>r.type==="hunt").reduce((s,r)=>s+(r.meso||0)+(r.etc||0),0)],
            ["✨ 솔에르다",     incRecs.reduce((s,r)=>s+calcSolIncome(r,favorites),0)],
            ["🎉 이벤트/기타", incRecs.filter(r=>r.type==="event").reduce((s,r)=>s+(r.meso||0)+(r.etc||0),0)],
            ["💎 물욕템",       incRecs.filter(r=>r.type==="drop").reduce((s,r)=>s+calcIncome(r,favorites),0)],
            ["🔮 에픽던전주화", incRecs.filter(r=>r.type==="epic").reduce((s,r)=>s+(r.meso||0)+(r.etc||0),0)],
          ].filter(([,v])=>v>0).map(([l,v])=>(
            <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`0.5px solid ${T.border}`,fontSize:13}}>
              <span style={{color:T.sub}}>{l}</span><span style={{color:T.text,fontWeight:500}}>{fmt(v)}</span>
            </div>
          ))}
        </div>
      )}
      {filtered.length===0&&(
        <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:32,textAlign:"center",color:T.sub}}>
          <div style={{fontSize:32,marginBottom:8}}>📊</div>
          <div>조건에 맞는 기록이 없습니다</div>
        </div>
      )}
    </div>
  );
}

// ── 랜딩 페이지 (로그아웃 상태) ────────────────────
function LandingPage({ T, dark, setDark, onLogin }) {
  // ── 인앱 브라우저 감지 ──
  const ua = navigator.userAgent || "";
  const isInApp = /KAKAOTALK|NAVER|Instagram|FBAN|FBAV|Line\/|snapchat|Twitter/i.test(ua)
    || ((/iPhone|iPad|iPod/i.test(ua)) && !/Safari/i.test(ua) && !/Chrome/i.test(ua));
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const currentUrl = window.location.href;

  // 인앱 브라우저면 안내 화면 표시
  if (isInApp) {
    // 카카오톡 iOS: kakaoweb:// 스킴으로 Safari 강제 오픈
    const openInSafari = () => {
      // 방법1: kakaotalk이 지원하는 외부브라우저 스킴
      const safariUrl = currentUrl.replace(/^https?:\/\//, "");
      // 카카오톡 인앱에서 외부 브라우저로 이동하는 방법들 순차 시도
      window.location.href = `safari-https://${safariUrl}`;
      // 실패 대비 setTimeout으로 clipboard 복사 안내
      setTimeout(() => {
        navigator.clipboard?.writeText(currentUrl).catch(()=>{});
      }, 500);
    };

    return (
      <div style={{minHeight:"100vh",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:24,fontFamily:"'Apple SD Gothic Neo','Malgun Gothic',sans-serif"}}>
        <div style={{maxWidth:360,width:"100%",textAlign:"center"}}>
          <div style={{fontSize:56,marginBottom:12}}>🍁</div>
          <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:4}}>메이플 파밍 트래커</div>
          <div style={{fontSize:12,color:T.sub,marginBottom:20}}>카카오톡에서는 Google 로그인이 제한됩니다</div>

          <div style={{background:T.card,borderRadius:16,border:`1.5px solid #534AB7`,padding:"24px 20px",marginBottom:16}}>
            <div style={{fontSize:18,fontWeight:700,color:T.text,marginBottom:8}}>브라우저에서 열어주세요</div>
            <div style={{fontSize:13,color:T.sub,lineHeight:1.8,marginBottom:20}}>
              Google 정책으로 인해 카카오톡 내부<br/>브라우저에서는 로그인이 불가능해요.
            </div>

            {/* 안드로이드: Chrome으로 열기 */}
            {isAndroid&&(
              <a
                href={`intent://${currentUrl.replace(/^https?:\/\//,"")}#Intent;scheme=https;package=com.android.chrome;end`}
                style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,background:"#534AB7",color:"#fff",borderRadius:12,padding:"14px 0",fontSize:15,fontWeight:700,textDecoration:"none",marginBottom:10}}>
                🌐 Chrome으로 열기
              </a>
            )}

            {/* iOS: 버튼 클릭으로 Safari 시도 */}
            {isIOS&&(
              <div>
                <button
                  onClick={openInSafari}
                  style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,width:"100%",background:"#534AB7",color:"#fff",borderRadius:12,padding:"14px 0",fontSize:15,fontWeight:700,border:"none",cursor:"pointer",marginBottom:12}}>
                  🧭 Safari로 열기
                </button>
                {/* 버튼 안 될 때 대비 수동 안내 */}
                <div style={{background:dark?"#1A1A2E":"#F5F3FF",borderRadius:10,border:`0.5px solid #534AB744`,padding:"12px 14px",textAlign:"left",marginBottom:10}}>
                  <div style={{fontSize:11,fontWeight:600,color:"#534AB7",marginBottom:6}}>버튼이 안 되면 👇</div>
                  <div style={{fontSize:12,color:T.sub,lineHeight:2}}>
                    <div>① 오른쪽 하단 <strong style={{color:T.text}}>점 세개 [···]</strong> 탭</div>
                    <div>② <strong style={{color:T.text}}>"기본 브라우저로 열기"</strong> 선택</div>
                  </div>
                </div>
              </div>
            )}

            {/* 링크 복사 (공통) */}
            <button
              onClick={()=>{
                if(navigator.clipboard) {
                  navigator.clipboard.writeText(currentUrl)
                    .then(()=>alert("✅ 주소 복사 완료!\n\nChrome 또는 Safari 주소창에 붙여넣기 해주세요."))
                    .catch(()=>alert("주소: "+currentUrl));
                } else {
                  alert("주소를 직접 복사해주세요:\n"+currentUrl);
                }
              }}
              style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,width:"100%",background:"transparent",border:`1px solid ${T.border}`,color:T.sub,borderRadius:12,padding:"12px 0",fontSize:13,cursor:"pointer"}}>
              📋 링크 복사하기
            </button>
          </div>

          {/* 주소 직접 표시 */}
          <div style={{background:T.card,borderRadius:10,border:`0.5px solid ${T.border}`,padding:"10px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
            <span style={{fontSize:12,color:"#534AB7",fontWeight:600,flex:1,textAlign:"left",wordBreak:"break-all"}}>
              mapel-farming-tracker.web.app
            </span>
            <button
              onClick={()=>navigator.clipboard?.writeText(currentUrl).then(()=>alert("복사됐습니다!")).catch(()=>{})}
              style={{background:"#534AB722",border:"none",borderRadius:6,padding:"4px 8px",fontSize:11,color:"#534AB7",cursor:"pointer",flexShrink:0}}>
              복사
            </button>
          </div>
        </div>
      </div>
    );
  }
  const faqList = [
    ["보스 수익을 자동으로 계산할 수 있나요?","네! 주간보스, 일일보스 결정석 가격이 자동으로 계산됩니다. 파티 인원수에 따른 분배도 지원하며, 솔에르다 조각 시세 환산도 자동으로 이루어집니다."],
    ["메이플 파밍 트래커는 무료인가요?","네, 기본 기능은 완전 무료입니다. Google 계정으로 로그인하면 바로 사용할 수 있습니다. 광고 제거 등 편의 기능은 추후 선택적으로 제공될 예정입니다."],
    ["어떤 기기에서 사용할 수 있나요?","PC, 태블릿, 스마트폰 모두 지원합니다. 반응형으로 설계되어 PC에서는 사이드바, 모바일에서는 하단 탭바가 자동으로 표시됩니다."],
    ["캐릭터 정보는 어떻게 불러오나요?","넥슨 Open API와 연동되어 있어, 닉네임을 입력하면 캐릭터의 직업·레벨·스탯·장비·잠재능력 정보를 자동으로 불러옵니다."],
    ["보스 결정석 가격은 어떻게 계산되나요?","메이플스토리 공식 보스별 결정석 가격 기준으로 자동 계산됩니다. 파티 인원수에 따른 분배도 지원합니다. 일일/주간/월간 보스를 구분하여 기록합니다."],
    ["솔에르다 시세 기준은 무엇인가요?","솔에르다 조각의 메소 환산가를 기준으로 수익을 계산합니다. 이벤트·일반·사냥·교환불가 등 종류별로 구분하여 기록할 수 있습니다."],
    ["데이터는 어디에 저장되나요?","Google Firebase(클라우드)에 안전하게 저장됩니다. 기기를 바꿔도 같은 Google 계정으로 로그인하면 모든 기록이 그대로 유지됩니다."],
    ["여러 캐릭터를 동시에 관리할 수 있나요?","네, 즐겨찾기 기능으로 여러 캐릭터를 등록해두고 원클릭으로 전환하며 기록할 수 있습니다. 캐릭터별 통계도 개별 확인 가능합니다."],
    ["심볼 강화 비용도 기록할 수 있나요?","아케인포스·어센틱포스 심볼 강화 지출을 지역별로 구분하여 기록할 수 있습니다. 큐브, 스타포스, 주문서 등 다양한 지출 유형도 지원합니다."],
  ];
  const guideList = [
    {icon:"⚔️",title:"보스 수익 기록하는 법",steps:["홈 탭에서 캐릭터를 선택합니다.","수익 탭 → 보스를 선택합니다.","클리어한 보스에 체크합니다. 파티 인원이 있으면 인원수도 설정합니다.","저장을 누르면 결정석 가격이 자동 계산되어 기록됩니다.","같은 날 중복 저장 시 수정 모드로 자동 이동합니다."]},
    {icon:"✨",title:"솔에르다 수익 기록하는 법",steps:["수익 탭 → 사냥/일퀘를 선택합니다.","솔에르다 기운, 이벤트 조각, 일반 조각, 사냥 조각, 교환불가 조각 수를 입력합니다.","메소 획득량, 코어 젬스톤, 기타 수익도 함께 입력할 수 있습니다.","저장하면 솔에르다 환산가가 자동 계산되어 합산됩니다."]},
    {icon:"📊",title:"통계 활용하는 법",steps:["통계 탭에서 기간을 설정합니다. (주간·월간·커스텀 기간 모두 지원)","캐릭터를 복수 선택하거나 전체를 함께 볼 수 있습니다.","수익·지출·순손익을 한눈에 확인합니다.","일별 추이를 통해 파밍 효율을 분석합니다."]},
  ];
  const isDark = T.bg === "#111" || T.bg === "#0F0F0F" || T.bg === "#141414" || T.bg === "#121212";
  const bossList = {
    "📅 일일 보스":{color:"#3B6D11",bg:isDark?"#0F1A0F":"#EAF3DE",list:["자쿰","혼테일","매그너스","힐라","파풀라투스","피에르","반반","블러디퀸","벨룸","반 레온","아카이럼","핑크빈"]},
    "📆 주간 보스":{color:"#534AB7",bg:isDark?"#1A1A2E":"#EEEDFE",list:["스우","데미안","루시드","윌","칼로스","세렌","카링","림보","발드릭스","유피테르","최초의 대적자","듄켈","진 힐라","검은 마법사 (이지/노말)"]},
    "🗓️ 월간 보스":{color:"#A32D2D",bg:isDark?"#1A0F0F":"#FCEBEB",list:["검은 마법사 (하드)","검은 마법사 (익스트림)"]},
  };
  return (
    <div style={{minHeight:"100vh",background:T.bg,fontFamily:"'Apple SD Gothic Neo','Malgun Gothic',sans-serif"}}>
      <div style={{background:T.card,borderBottom:`0.5px solid ${T.border}`,padding:"0 24px",display:"flex",alignItems:"center",height:52,position:"sticky",top:0,zIndex:100}}>
        <span style={{fontSize:20,marginRight:8}}>🍁</span>
        <span style={{fontSize:16,fontWeight:600,color:T.text,flex:1}}>메이플 파밍 트래커</span>
        <button onClick={()=>setDark(d=>!d)} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:18,marginRight:10}}>{isDark?"☀️":"🌙"}</button>
        <button onClick={onLogin} style={{background:"#534AB7",color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:600,cursor:"pointer"}}>시작하기</button>
      </div>
      <div style={{maxWidth:860,margin:"0 auto",padding:"64px 20px 40px",textAlign:"center"}}>
        <div style={{fontSize:72,marginBottom:16,lineHeight:1}}>🍁</div>
        <h1 style={{fontSize:32,fontWeight:700,color:T.text,marginBottom:14,lineHeight:1.3}}>메이플 파밍 수익,<br/>자동으로 계산하고 관리하세요</h1>
        <p style={{fontSize:16,color:T.sub,marginBottom:10,lineHeight:1.8}}>메이플 파밍 트래커는 메이플스토리 파밍 유저를 위한 수익·지출 전문 관리 도구입니다.<br/>보스 결정석 자동 계산, 솔에르다 시세 환산, 캐릭터 INFO 조회, 주간 수익 통계까지 모두 무료로 제공합니다.</p>
        <p style={{fontSize:14,color:T.sub,marginBottom:36,lineHeight:1.7}}>Google 계정 하나로 PC·모바일 어디서나 동기화되며, 여러 캐릭터를 동시에 관리할 수 있습니다.</p>
        <button onClick={onLogin} style={{background:"#534AB7",color:"#fff",border:"none",borderRadius:12,padding:"16px 48px",fontSize:16,fontWeight:700,cursor:"pointer",boxShadow:"0 4px 20px rgba(83,74,183,0.35)"}}>Google 계정으로 무료 시작하기</button>
        <p style={{fontSize:12,color:T.sub,marginTop:12}}>로그인 필요 · 광고 없는 무료 서비스</p>
      </div>
      <div style={{maxWidth:860,margin:"0 auto",padding:"0 20px 48px"}}>
        <h2 style={{fontSize:20,fontWeight:700,color:T.text,marginBottom:6,textAlign:"center"}}>주요 기능</h2>
        <p style={{fontSize:13,color:T.sub,textAlign:"center",marginBottom:24}}>메이플 파밍에 필요한 모든 기록 기능을 제공합니다</p>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:12}}>
          {[["⚔️","보스 수익 자동 계산","일일·주간·월간 보스 결정석 가격 자동 반영. 파티 인원별 분배 계산. 중복 저장 방지 및 수정 모드 지원."],["✨","솔에르다 수익 기록","솔에르다 기운, 이벤트/일반/사냥/교환불가 조각 종류별 입력. 메소 환산가 자동 계산."],["📊","수익·지출 통계","캐릭터별·기간별 수익, 지출, 순손익 분석. 누적·월간·주간·일간 대시보드 제공."],["🔍","캐릭터 INFO","닉네임 입력 시 넥슨 API로 스탯·장비·잠재능력 자동 조회. 즐겨찾기 등록 지원."],["💸","지출 유형별 기록","큐브, 스타포스, 주문서, 심볼업(아케인/어센틱), 보스 입장권, 캐시 아이템 등 지출 구분 기록."],["☁️","클라우드 동기화","Google Firebase 기반 실시간 저장. PC·모바일 동시 접속. 기기 바꿔도 데이터 유지."],["⭐","즐겨찾기 캐릭터","자주 기록하는 캐릭터를 즐겨찾기로 등록. 드롭다운 원클릭으로 캐릭터 전환."],["🌙","다크/라이트 모드","눈에 편한 다크모드와 라이트모드 지원. PC 사이드바·모바일 하단탭 자동 분기."]].map(([icon,title,desc])=>(
            <div key={title} style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:"18px 16px"}}>
              <div style={{fontSize:28,marginBottom:10}}>{icon}</div>
              <div style={{fontSize:14,fontWeight:600,color:T.text,marginBottom:8}}>{title}</div>
              <div style={{fontSize:12,color:T.sub,lineHeight:1.7}}>{desc}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{maxWidth:860,margin:"0 auto",padding:"0 20px 48px"}}>
        <h2 style={{fontSize:20,fontWeight:700,color:T.text,marginBottom:6,textAlign:"center"}}>사용 가이드</h2>
        <p style={{fontSize:13,color:T.sub,textAlign:"center",marginBottom:24}}>처음 사용하시는 분도 쉽게 따라할 수 있습니다</p>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:14}}>
          {guideList.map(({icon,title,steps})=>(
            <div key={title} style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:"18px 16px"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}><span style={{fontSize:24}}>{icon}</span><span style={{fontSize:14,fontWeight:600,color:T.text}}>{title}</span></div>
              <ol style={{margin:0,paddingLeft:18}}>{steps.map((s,i)=><li key={i} style={{fontSize:12,color:T.sub,lineHeight:1.8,marginBottom:4}}>{s}</li>)}</ol>
            </div>
          ))}
        </div>
      </div>
      <div style={{maxWidth:860,margin:"0 auto",padding:"0 20px 48px"}}>
        <h2 style={{fontSize:20,fontWeight:700,color:T.text,marginBottom:6,textAlign:"center"}}>지원 보스 목록</h2>
        <p style={{fontSize:13,color:T.sub,textAlign:"center",marginBottom:24}}>메이플스토리 주요 보스의 결정석 가격이 자동으로 계산됩니다</p>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {Object.entries(bossList).map(([label,{color,bg,list}])=>(
            <div key={label} style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:"16px"}}>
              <div style={{fontSize:13,fontWeight:600,color,marginBottom:10}}>{label}</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>{list.map(b=><span key={b} style={{background:bg,borderRadius:20,padding:"4px 12px",fontSize:12,color,fontWeight:500}}>{b}</span>)}</div>
            </div>
          ))}
        </div>
        <p style={{fontSize:12,color:T.sub,marginTop:12,textAlign:"center"}}>* 결정석 가격은 메이플스토리 공식 기준이며, 업데이트에 따라 변경될 수 있습니다.</p>
      </div>
      <div style={{maxWidth:860,margin:"0 auto",padding:"0 20px 48px"}}>
        <h2 style={{fontSize:20,fontWeight:700,color:T.text,marginBottom:6,textAlign:"center"}}>앱 구성 안내</h2>
        <p style={{fontSize:13,color:T.sub,textAlign:"center",marginBottom:24}}>6개 탭으로 구성된 직관적인 인터페이스</p>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:10}}>
          {[["🏠","홈","대시보드에서 누적·월간·주간·일간 수익과 지출을 한눈에 확인. 캐릭터별 필터로 개별 현황도 조회 가능."],["💰","수익","사냥, 보스, 이벤트, 물욕템, 에픽던전 주화 등 수익 유형별 상세 기록."],["💸","지출","큐브, 스타포스, 심볼업 등 지출 항목 기록. 아케인/어센틱포스 지역별 심볼업 구분."],["📋","조회/수정","기록된 내역을 카드·테이블 뷰로 확인. 아코디언으로 세부사항 펼치기. 수정·삭제 즉시 가능."],["📊","통계","기간·캐릭터 멀티 선택으로 수익 추이 분석. 순손익 마이너스 포함 정밀 통계."],["⚙️","설정","캐릭터 즐겨찾기 관리, 테마 설정, 계정 정보 확인."]].map(([icon,tab,desc])=>(
            <div key={tab} style={{background:T.card,borderRadius:10,border:`0.5px solid ${T.border}`,padding:"14px"}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}><span style={{fontSize:18}}>{icon}</span><span style={{fontSize:13,fontWeight:600,color:"#534AB7"}}>{tab}</span></div>
              <div style={{fontSize:11,color:T.sub,lineHeight:1.7}}>{desc}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{maxWidth:860,margin:"0 auto",padding:"0 20px 48px"}}>
        <h2 style={{fontSize:20,fontWeight:700,color:T.text,marginBottom:6,textAlign:"center"}}>자주 묻는 질문</h2>
        <p style={{fontSize:13,color:T.sub,textAlign:"center",marginBottom:24}}>궁금한 점이 있으시면 아래를 확인해주세요</p>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {faqList.map(([q,a])=>(
            <div key={q} style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:"16px 18px"}}>
              <div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:8}}>Q. {q}</div>
              <div style={{fontSize:12,color:T.sub,lineHeight:1.8}}>A. {a}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{maxWidth:860,margin:"0 auto",padding:"0 20px 48px"}}>
        <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:"24px 20px"}}>
          <h2 style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:12}}>메이플 파밍 트래커 소개</h2>
          <p style={{fontSize:13,color:T.sub,lineHeight:1.9,marginBottom:12}}>메이플 파밍 트래커는 메이플스토리 파밍을 즐기는 유저들이 보스 수익과 파밍 지출을 체계적으로 관리할 수 있도록 만들어진 무료 웹 서비스입니다. 주간보스 결정석 가격 자동 계산, 솔에르다 시세 환산, 일반 메소 등 다양한 수익원을 한 곳에 기록하고 분석할 수 있습니다.</p>
          <p style={{fontSize:13,color:T.sub,lineHeight:1.9,marginBottom:12}}>메이플스토리에서 파밍이란 반복적인 사냥이나 보스 레이드를 통해 메소 및 재화를 획득하는 활동을 말합니다. 효율적인 파밍 관리를 위해서는 어떤 콘텐츠에서 얼마를 벌었는지, 큐브·스타포스 강화에 얼마를 지출했는지 정확히 파악하는 것이 중요합니다. 메이플 파밍 트래커는 이 과정을 자동화하고 시각화하여 제공합니다.</p>
          <p style={{fontSize:13,color:T.sub,lineHeight:1.9}}>넥슨 Open API와 연동하여 캐릭터 정보를 실시간으로 불러오고, Google Firebase를 통해 클라우드에 안전하게 저장됩니다. PC와 모바일 모두 최적화되어 있으며, 여러 캐릭터를 동시에 관리하는 다계정 파밍 유저에게도 적합합니다.</p>
        </div>
      </div>
      <div style={{maxWidth:860,margin:"0 auto",padding:"0 20px 60px",textAlign:"center"}}>
        <div style={{background:"#534AB7",borderRadius:16,padding:"40px 24px"}}>
          <div style={{fontSize:40,marginBottom:14}}>🍁</div>
          <h2 style={{fontSize:22,fontWeight:700,color:"#fff",marginBottom:10}}>지금 바로 파밍 기록을 시작하세요</h2>
          <p style={{fontSize:14,color:"rgba(255,255,255,0.85)",marginBottom:8,lineHeight:1.7}}>Google 계정만 있으면 무료로 사용할 수 있습니다.</p>
          <p style={{fontSize:13,color:"rgba(255,255,255,0.7)",marginBottom:24}}>보스 결정석 자동 계산 · 솔에르다 환산 · 캐릭터 INFO · 통계 분석</p>
          <button onClick={onLogin} style={{background:"#fff",color:"#534AB7",border:"none",borderRadius:10,padding:"14px 40px",fontSize:15,fontWeight:700,cursor:"pointer"}}>무료로 시작하기</button>
        </div>
      </div>
      <div style={{borderTop:`0.5px solid ${T.border}`,padding:"24px",textAlign:"center",color:T.sub,fontSize:11,lineHeight:1.8}}>
        <p style={{marginBottom:4,fontWeight:600,fontSize:12,color:T.text}}>🍁 메이플 파밍 트래커</p>
        <p style={{marginBottom:6}}>메이플스토리 파밍 수익·지출 관리 도구 · Google 계정 기반 클라우드 저장</p>
        <p style={{marginBottom:10,fontSize:10,color:dark?"#555577":"#aaaacc",lineHeight:1.7}}>
          본 서비스는 넥슨(NEXON Co., Ltd.)의 메이플스토리 게임을 기반으로 한 비공식 팬 제작 유틸리티입니다.<br/>
          메이플스토리 및 관련 콘텐츠의 저작권은 넥슨에 있으며, 본 사이트는 넥슨과 공식 제휴 관계가 아닙니다.<br/>
          캐릭터 정보 조회는 넥슨 Open API를 통해 제공됩니다.
        </p>
        <p style={{marginBottom:6}}>
          <a href="/about" style={{color:"#534AB7",marginRight:12}}>서비스 소개</a>
          <a href="/privacy.html" style={{color:"#534AB7",marginRight:12}}>개인정보처리방침</a>
          <a href="/terms.html" style={{color:"#534AB7",marginRight:12}}>이용약관</a>
        </p>
        <p style={{fontSize:10,color:dark?"#444466":"#bbbbcc"}}>© 2026 메이플 파밍 트래커 · All rights reserved.</p>
      </div>
    </div>
  );
}


function SelBarComp({ T, selChar, favorites, setSelChar, handleLoadInfo }) {
  const [open, setOpen] = useState(false);
  if (selChar) return (
    <div style={{background:T.charBg,borderRadius:10,border:"0.5px solid #534AB7",padding:"10px 14px",marginBottom:10,display:"flex",alignItems:"center",gap:10}}>
      {selChar.image&&<CharAvatar src={selChar.image} alt={selChar.nickname} size={42}/>}
      <div style={{flex:1}}>
        <div style={{fontSize:14,fontWeight:500,color:T.text}}>{selChar.nickname}</div>
        <div style={{fontSize:11,color:T.sub}}>{selChar.job} · {selChar.world} · Lv.{selChar.level}</div>
      </div>
      <button onClick={()=>handleLoadInfo(selChar.nickname)} style={{background:"transparent",border:`0.5px solid #534AB7`,borderRadius:8,padding:"5px 10px",fontSize:11,color:"#534AB7",cursor:"pointer",marginRight:4}}>📊 INFO</button>
      <div style={{position:"relative"}}>
        <button onClick={()=>setOpen(o=>!o)} style={{background:"transparent",border:`0.5px solid ${T.border}`,borderRadius:8,padding:"6px 12px",fontSize:12,color:T.sub,cursor:"pointer"}}>변경 ▾</button>
        {open&&(
          <div style={{position:"absolute",right:0,top:"110%",background:T.card,border:`0.5px solid ${T.border}`,borderRadius:10,zIndex:200,minWidth:160,boxShadow:"0 4px 16px rgba(0,0,0,0.12)"}}>
            {favorites.length===0?(
              <div style={{padding:"12px 14px",fontSize:12,color:T.sub}}>즐겨찾기가 없습니다</div>
            ):favorites.map(char=>(
              <div key={char.nickname} onClick={()=>{setSelChar(char);setOpen(false);}}
                style={{display:"flex",alignItems:"center",gap:8,padding:"10px 14px",cursor:"pointer",borderBottom:`0.5px solid ${T.border}`}}>
                {char.image&&<CharAvatar src={char.image} alt={char.nickname} size={34}/>}
                <div>
                  <div style={{fontSize:13,fontWeight:500,color:T.text}}>{char.nickname}</div>
                  <div style={{fontSize:11,color:T.sub}}>{char.job}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
  return (
    <div style={{position:"relative",marginBottom:10}}>
      <div onClick={()=>setOpen(o=>!o)} style={{background:T.charBg,borderRadius:10,border:"0.5px dashed #534AB7",padding:"12px 14px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <span style={{fontSize:13,color:"#534AB7",fontWeight:500}}>⭐ 캐릭터 선택</span>
        <span style={{fontSize:13,color:"#534AB7"}}>▾</span>
      </div>
      {open&&(
        <div style={{position:"absolute",top:"110%",left:0,right:0,background:T.card,border:`0.5px solid ${T.border}`,borderRadius:10,zIndex:200,boxShadow:"0 4px 16px rgba(0,0,0,0.12)"}}>
          {favorites.length===0?(
            <div style={{padding:"12px 14px",fontSize:12,color:T.sub}}>홈에서 캐릭터를 즐겨찾기에 추가해주세요</div>
          ):favorites.map(char=>(
            <div key={char.nickname} onClick={()=>{setSelChar(char);setOpen(false);}}
              style={{display:"flex",alignItems:"center",gap:8,padding:"10px 14px",cursor:"pointer",borderBottom:`0.5px solid ${T.border}`}}>
              {char.image&&<CharAvatar src={char.image} alt={char.nickname} size={34}/>}
              <div>
                <div style={{fontSize:13,fontWeight:500,color:T.text}}>{char.nickname}</div>
                <div style={{fontSize:11,color:T.sub}}>{char.job} · Lv.{char.level}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 메인 앱 ─────────────────────────────────────────
export default function App() {
  const isMobile = useIsMobile();
  const [user,          setUser]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [records,       setRecords]       = useState([]);
  const [notices,       setNotices]       = useState([]);
  const [noticePopup,   setNoticePopup]   = useState(null);  // 팝업에 표시할 공지
  const [noPopupCheck,  setNoPopupCheck]  = useState(false); // "다음 공지까지 보지 않기" 체크
  const [dark,          setDark]          = useState(()=>localStorage.getItem("dark")==="true");
  const [tab,           setTab]           = useState("home");
  const [selChar,       setSelChar]       = useState(null);
  const [favorites,     setFavorites]     = useState([]);
  const [saving,        setSaving]        = useState(false);
  const [showNotices,   setShowNotices]   = useState(false);
  const [showAdminInq,  setShowAdminInq]  = useState(false); // 관리자 건의함 모달
  const [openNoticeId,  setOpenNoticeId]  = useState(null);
  const [noticeFilter,  setNoticeFilter]  = useState("all");
  const [showNoticeManager, setShowNoticeManager] = useState(false); // 공지관리 모달
  const [noticeChecked,     setNoticeChecked]     = useState({});    // 체크된 공지 id
  const [bulkCategory,      setBulkCategory]      = useState("patch"); // 일괄 변경 카테고리
  const [editingNotice,     setEditingNotice]      = useState(null);  // 인라인 수정 중인 공지
  const [noticeForm,    setNoticeForm]    = useState(false);
  const [editNoticeId,  setEditNoticeId]  = useState(null);
  const [nTitle,        setNTitle]        = useState("");
  const [nContent,      setNContent]      = useState("");
  const [nPinned,       setNPinned]       = useState(false);
  const [nCategory,     setNCategory]     = useState("patch"); // patch|notice|event
  const [nSummary,      setNSummary]      = useState("");
  const [nThumb,        setNThumb]        = useState("");      // 썸네일 URL or 파일
  const [nThumbFile,    setNThumbFile]    = useState(null);
  const [detailRec,     setDetailRec]     = useState(null);
  const [editMode,      setEditMode]      = useState(false);
  const [charMoveMode,  setCharMoveMode]  = useState(false); // 캐릭터 이동 모드
  const [charMoveTarget, setCharMoveTarget] = useState(""); // 이동할 캐릭터 닉네임
  const [editDate,      setEditDate]      = useState("");
  const [editType,      setEditType]      = useState("");
  const [editMins,      setEditMins]      = useState("");
  const [editSol0,      setEditSol0]      = useState("");
  const [editSol1,      setEditSol1]      = useState("");
  const [editSol2,      setEditSol2]      = useState("");
  const [editSol3,      setEditSol3]      = useState("");
  const [editSol4,      setEditSol4]      = useState("");
  const [editMeso,      setEditMeso]      = useState("");
  const [editCore,      setEditCore]      = useState("");
  const [editEtc,       setEditEtc]       = useState("");
  const [editMile,      setEditMile]      = useState("");
  const [editNote,      setEditNote]      = useState("");
  const [editExpMeso,   setEditExpMeso]   = useState("");
  const [editExpCash,   setEditExpCash]   = useState("");
  const [editExpMile,   setEditExpMile]   = useState("");
  const [editExpType,   setEditExpType]   = useState("");
  const [editSymbolForce, setEditSymbolForce] = useState("");
  const [editSymbolArea,  setEditSymbolArea]  = useState("");
  const [editSymCalcOpen, setEditSymCalcOpen] = useState(false);
  const [editSymCurLv,    setEditSymCurLv]    = useState("");
  const [editSymTgtLv,    setEditSymTgtLv]    = useState("");
  const [editCheckedBosses,    setEditCheckedBosses]    = useState({});
  const [editBossParty,        setEditBossParty]        = useState({});
  const [editBossPartyMembers, setEditBossPartyMembers] = useState({});
  const [editBossDiffs,        setEditBossDiffs]        = useState({});
  const [editBossPricePhaseMap, setEditBossPricePhaseMap] = useState({}); // 수정 모달: 보스별 패치 전/후 선택
  const [searchNick,    setSearchNick]    = useState("");
  const [searchResult,  setSearchResult]  = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError,   setSearchError]   = useState("");
  const [infoChar,      setInfoChar]      = useState(null);
  const [infoLoading,   setInfoLoading]   = useState(false);
  const [infoError,     setInfoError]     = useState("");
  const [showInfo,      setShowInfo]      = useState(false);
  const [checkedBosses, setCheckedBosses] = useState({});
  const [bossDropList, setBossDropList] = useState([]); // BossSection에서 체크된 드롭 아이템 목록
  const [bossParty,     setBossParty]     = useState({});
  const [bossPartyMembers, setBossPartyMembers] = useState({});
  const [bossDiffs,     setBossDiffs]     = useState({});
  // 패치 당일(예: 결정석 너프일) 보스별 가격 전/후 선택 — { [bkey]: "before"|"after" }, 미지정 시 "after"
  const [bossPricePhaseMap, setBossPricePhaseMap] = useState({});
  // ── 보스 프리셋 ──
  const [presets, setPresets] = useState(null); // 현재 selChar의 프리셋 {preset1,preset2,preset3}
  const [presetModalOpen, setPresetModalOpen] = useState(false);
  const [presetEditSlot, setPresetEditSlot] = useState("preset1");
  const [presetWidgetSlot, setPresetWidgetSlot] = useState("preset1");
  const [presetWidgetChecks, setPresetWidgetChecks] = useState({}); // 위젯 내 체크 해제용 {bossIdx: true/false}
  const [presetWidgetCharNick, setPresetWidgetCharNick] = useState(""); // 위젯 전용 캐릭터 선택 (selChar와 독립)
  const [presetWidgetPricePhaseMap, setPresetWidgetPricePhaseMap] = useState({}); // 프리셋 위젯: 보스별(idx) 패치 전/후 선택
  // 설정 모달 내부 편집용 임시 보스 체크 state (BossSection 재사용)
  const [pmCheckedBosses, setPmCheckedBosses] = useState({});
  const [pmBossParty, setPmBossParty] = useState({});
  const [pmBossPartyMembers, setPmBossPartyMembers] = useState({});
  const [pmBossDiffs, setPmBossDiffs] = useState({});
  const [presetNameInput, setPresetNameInput] = useState("");
  const [incDate, setIncDate] = useState(today());
  const [incType, setIncType] = useState("hunt");
  const [incMins, setIncMins] = useState("");
  // ── 에픽던전 state ──
  const [epicDungeon,  setEpicDungeon]  = useState("highmountain");
  const [epicExpLv,    setEpicExpLv]    = useState(0);
  const [epicSolLv,    setEpicSolLv]    = useState(0);
  const [epicMp,       setEpicMp]       = useState("");
  const [incSol0, setIncSol0] = useState("");
  const [incSol1, setIncSol1] = useState("");
  const [incSol2, setIncSol2] = useState("");
  const [incSol3, setIncSol3] = useState("");
  const [incSol4, setIncSol4] = useState("");
  // ── 드롭 아이템 수량 { milk:"", tdew:"", ddew:"", cheese:"" } ──
  const [dropQty, setDropQty] = useState(() => Object.fromEntries(DROP_ITEMS.map(d=>[d.key,""])));
  const [incMeso, setIncMeso] = useState("");
  const [incCore, setIncCore] = useState("");
  const [incEtc,  setIncEtc]  = useState("");
  const [incMile, setIncMile] = useState("");
  const [incNote, setIncNote] = useState("");
  const [expDate, setExpDate] = useState(today());
  const [expType, setExpType] = useState("cube");
  const [expMeso, setExpMeso] = useState("");
  const [expCash, setExpCash] = useState("");
  const [expMile, setExpMile] = useState("");
  const [expNote, setExpNote] = useState("");
  const [expSymbolForce, setExpSymbolForce] = useState("arcane"); // "arcane"|"authentic"|"grand"
  // symLevels: { "소멸의여로": { cur:"", tgt:"" }, ... }
  const initSymLevels = (type) => {
    const areas = type === "arcane" ? ARCANE_AREAS : type === "grand" ? GRAND_AUTHENTIC_AREAS : AUTHENTIC_AREAS;
    return Object.fromEntries(areas.map(a => [a, { cur:"", tgt:"" }]));
  };
  const [symLevels, setSymLevels] = useState(() => initSymLevels("arcane"));
  // ── 환전 탭 ──
  const [exchDir,     setExchDir]     = useState("buy");  // "buy"=캐시→메소, "sell"=메소→캐시
  const [exchMeso,    setExchMeso]    = useState("");    // 거래 메소 (억 단위)
  const [exchMp,      setExchMp]      = useState("");    // 실지불/수령 메포
  const [exchMarket,  setExchMarket]  = useState("");    // 시장가 (1억당 메포)
  const [exchDate,    setExchDate]    = useState(today());
  const [exchNote,    setExchNote]    = useState("");
  // ── 경매장 탭 ──
  const [mktTab,    setMktTab]    = useState("sell"); // "sell" | "buy"
  const [mktMode,   setMktMode]   = useState("inventory"); // "inventory" | "direct"
  const [mktDate,   setMktDate]   = useState(today());
  const [mktNote,   setMktNote]   = useState("");
  const [mktSelIds, setMktSelIds] = useState([]); // 선택된 보관함 아이템 ids
  const [mktPrices, setMktPrices] = useState({}); // {id: 실제판매가}
  const [mktQtys,   setMktQtys]   = useState({}); // {id: 수량}
  const [mktFee,    setMktFee]    = useState(5);   // 수수료 3 or 5
  const [mktItems,  setMktItems]  = useState([{name:"",price:"",qty:1}]); // 직접입력
  // 구매 탭
  const [mktBuyName,  setMktBuyName]  = useState("");
  const [mktBuyPrice, setMktBuyPrice] = useState("");
  const [mktBuyQty,   setMktBuyQty]   = useState(1);
  const [mktBuyMp,    setMktBuyMp]    = useState(""); // 메포 입력
  const [mktBuyNote,  setMktBuyNote]  = useState("");
  const [logTab,   setLogTab]   = useState("all");
  const [logDateF, setLogDateF] = useState("");
  const [logMonF,  setLogMonF]  = useState("");
  const [logBossWeek, setLogBossWeek] = useState(thisWeekStart()); // 보스탭 전용 주차
  const [logBossNavYear,  setLogBossNavYear]  = useState(+today().slice(0,4));
  const [logBossNavMonth, setLogBossNavMonth] = useState(+today().slice(5,7));
  const [logCharF, setLogCharF] = useState(""); // 캐릭터 필터
  const [logTypeF, setLogTypeF] = useState(""); // 수익 유형 필터
  const [logView,  setLogView]  = useState("card"); // "card" | "table"
  const [expandedLogId, setExpandedLogId] = useState(null); // 펼쳐진 카드 id
  const [dashChar,      setDashChar]      = useState("all");
  const [midnightAlert, setMidnightAlert] = useState(null);
  const [toast,         setToast]         = useState(null);
  const [favDraft,      setFavDraft]      = useState({}); // 캐릭터별 설정 임시 draft
  const [inventory,     setInventory]     = useState([]); // 물욕템 보관함
  const [dropItems,     setDropItems]     = useState([{name:"",price:"",instant:false}]); // 물욕템 입력 리스트
  // ── 건의함 ──
  const [inquiries,     setInquiries]     = useState([]);
  const [allInquiries,  setAllInquiries]  = useState([]);
  const [inqTitle,      setInqTitle]      = useState("");
  const [inqContent,    setInqContent]    = useState("");
  const [inqOpen,       setInqOpen]       = useState(null);
  // ── 기프티콘/커피 후원 ──
  const [giftNote,      setGiftNote]      = useState("");
  const [giftFile,      setGiftFile]      = useState(null);
  const [giftSending,   setGiftSending]   = useState(false);
  const [showDonation,  setShowDonation]  = useState(false); // 후원 팝업
  const [showGifts,     setShowGifts]     = useState(false); // 관리자 후원 내역
  const [gifts,         setGifts]         = useState([]);    // 후원 내역 목록
  const [inqReply,      setInqReply]      = useState("");

  const showToast = (msg, icon="📅") => {
    setToast({msg, icon});
    setTimeout(()=>setToast(null), 3500);
  };

  const T       = getTheme(dark);
  const isAdmin = user?.uid === ADMIN_UID;

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `@keyframes fadeInUp { from { opacity:0; transform:translateX(-50%) translateY(12px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }`;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => { localStorage.setItem("dark", dark); }, [dark]);

  // ── 자정 감지: 날짜 자동 갱신 + 토스트 ──
  useEffect(() => {
    let lastDate = today();
    const interval = setInterval(() => {
      const now = today();
      if (now !== lastDate) {
        lastDate = now;
        // 날짜 input 자동 갱신
        setIncDate(now);
        setExpDate(now);
        // 토스트 알림
        showToast("날짜가 변경되었습니다. 날짜를 확인해주세요.", "🌙");
      }
    }, 30000); // 30초마다 체크
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => { setUser(u); setLoading(false); });
    return () => unsub();
  }, []);

  useEffect(() => { if (!user) return; return subscribeRecords(user.uid, setRecords); }, [user]);
  useEffect(() => { if (!user) return; return subscribeInventory(user.uid, setInventory); }, [user]);
  // ── 프리셋: 위젯 전용 캐릭터 선택 기준 구독 (selChar와 독립) ──
  useEffect(() => {
    // 위젯에서 캐릭터가 아직 선택 안 됐으면 selChar 또는 첫 즐겨찾기로 기본값 세팅
    if (!presetWidgetCharNick) {
      if (selChar?.nickname) setPresetWidgetCharNick(selChar.nickname);
      else if (favorites.length>0) setPresetWidgetCharNick(favorites[0].nickname);
    }
  }, [selChar?.nickname, favorites]);
  useEffect(() => {
    if (!user || !presetWidgetCharNick) { setPresets(null); return; }
    return subscribePresets(user.uid, presetWidgetCharNick, (data) => {
      setPresets(data || emptyPresets());
    });
  }, [user, presetWidgetCharNick]);
  useEffect(() => { if (!user) return; return subscribeMyInquiries(user.uid, setInquiries); }, [user]);
  useEffect(() => { if (!user || !isAdmin) return; return subscribeAllInquiries(setAllInquiries); }, [user, isAdmin]);

  // ── 핑크빈 노멀 결정석 가격 소급 수정 ──────────────
  // 기존 6,580,000 → 올바른 799,000 으로 자동 보정
  useEffect(() => {
    if (!user || records.length === 0) return;
    const WRONG = 6580000;
    const RIGHT = 799000;
    const fix = async () => {
      for (const r of records) {
        if (r.kind !== "income" || r.type !== "boss") continue;
        if (!Array.isArray(r.bossList)) continue;
        const pb = r.bossList.find(b => b.name === "핑크빈 (노멀)" && (b.crystal === WRONG || b.crystal === Math.floor(WRONG / (b.party||1))));
        if (!pb) continue;
        // 보정된 bossList
        const newList = r.bossList.map(b => {
          if (b.name !== "핑크빈 (노멀)") return b;
          return { ...b, crystal: Math.floor(RIGHT / (b.party||1)) };
        });
        const newBossIncome = newList.reduce((s,b) => s + (b.crystal||0), 0);
        const diff = newBossIncome - (r.bossIncome||0);
        const newTotal = (r.totalIncome||0) + diff;
        await updateRecord(user.uid, r.id, {
          bossList: newList,
          bossIncome: newBossIncome,
          totalIncome: newTotal,
        });
      }
    };
    fix();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, records.length]); // records.length로 최초 로드 시 1회만 실행
  useEffect(() => {
    if (!user) return;
    getFavorites(user.uid).then(async (f) => {
      if (!Array.isArray(f)) return;
      setFavorites(f);
      // 즐겨찾기 캐릭터 레벨/정보 자동 갱신 (24시간 캐시 활용)
      const updated = await Promise.all(f.map(async (fav) => {
        if (!fav.nickname) return fav;
        try {
          const res = await fetch(`${FUNCTION_URL}?nickname=${encodeURIComponent(fav.nickname)}&lang=ko`);
          if (!res.ok) return fav;
          const data = await res.json();
          if (data.level && (data.level !== fav.level || data.job !== fav.job)) {
            return { ...fav, level: data.level, job: data.job, image: data.image||fav.image, world: data.world||fav.world };
          }
        } catch { /* 실패 시 기존 데이터 유지 */ }
        return fav;
      }));
      // 변경된 게 있을 때만 저장
      const hasChange = updated.some((u,i) => u.level !== f[i].level || u.job !== f[i].job);
      if (hasChange) {
        setFavorites(updated);
        await saveFavorites(user.uid, updated);
      }
    });
  }, [user]);
  useEffect(() => { return subscribeNotices(setNotices); }, []);

  // 관리자 후원 내역 불러오기
  useEffect(()=>{
    if(!isAdmin||!showGifts) return;
    (async()=>{
      const { getFirestore, collection, getDocs, orderBy, query } = await import("firebase/firestore");
      const db = getFirestore();
      const snap = await getDocs(query(collection(db,"gifts"), orderBy("createdAt","desc")));
      setGifts(snap.docs.map(d=>({id:d.id,...d.data()})));
    })();
  },[isAdmin, showGifts]);
  useEffect(() => {
    if(!notices.length) return;
    // noticeData.js 공지 + DB 공지 합쳐서 가장 최신 id 찾기
    const allNotices = [...getNotices(), ...notices];
    const latest = allNotices.sort((a,b)=>(b.date||"").localeCompare(a.date||""))[0];
    if(!latest) return;
    const latestId = latest.id;
    const seenId = localStorage.getItem("lastSeenNoticeId");
    if(seenId === latestId) return; // 이미 본 공지
    setNoticePopup(latest);
  }, [notices]);

  const solV     = ((+incSol1||0)+(+incSol2||0)+(+incSol3||0))*SOL_PRICE;
  const dropV    = DROP_ITEMS.reduce((s,d)=>s+(+dropQty[d.key]||0)*d.price, 0);
  const mesoV    = (+incMeso||0)+(+incEtc||0);
  // 체크된 보스 결정석 총액: "오늘"이 아니라 실제 기록 날짜(+보스별 선택된 패치 전/후)의 가격을 써야 함
  const bossTotal= Object.entries(checkedBosses).reduce((s,[k,checked])=>{
    if(!checked) return s;
    const parts=k.split("_"); const sec=parts[0]; const diff=parts[parts.length-1]; const bname=parts.slice(1,-1).join("_");
    const { crystal } = resolveBossPrice(incDate||today(), sec, bname, diff, bossPricePhaseMap, k);
    if(crystal==null) return s;
    const p=bossParty[k]||1;
    return s+Math.floor(crystal/p);
  },0);
  const epicV    = incType==="epic" ? (calcEpicReward(epicDungeon,epicExpLv,epicSolLv)?.mesoIncome||0) : 0;
  const totalInc = solV+dropV+mesoV+epicV+(incType==="boss"?bossTotal:0);
  const mins     = +incMins||0;
  const incRecs  = records.filter(r=>r.kind==="income");
  const expRecs  = records.filter(r=>r.kind==="expense");
  const pinnedCount = notices.filter(n=>n.pinned).length;
  const filteredLogs = records.filter(r=>{
    // 새 서브탭 필터
    if(logTab==="boss")   { if(!(r.kind==="income"&&r.type==="boss")) return false; }
    else if(logTab==="hunt")   { if(!(r.kind==="income"&&r.type==="hunt")) return false; }
    else if(logTab==="market") { if(!((r.kind==="income"&&(r.type==="market-sell"||r.type==="drop"))||(r.kind==="expense"&&r.type==="market"))) return false; }
    else if(logTab==="etc")    { if(r.type==="boss"||r.type==="hunt"||r.type==="market-sell"||r.type==="drop"||(r.kind==="expense"&&r.type==="market")) return false; }
    else if(logTab==="income") { if(r.kind!=="income") return false; }
    else if(logTab==="expense"){ if(r.kind!=="expense") return false; }
    // 날짜/캐릭터 필터
    if(logDateF&&r.date!==logDateF) return false;
    if(logMonF&&r.date.slice(0,7)!==logMonF) return false;
    if(logCharF&&(r.nickname||"공통")!==logCharF) return false;
    if(logTypeF&&r.type!==logTypeF) return false;
    return true;
  });

  // ── 심볼업 그룹핑: 같은 날짜+캐릭터+symbolForce → 하나의 그룹으로 묶기 ──
  const groupedLogs = (() => {
    const result = [];
    const usedIds = new Set();
    for (const r of filteredLogs) {
      if (usedIds.has(r.id)) continue;
      if (r.kind === "expense" && r.type === "symbol" && r.symbolForce) {
        // 같은 날짜 + 닉네임 + symbolForce인 symbol record 모두 수집
        const group = filteredLogs.filter(x =>
          x.kind === "expense" && x.type === "symbol" &&
          x.symbolForce === r.symbolForce &&
          x.date === r.date &&
          (x.nickname||"") === (r.nickname||"")
        );
        group.forEach(x => usedIds.add(x.id));
        if (group.length > 1) {
          // 그룹 가상 record 생성
          result.push({
            _isSymbolGroup: true,
            id: "symgrp_" + group.map(x=>x.id).join("_"),
            date: r.date,
            nickname: r.nickname,
            kind: "expense",
            type: "symbol",
            symbolForce: r.symbolForce,
            meso: group.reduce((s,x)=>s+(x.meso||0),0),
            note: r.note||"",
            _items: group, // 낱개 record 보관
          });
        } else {
          result.push(r);
          usedIds.add(r.id);
        }
      } else {
        result.push(r);
        usedIds.add(r.id);
      }
    }
    return result;
  })();

  const IS      = {padding:"7px 10px",borderRadius:8,border:`0.5px solid ${T.inpBdr}`,fontSize:13,background:T.inp,color:T.text,width:"100%",fontFamily:"Arial",boxSizing:"border-box"};
  const IS_NOEX = {...IS,background:dark?"#1A1A1A":"#F9F9F9",color:T.sub};
  const IS_TA   = {...IS,height:120,resize:"vertical"};
  const EXP_TYPE_MAP = {cube:"큐브 강화",star:"스타포스",spell:"주문서",symbol:"심볼업","boss-entry":"보스입장권",cash:"캐시",mp:"메이플포인트",market:"경매장",etc:"기타",drop:"물욕템",epic:"에픽던전주화"};

  const handleLogin = () => signInWithPopup(auth, provider);

  const handleSearch = async () => {
    if(!searchNick.trim()) return;
    setSearchLoading(true); setSearchError(""); setSearchResult(null);
    try {
      const res=await fetch(`${FUNCTION_URL}?nickname=${encodeURIComponent(searchNick.trim())}&lang=ko`);
      const data=await res.json();
      if(!res.ok) {
        if(res.status===404) setSearchError("존재하지 않는 캐릭터입니다. 닉네임을 확인해주세요.\n메이플랜드 등 사설 서버는 조회가 지원되지 않습니다.");
        else if(res.status===429) setSearchError("요청이 너무 많습니다. 잠시 후 다시 시도해주세요.");
        else setSearchError(data.error||"조회에 실패했습니다. 잠시 후 다시 시도해주세요.");
      } else {
        setSearchResult(data);
      }
    } catch(e) { setSearchError("네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요."); }
    setSearchLoading(false);
  };

  const handleLoadInfo = async (nickname) => {
    setInfoLoading(true); setInfoError(""); setInfoChar(null); setShowInfo(true);
    try {
      const res=await fetch(`${DETAIL_URL}?nickname=${encodeURIComponent(nickname)}&lang=ko`);
      const data=await res.json();
      if(!res.ok) {
        if(res.status===404) setInfoError("존재하지 않는 캐릭터입니다.");
        else if(res.status===429) setInfoError("요청이 너무 많습니다. 잠시 후 다시 시도해주세요.");
        else setInfoError(data.error||"조회에 실패했습니다.");
      } else {
        setInfoChar(data);
      }
    } catch(e) { setInfoError("네트워크 오류가 발생했습니다."); }
    setInfoLoading(false);
  };

  const addFavorite = async (char) => {
    if(favorites.find(f=>f.nickname===char.nickname)) return alert("이미 즐겨찾기에 있습니다");
    const next=[...favorites,char]; setFavorites(next);
    await saveFavorites(user.uid,next); alert(char.nickname+" 즐겨찾기 추가!");
  };
  const removeFavorite = async (nickname) => {
    if(!window.confirm(nickname+"을(를) 삭제할까요?")) return;
    const next=favorites.filter(f=>f.nickname!==nickname);
    setFavorites(next); await saveFavorites(user.uid,next);
  };
  const selectChar = (char) => { setSelChar(char); setTab("income"); };

  const handleSaveIncome = async () => {
    if(!selChar) return alert("홈에서 캐릭터를 먼저 선택해주세요");
    if(!incDate) return alert("날짜를 선택해주세요");
    // 자정 경과 체크: 설정된 날짜가 오늘과 다를 때만
    if(incDate !== today()) {
      setMidnightAlert({ kind:"income" }); return;
    }
    await _doSaveIncome(incDate);
  };

  const _doSaveIncome = async (dateToUse) => {
    if(!user) { showToast("로그인 후 이용할 수 있습니다","🔐"); return; }
    const usedDate = dateToUse || incDate;

    // 보스 중복 체크
    if(incType==="boss") {
      const dup=records.find(r=>r.kind==="income"&&r.type==="boss"&&r.nickname===selChar.nickname&&r.date===usedDate);
      if(dup) {
        alert("이미 오늘은 보스 수익을 기록하셨습니다.\n수정 페이지로 이동합니다.");
        openDetail(dup, true); return;
      }
    }
    setSaving(true);
    try {
      // 저장 시점에도 "오늘"이 아니라 실제 저장 날짜(usedDate) + 보스별 선택된 패치 전/후 가격을 사용
      const bossIncome=incType==="boss"?bossTotal:0;
      const checkedBossList=incType==="boss"
        ?Object.entries(checkedBosses).filter(([,v])=>v).map(([k])=>{
            const parts=k.split("_");
            if(parts.length<3) return null;
            const sec=parts[0]; const diff=parts[parts.length-1]; const bname=parts.slice(1,-1).join("_");
            const { crystal, changed, phase } = resolveBossPrice(usedDate, sec, bname, diff, bossPricePhaseMap, k);
            if(crystal==null) return null;
            const p=bossParty[k]||1;
            const members=(bossPartyMembers[k]||[]).filter(Boolean);
            // changed(그날 실제로 가격이 두 갈래였던 보스)일 때만 phase를 저장 — 평소엔 의미 없는 값이라 저장 안 함
            return {name:`${bname} (${diff})`,crystal:Math.floor(crystal/p),party:p,members,...(changed?{phase}:{})};
          }).filter(Boolean):[];
      const sv=((+incSol1||0)+(+incSol2||0)+(+incSol3||0))*SOL_PRICE;
      const dv = DROP_ITEMS.reduce((s,d)=>s+(+dropQty[d.key]||0)*d.price, 0);

      // 물욕템 처리
      let dropMeso = 0;
      let dropList = [];
      if(incType==="drop") {
        const validItems = dropItems.filter(it=>it.name.trim());
        dropList = validItems.map(it=>({
          name:it.name.trim(),
          price: it.instant ? calcNetPrice(+it.price||0, +it.qty||1, mktFee)+MKT_DEPOSIT : +it.price||0,
          qty:+it.qty||1,
          fee:it.instant?mktFee:0,
          instant:!!it.instant
        }));
        // 즉시 반영 체크된 아이템만 수익 합산 (실수령액 기준)
        dropMeso = dropList.filter(it=>it.instant).reduce((s,it)=>s+it.price, 0);
        // 보관함 등록 (아이템명 있는 것만)
        for(const it of dropList) {
          await addInventoryItem(user.uid, {
            name: it.name,
            estimatedPrice: it.price,
            instant: it.instant, // 즉시반영 여부 저장
            obtainDate: usedDate,
            nickname: selChar.nickname,
            sold: false,
          });
        }
      }

      const mv = incType==="drop" ? dropMeso : (+(+incMeso||0)+(+incEtc||0));

      // ── 에픽던전 처리 ──
      let epicIncome = 0;
      let epicRecord = {};
      if(incType==="epic") {
        const reward = calcEpicReward(epicDungeon, epicExpLv, epicSolLv);
        epicIncome = reward?.mesoIncome || 0;
        epicRecord = {
          epicDungeon, epicExpLv, epicSolLv,
          epicCoin: reward?.coin||0,
          epicSol: reward?.sol||0,
          epicDarkGun: reward?.darkGun||0,
          epicSolFragment: reward?.solFragment||0,
          epicTotalGun: reward?.totalGunEquiv||0,
        };
        // 메포 지출 별도 기록
        if(epicMp && +epicMp > 0) {
          await saveExpense(user.uid, {
            date: usedDate, type: "mp",
            meso: 0, cash: 0, mile: 0,
            mp: +epicMp,
            note: `에픽던전 메포: ${EPIC_DUNGEONS.find(d=>d.key===epicDungeon)?.name||epicDungeon}`,
          });
        }
      }

      // 기록 단위 요약: 보스마다 phase가 다를 수 있으니 "혼합"까지 표현 (조회 화면 배지용)
      const phasesUsed = [...new Set(checkedBossList.map(b=>b.phase).filter(Boolean))];
      const summaryPhase = phasesUsed.length===0 ? null : phasesUsed.length===1 ? phasesUsed[0] : "mixed";

      await saveIncome(user.uid,{
        date:usedDate,job:selChar.job,nickname:selChar.nickname,world:selChar.world,
        type:incType,sol0:+incSol0||0,sol1:+incSol1||0,sol2:+incSol2||0,
        sol3:+incSol3||0,sol4:+incSol4||0,
        meso:incType==="drop"?dropMeso:(+incMeso||0),
        core:+incCore||0,
        etc:incType==="drop"?0:(+incEtc||0),
        mile:+incMile||0,mins:+incMins||0,note:incNote,
        dropQty: Object.fromEntries(DROP_ITEMS.map(d=>[d.key,+dropQty[d.key]||0])),
        dropIncome: dv,
        dropList: incType==="drop"?dropList:[],
        bossIncome,bossList:checkedBossList,totalSol:sv,
        pricePhase: incType==="boss" ? summaryPhase : null,
        ...epicRecord,
        totalIncome:sv+dv+mv+bossIncome+epicIncome,
      });
      // 드롭 아이템 인벤토리 자동 추가
      if(bossDropList.length>0 && selChar) {
        await Promise.all(bossDropList.map(drop=>
          addInventoryItem(user.uid, {
            name: drop.itemName,
            nickname: selChar.nickname,
            world: selChar.world||"",
            fromBoss: drop.bossKey,
            ringLevel: drop.ringLevel||null,
            ringName: drop.ringName||null,
            price: "",
            note: "",
          })
        ));
        setBossDropList([]);
      }
      [setIncSol0,setIncSol1,setIncSol2,setIncSol3,setIncSol4,
       setIncMeso,setIncCore,setIncEtc,setIncMile,setIncMins,setIncNote].forEach(fn=>fn(""));
      setCheckedBosses({}); setBossParty({}); setBossPartyMembers({}); setBossDiffs({});
      setDropQty(Object.fromEntries(DROP_ITEMS.map(d=>[d.key,""])));
      setDropItems([{name:"",price:"",qty:1,instant:false}]);
      setEpicExpLv(0); setEpicSolLv(0); setEpicMp("");
      setMidnightAlert(null);
      showToast("저장 완료!", "✅");
    } catch(e) { alert("오류: "+e.message); }
    setSaving(false);
  };

  // ── 프리셋 위젯: 체크된 보스 한번에 일괄 저장 ──
  const [presetWidgetDate, setPresetWidgetDate] = useState(today());
  const handleSavePresetWidget = async () => {
    if(!user) { showToast("로그인 후 이용할 수 있습니다","🔐"); return; }
    const widgetChar = favorites.find(f=>f.nickname===presetWidgetCharNick) || (selChar?.nickname===presetWidgetCharNick?selChar:null);
    if(!widgetChar) { showToast("캐릭터를 선택해주세요","⚠️"); return; }
    const slot = presets?.[presetWidgetSlot];
    if(!slot || !slot.bosses?.length) { showToast("선택된 프리셋에 보스가 없어요","⚠️"); return; }

    const usedDate = presetWidgetDate || today();

    // 위젯에서 체크 해제된 항목 제외
    const activeBosses = slot.bosses
      .map((b, idx) => ({ b, idx }))
      .filter(({idx}) => presetWidgetChecks[idx] !== false);
    if(activeBosses.length === 0) { showToast("체크된 보스가 없어요","⚠️"); return; }

    // 기존 당일 보스 기록 있는지 체크
    const dup = records.find(r=>r.kind==="income"&&r.type==="boss"&&r.nickname===widgetChar.nickname&&r.date===usedDate);
    if(dup) {
      alert("이미 해당 날짜에 보스 수익을 기록하셨습니다.\n수정 페이지로 이동합니다.");
      openDetail(dup, true); return;
    }

    setSaving(true);
    try {
      let bossIncome = 0;
      const checkedBossList = activeBosses.map(({b, idx})=>{
        const bkey = `preset_${idx}`;
        const { crystal, changed, phase } = resolveBossPrice(usedDate, b.sec, b.name, b.diff, presetWidgetPricePhaseMap, bkey);
        if(crystal==null) return null;
        const p = b.partySize || 1;
        const members = (b.partyMembers||[]).filter(Boolean);
        const itemCrystal = Math.floor(crystal/p);
        bossIncome += itemCrystal;
        return { name:`${b.name} (${b.diff})`, crystal:itemCrystal, party:p, members, ...(changed?{phase}:{}) };
      }).filter(Boolean);

      if(checkedBossList.length === 0) { showToast("가격 정보를 찾을 수 없어요","⚠️"); setSaving(false); return; }

      const phasesUsed = [...new Set(checkedBossList.map(b=>b.phase).filter(Boolean))];
      const summaryPhase = phasesUsed.length===0 ? null : phasesUsed.length===1 ? phasesUsed[0] : "mixed";

      await saveIncome(user.uid,{
        date:usedDate, job:widgetChar.job, nickname:widgetChar.nickname, world:widgetChar.world,
        type:"boss", sol0:0,sol1:0,sol2:0,sol3:0,sol4:0,
        meso:0, core:0, etc:0, mile:0, mins:0, note:`⭐ 프리셋: ${slot.name||presetWidgetSlot}`,
        dropList:[],
        bossIncome, bossList:checkedBossList, totalSol:0, totalIncome:bossIncome,
        pricePhase: summaryPhase,
      });
      setPresetWidgetChecks({});
      showToast("프리셋 저장 완료!", "✅");
    } catch(e) { alert("오류: "+e.message); }
    setSaving(false);
  };

  // ── 프리셋 설정 모달: 현재 슬롯 저장 ──
  const handleSavePresetSlot = async () => {
    if(!user || !presetWidgetCharNick) { showToast("캐릭터를 먼저 선택해주세요","⚠️"); return; }
    const bosses = bossStateToPreset(pmCheckedBosses, pmBossParty, pmBossPartyMembers);
    const next = {
      ...(presets || emptyPresets()),
      [presetEditSlot]: { name: presetNameInput, bosses },
    };
    try {
      await savePresets(user.uid, presetWidgetCharNick, next);
      showToast("프리셋 저장 완료!", "✅");
    } catch(e) { alert("오류: "+e.message); }
  };

  // 프리셋 설정 모달 슬롯 전환 시 편집 state 동기화
  const loadPresetSlotToEditor = (slotKey) => {
    setPresetEditSlot(slotKey);
    const slot = presets?.[slotKey] || emptyPreset_local();
    setPresetNameInput(slot.name || "");
    const { checkedBosses:cb, bossParty:bp, bossPartyMembers:bpm, bossDiffs:bd } = presetToBossState(slot.bosses||[]);
    setPmCheckedBosses(cb); setPmBossParty(bp); setPmBossPartyMembers(bpm); setPmBossDiffs(bd);
  };
  function emptyPreset_local(){ return { name:"", bosses:[] }; }

  // 수익탭 ⭐버튼: 프리셋 내용을 현재 보스 체크 state에 덮어쓰기
  const applyPresetToIncomeBoss = (slotKey) => {
    const slot = presets?.[slotKey];
    if(!slot || !slot.bosses?.length) { showToast("저장된 프리셋이 없어요","⚠️"); return; }
    const { checkedBosses:cb, bossParty:bp, bossPartyMembers:bpm, bossDiffs:bd } = presetToBossState(slot.bosses);

    // BossSection과 동일한 기간 로직으로 이미 잡은 보스 판별
    const bossRecsNow = (records||[]).filter(r=>
      r.kind==="income" && r.type==="boss" && r.nickname===(selChar?.nickname||"")
    );
    const base    = incDate || today();
    const _wStart = (()=>{ const d=new Date(base+"T00:00:00"); const day=d.getDay(); const diff=day>=4?day-4:day+3; d.setDate(d.getDate()-diff); return localDateStr(d); })();
    const _wEnd   = (()=>{ const d=new Date(_wStart+"T00:00:00"); d.setDate(d.getDate()+6); return localDateStr(d); })();
    const _mStart = base.slice(0,7)+"-01";
    const _mEnd   = (()=>{ const [y,m]=base.slice(0,7).split("-").map(Number); return `${base.slice(0,7)}-${String(new Date(y,m,0).getDate()).padStart(2,"0")}`; })();
    const isAlreadyRecorded = (sec, bossName, diff) => {
      return bossRecsNow.some(r => {
        if(sec==="weekly"  && (r.date<_wStart||r.date>_wEnd))  return false;
        if(sec==="monthly" && (r.date<_mStart||r.date>_mEnd))  return false;
        if(sec==="daily"   && r.date!==base)                    return false;
        return r.bossList?.some(b=>b.name===`${bossName} (${diff})`);
      });
    };

    // 이미 잡은 보스는 체크에서 제외
    const filtered = Object.fromEntries(
      Object.entries(cb).filter(([bkey]) => {
        const parts = bkey.split("_");
        const sec  = parts[0];
        const diff = parts[parts.length-1];
        const name = parts.slice(1,-1).join("_");
        return !isAlreadyRecorded(sec, name, diff);
      })
    );

    setCheckedBosses(prev=>({...prev,...filtered}));
    setBossParty(prev=>({...prev,...Object.fromEntries(Object.entries(bp).filter(([k])=>filtered[k]))}));
    setBossPartyMembers(prev=>({...prev,...Object.fromEntries(Object.entries(bpm).filter(([k])=>filtered[k]))}));
    setBossDiffs(prev=>({...prev,...bd}));
    setIncType("boss");
    const skipped = Object.keys(cb).length - Object.keys(filtered).length;
    showToast(`${slot.name||slotKey} 적용됨${skipped>0?` (${skipped}개 이미 기록됨 제외)`:""}`, "⭐");
  };

  const handleSaveExpense = async () => {
    if(!expDate) return alert("날짜를 선택해주세요");
    if(expDate !== today()) { setMidnightAlert({ kind:"expense" }); return; }
    await _doSaveExpense(expDate);
  };

  const _doSaveExpense = async (dateToUse) => {
    const usedDate = dateToUse || expDate;
    setSaving(true);
    try {
      if (expType === "symbol") {
        // 심볼업: 입력된 지역만 각각 별도 record 저장
        const areas = expSymbolForce === "arcane" ? ARCANE_AREAS : expSymbolForce === "grand" ? GRAND_AUTHENTIC_AREAS : AUTHENTIC_AREAS;
        const maxLv = expSymbolForce === "arcane" ? 20 : 11;
        const entries = areas.map(area => {
          const { cur, tgt } = symLevels[area] || {};
          const c = parseInt(cur)||0, t = parseInt(tgt)||0;
          if (!c || !t || c < 1 || t > maxLv || c >= t) return null;
          const { meso } = calcSymbolCost(expSymbolForce, c, t, area, usedDate);
          return { area, cur:c, tgt:t, meso };
        }).filter(Boolean);
        if (entries.length === 0) { setSaving(false); return alert("입력된 심볼이 없어요. 현재/목표 레벨을 입력해주세요."); }
        for (const e of entries) {
          await saveExpense(user.uid, {
            date:usedDate, job:selChar?.job||"", nickname:selChar?.nickname||"",
            type:"symbol", symbolForce:expSymbolForce, symbolArea:e.area,
            symbolCur:e.cur, symbolTgt:e.tgt,
            meso:e.meso, cash:0, mile:0, note:expNote,
          });
        }
        setSymLevels(initSymLevels(expSymbolForce));
      } else {
        await saveExpense(user.uid,{
          date:usedDate, job:selChar?.job||"", nickname:selChar?.nickname||"",
          type:expType, meso:+expMeso||0, cash:+expCash||0, mile:+expMile||0, note:expNote,
        });
        [setExpMeso,setExpCash,setExpMile].forEach(fn=>fn(""));
      }
      setExpNote(""); setMidnightAlert(null); alert("저장 완료!");
    } catch(e) { alert("오류: "+e.message); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if(!window.confirm("삭제할까요?")) return;
    await deleteRecord(user.uid,id); setDetailRec(null);
  };

  // ── 기프티콘 후원 전송 ──
  const handleGiftSubmit = async () => {
    if(!user) { showToast("로그인 후 이용해주세요","🔐"); return; }
    if(!giftFile) { showToast("기프티콘 사진을 첨부해주세요","⚠️"); return; }
    setGiftSending(true);
    try {
      const { getStorage, ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
      const storage = getStorage();
      const storageRef = ref(storage, `gifts/${user.uid}_${Date.now()}_${giftFile.name}`);
      await uploadBytes(storageRef, giftFile);
      const fileUrl = await getDownloadURL(storageRef);
      const { getFirestore, collection, addDoc, serverTimestamp } = await import("firebase/firestore");
      const db = getFirestore();
      await addDoc(collection(db,"gifts"),{
        uid: user.uid,
        email: user.email||"",
        note: giftNote,
        fileUrl,
        createdAt: serverTimestamp(),
      });
      setGiftNote(""); setGiftFile(null);
      setShowDonation(false);
      showToast("후원 감사합니다! 💛","☕");
    } catch(e) { showToast("오류가 발생했어요: "+e.message,"❌"); }
    setGiftSending(false);
  };

  const handleDeleteFromList = async (e, id) => {
    e.stopPropagation();
    if(!window.confirm("삭제할까요?")) return;
    await deleteRecord(user.uid,id);
  };

  // ── 캐릭터 이동 핸들러 ──
  const handleCharMove = async () => {
    if(!detailRec||!charMoveTarget) return;
    const targetFav = favorites.find(f=>f.nickname===charMoveTarget);
    if(!targetFav) { showToast("즐겨찾기에 없는 캐릭터예요","⚠️"); return; }
    await updateRecord(user.uid, detailRec.id, {
      nickname: targetFav.nickname,
      job: targetFav.job||detailRec.job||"",
      world: targetFav.world||detailRec.world||"",
    });
    showToast(`${targetFav.nickname}으로 이동 완료!`, "✅");
    setCharMoveMode(false);
    setCharMoveTarget("");
    setDetailRec(null);
  };

  const openDetail = (r, startEdit=false) => {
    setDetailRec(r); setEditMode(startEdit);
    setEditDate(r.date||""); setEditType(r.type||"hunt");
    setEditMins(String(r.mins||"")); setEditSol0(String(r.sol0||""));
    setEditSol1(String(r.sol1||"")); setEditSol2(String(r.sol2||""));
    setEditSol3(String(r.sol3||"")); setEditSol4(String(r.sol4||""));
    setEditMeso(String(r.meso||"")); setEditCore(String(r.core||""));
    setEditEtc(String(r.etc||"")); setEditMile(String(r.mile||""));
    setEditNote(r.note||"");
    setEditExpMeso(String(r.meso||"")); setEditExpCash(String(r.cash||""));
    setEditExpMile(String(r.mile||"")); setEditExpType(r.type||"cube");
    setEditSymbolForce(r.symbolForce||""); setEditSymbolArea(r.symbolArea||"");
    setEditSymCalcOpen(false);
    setEditSymCurLv(r.symbolCur?String(r.symbolCur):"");
    setEditSymTgtLv(r.symbolTgt?String(r.symbolTgt):"");
    if (r.type==="boss"&&r.bossList?.length>0) {
      // 저장 당시(기록 날짜) 기준으로 매칭해야 보스명/난이도가 정확히 복원됨.
      // phase가 항목별(b.phase)로 없는 구버전 기록은 레코드 단위 r.pricePhase를 대신 사용.
      const legacyPhase = (r.pricePhase && r.pricePhase!=="mixed") ? r.pricePhase : "after";
      const afterGroups  = getBossData(r.date, "after");
      const beforeGroups = getBossData(r.date, "before");
      const rc={}; const rp={}; const rd={}; const rm={}; const rph={};
      r.bossList.forEach(b=>{
        const match = b.name.match(/^(.+) \((.+)\)$/);
        if(!match) return;
        const [,bname,diff]=match;
        Object.keys(afterGroups).forEach(sec=>{
          const grp = afterGroups[sec].find(g=>g.name===bname) || beforeGroups[sec]?.find(g=>g.name===bname);
          if(grp&&grp.diffs.find(d=>d.diff===diff)) {
            const key=`${sec}_${bname}_${diff}`;
            rc[key]=true; rp[key]=b.party||1;
            rd[`${sec}_${bname}`]=diff;
            rph[key]=b.phase || legacyPhase;
            // 저장된 파티원 이름 불러오기
            if(Array.isArray(b.members)&&b.members.length>0) rm[key]=b.members;
          }
        });
      });
      setEditCheckedBosses(rc); setEditBossParty(rp); setEditBossDiffs(rd); setEditBossPartyMembers(rm); setEditBossPricePhaseMap(rph);
    } else { setEditCheckedBosses({}); setEditBossParty({}); setEditBossDiffs({}); setEditBossPartyMembers({}); setEditBossPricePhaseMap({}); }
  };

  const handleUpdateRecord = async () => {
    if(!detailRec) return;
    setSaving(true);
    try {
      if(detailRec.kind==="income") {
        const sv=((+editSol1||0)+(+editSol2||0)+(+editSol3||0))*SOL_PRICE;
        const mv=(+editMeso||0)+(+editEtc||0);
        let newBossIncome=0; let newBossList=[];
        if(editType==="boss") {
          // 수정 저장 시에도 "오늘"이 아니라 editDate + 보스별 선택된 패치 전/후 가격을 써야 함
          Object.entries(editCheckedBosses).filter(([,v])=>v).forEach(([k])=>{
            const parts=k.split("_");
            if(parts.length<3) return; // 구버전 key 방어
            const sec=parts[0]; const diff=parts[parts.length-1]; const bname=parts.slice(1,-1).join("_");
            const { crystal, changed, phase } = resolveBossPrice(editDate, sec, bname, diff, editBossPricePhaseMap, k);
            if(crystal==null) return;
            const party=editBossParty[k]||1;
            const members=(editBossPartyMembers[k]||[]).filter(Boolean);
            const itemCrystal=Math.floor(crystal/party);
            newBossIncome+=itemCrystal;
            newBossList.push({name:`${bname} (${diff})`,crystal:itemCrystal,party,members,...(changed?{phase}:{})});
          });
        }
        const editPhasesUsed = [...new Set(newBossList.map(b=>b.phase).filter(Boolean))];
        const editSummaryPhase = editPhasesUsed.length===0 ? null : editPhasesUsed.length===1 ? editPhasesUsed[0] : "mixed";
        await updateRecord(user.uid,detailRec.id,{
          date:editDate,type:editType,sol0:+editSol0||0,sol1:+editSol1||0,sol2:+editSol2||0,
          sol3:+editSol3||0,sol4:+editSol4||0,meso:+editMeso||0,core:+editCore||0,
          etc:+editEtc||0,mile:+editMile||0,mins:+editMins||0,note:editNote,
          totalSol:sv,bossIncome:newBossIncome,bossList:newBossList,totalIncome:sv+mv+newBossIncome,
          pricePhase: editType==="boss" ? editSummaryPhase : null,
        });
      } else {
        await updateRecord(user.uid,detailRec.id,{
          date:editDate,type:editExpType,meso:+editExpMeso||0,cash:+editExpCash||0,mile:+editExpMile||0,note:editNote,
          symbolForce:editExpType==="symbol"?editSymbolForce:"",symbolArea:editExpType==="symbol"?editSymbolArea:"",
          symbolCur:editExpType==="symbol"?(parseInt(editSymCurLv)||0):0,
          symbolTgt:editExpType==="symbol"?(parseInt(editSymTgtLv)||0):0,
        });
      }
      alert("수정 완료!"); setDetailRec(null); setEditMode(false);
    } catch(e) { alert("오류: "+e.message); }
    setSaving(false);
  };

  const handleSaveNotice = async () => {
    if(!nTitle.trim()) return alert("제목을 입력해주세요");
    if(!nContent.trim()) return alert("내용을 입력해주세요");
    try {
      let thumbUrl = nThumb;
      if(nThumbFile) {
        const { getStorage, ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
        const storage = getStorage();
        const sRef = ref(storage, `notice-images/${Date.now()}_${nThumbFile.name}`);
        await uploadBytes(sRef, nThumbFile);
        thumbUrl = await getDownloadURL(sRef);
      }
      const payload = {
        title: nTitle.trim(), content: nContent.trim(), pinned: nPinned,
        category: nCategory,
        summary: nSummary.trim() || nContent.trim().slice(0,80)+"...",
        thumbnail: thumbUrl||"",
      };
      if(editNoticeId) { await updateNotice(editNoticeId, payload.title, payload.content, payload.pinned, payload); alert("수정 완료!"); }
      else { await addNotice(user.uid, payload.title, payload.content, payload.pinned, payload); alert("등록 완료!"); }
      setNoticeForm(false); setEditNoticeId(null);
      setNTitle(""); setNContent(""); setNPinned(false);
      setNCategory("patch"); setNSummary(""); setNThumb(""); setNThumbFile(null);
    } catch(e) { alert("오류: "+e.message); }
  };
  const handleDeleteNotice = async (id) => {
    if(!window.confirm("삭제할까요?")) return;
    await deleteNotice(id); setOpenNoticeId(null);
  };
  const openNoticeEdit = (n) => {
    setEditNoticeId(n.id); setNTitle(n.title); setNContent(n.content); setNPinned(n.pinned||false);
    setNCategory(n.category||"notice"); setNSummary(n.summary||""); setNThumb(n.thumbnail||""); setNThumbFile(null);
    setShowNotices(false); setShowNoticeManager(false); setNoticeForm(true); setOpenNoticeId(null);
  };

  // 일괄 카테고리 변경
  const handleBulkCategory = async () => {
    const ids = Object.keys(noticeChecked).filter(id=>noticeChecked[id]);
    if(!ids.length) return alert("변경할 공지를 선택해주세요");
    if(!window.confirm(`선택한 ${ids.length}개를 "${NOTICE_CATEGORIES.find(c=>c.value===bulkCategory)?.label}"로 변경할까요?`)) return;
    try {
      const { getFirestore, doc, updateDoc } = await import("firebase/firestore");
      const db = getFirestore();
      await Promise.all(ids.map(id => updateDoc(doc(db,"notices",id), { category: bulkCategory })));
      setNoticeChecked({});
      showToast(`${ids.length}개 카테고리 변경 완료!`, "✅");
    } catch(e) { alert("오류: "+e.message); }
  };

  if (loading) return <div style={{display:"flex",justifyContent:"center",alignItems:"center",minHeight:"100vh",background:"#F5F5F5"}}><p style={{color:"#534AB7",fontSize:16}}>🍁 로딩 중...</p></div>;

  // ── 점검 모드 (관리자는 통과, 일반 유저는 점검 화면) ──
  const IS_MAINTENANCE = false; // 점검 종료 시 false로 변경
  if (IS_MAINTENANCE && user?.uid !== ADMIN_UID) {
    return (
      <div style={{minHeight:"100vh",background:"#0D0D1A",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"24px",fontFamily:"Arial,sans-serif"}}>
        <div style={{fontSize:48,marginBottom:16}}>🍁</div>
        <div style={{fontSize:22,fontWeight:700,color:"#fff",marginBottom:8}}>메이플 파밍 트래커</div>
        <div style={{fontSize:14,color:"#534AB7",fontWeight:600,marginBottom:32}}>서버 점검 중</div>
        <div style={{background:"#1A1A2E",borderRadius:16,border:"0.5px solid #534AB733",padding:"28px 32px",maxWidth:420,width:"100%",textAlign:"center"}}>
          <div style={{fontSize:36,marginBottom:12}}>🔧</div>
          <div style={{fontSize:16,fontWeight:600,color:"#fff",marginBottom:12}}>서비스 점검 안내</div>
          <div style={{fontSize:13,color:"#aaa",lineHeight:1.9,marginBottom:20}}>
            메이플스토리 정기 패치에 따른<br/>
            보스 결정석 가격 업데이트 작업 중입니다.<br/><br/>
            <span style={{color:"#534AB7",fontWeight:600}}>2026년 6월 18일 (목)</span><br/>
            오전 3:00 ~ 오후 3:00 (예정)<br/><br/>
            점검 완료 후 새 가격이 자동 반영됩니다.
          </div>
          <div style={{background:"#534AB722",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#9990FF"}}>
            기존에 기록된 데이터는 안전하게 보관됩니다 🔒
          </div>
        </div>
        <div style={{marginTop:24,fontSize:11,color:"#444"}}>© 2026 Mapel Farming Tracker. All rights reserved.</div>
      </div>
    );
  }

  // ── 로그아웃 상태에서도 앱 진입 허용 (저장 시에만 로그인 요구) ──

  const TABS=[["home","🏠","홈"],["income","💰","수익"],["expense","💸","지출"],["exchange","💱","환전"],["market","🏪","경매장"],["log","📋","조회/수정"],["stats","📊","통계"],["settings","⚙️","설정"]];
  const MAIN_STATS=["STR","DEX","INT","LUK","최대 HP","최대 MP","공격력","마력","보스 몬스터 공격 시 데미지","몬스터 방어율 무시","크리티컬 데미지","최종 데미지","스탠스","방어력","전투력"];

  const weekStart   = thisWeekStart();
  const todayStr    = today();
  const weekIncRecs = incRecs.filter(r=>r.date>=weekStart);
  const weekExpRecs = expRecs.filter(r=>r.date>=weekStart);
  const dayIncRecs  = incRecs.filter(r=>r.date===todayStr);
  const dayExpRecs  = expRecs.filter(r=>r.date===todayStr);
  const weekInc  = weekIncRecs.reduce((s,r)=>s+calcIncome(r,favorites),0);
  const weekExp  = weekExpRecs.reduce((s,r)=>s+(r.meso||0),0);
  const dayInc   = dayIncRecs.reduce((s,r)=>s+calcIncome(r,favorites),0);
  const dayExp   = dayExpRecs.reduce((s,r)=>s+(r.meso||0),0);

  const fmtNet = (v) => {
    if (v === 0) return "0메소";
    return (v > 0 ? "+" : "") + fmt(v);
  };

  const SumCards=(
    <div style={{padding:isMobile?"8px 12px 0":"8px 0 0"}}>
      {/* 전광판 — 주간 한줄 / 일간 한줄 */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:6}}>
        {[["주간 수익",weekInc,"#3B6D11"],["주간 지출",weekExp,"#A32D2D"],["주간 순손익",weekInc-weekExp,weekInc-weekExp>0?"#3B6D11":weekInc-weekExp<0?"#A32D2D":T.sub]].map(([l,v,c])=>(
          <div key={l} style={{background:T.card,borderRadius:10,border:`0.5px solid ${T.border}`,padding:"10px 12px"}}>
            <div style={{fontSize:10,color:T.sub,marginBottom:4}}>{l}</div>
            <div style={{fontSize:isMobile?13:15,fontWeight:500,color:c}}>{l==="주간 순손익"?fmtNet(v):fmt(v)}</div>
          </div>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
        {[["일간 수익",dayInc,"#3B6D11"],["일간 지출",dayExp,"#A32D2D"],["일간 순손익",dayInc-dayExp,dayInc-dayExp>0?"#3B6D11":dayInc-dayExp<0?"#A32D2D":T.sub]].map(([l,v,c])=>(
          <div key={l} style={{background:T.card,borderRadius:10,border:`0.5px solid ${T.border}`,padding:"10px 12px"}}>
            <div style={{fontSize:10,color:T.sub,marginBottom:4}}>{l}</div>
            <div style={{fontSize:isMobile?13:15,fontWeight:500,color:c}}>{l==="일간 순손익"?fmtNet(v):fmt(v)}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const SelBar=selChar?(
    <SelBarComp T={T} selChar={selChar} favorites={favorites} setSelChar={setSelChar} handleLoadInfo={handleLoadInfo}/>
  ):(
    <SelBarComp T={T} selChar={null} favorites={favorites} setSelChar={setSelChar} handleLoadInfo={handleLoadInfo}/>
  );

  // ── ⭐ 프리셋 위젯 (PC: 우측 칼럼 / 모바일: 홈 탭 상단) ──
  const PresetWidget = (
    <div style={{background:T.card,borderRadius:12,border:`1px solid #E4A80050`,padding:16,marginBottom:10}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,gap:8}}>
        <div style={{fontSize:13,fontWeight:600,color:"#E4A800",flexShrink:0}}>⭐ 프리셋</div>
        {favorites.length>0?(
          <select value={presetWidgetCharNick} onChange={e=>setPresetWidgetCharNick(e.target.value)}
            style={{flex:1,minWidth:0,background:T.bg,border:`0.5px solid ${T.border}`,borderRadius:6,
              padding:"4px 8px",fontSize:12,color:T.text,cursor:"pointer"}}>
            <option value="">캐릭터 선택</option>
            {favorites.map(f=><option key={f.nickname} value={f.nickname}>{f.nickname}</option>)}
          </select>
        ):(
          <span style={{fontSize:11,color:T.sub}}>즐겨찾기 캐릭터가 없어요</span>
        )}
        <button onClick={()=>{loadPresetSlotToEditor(presetWidgetSlot);setPresetModalOpen(true);}}
          disabled={!presetWidgetCharNick}
          style={{background:"transparent",border:`0.5px solid ${T.border}`,borderRadius:6,padding:"4px 8px",fontSize:11,
            color:presetWidgetCharNick?T.sub:T.border,cursor:presetWidgetCharNick?"pointer":"default",flexShrink:0}}>
          ⚙️ 설정
        </button>
      </div>
      {!presetWidgetCharNick ? (
        <div style={{fontSize:12,color:T.sub,textAlign:"center",padding:"24px 0"}}>
          {favorites.length>0 ? "캐릭터를 선택해주세요" : "홈에서 캐릭터를 즐겨찾기에 추가해주세요"}
        </div>
      ) : (
      <>
      <div style={{display:"flex",gap:6,marginBottom:10}}>
        {["preset1","preset2","preset3"].map((slotKey,i)=>{
          const slot = presets?.[slotKey];
          const hasData = slot?.bosses?.length>0;
          return (
            <button key={slotKey} onClick={()=>{setPresetWidgetSlot(slotKey);setPresetWidgetChecks({});}}
              style={{flex:1,padding:"7px 4px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",
                border:`1.5px solid ${presetWidgetSlot===slotKey?"#E4A800":T.border}`,
                background:presetWidgetSlot===slotKey?"#E4A800":"transparent",
                color:presetWidgetSlot===slotKey?"#fff":hasData?T.text:T.sub,
                whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
              {slot?.name || `프리셋 ${i+1}`}
            </button>
          );
        })}
      </div>
      {(() => {
        const slot = presets?.[presetWidgetSlot];
        if(!slot?.bosses?.length) return (
          <div style={{fontSize:12,color:T.sub,textAlign:"center",padding:"16px 0"}}>
            저장된 보스가 없어요. ⚙️ 설정에서 추가해보세요.
          </div>
        );
        const pwDate = presetWidgetDate||today();
        const pwPatchDay = isPatchTransitionDate(pwDate);
        let totalCrystal = 0;
        return (
          <>
            {pwPatchDay&&(
              <div style={{background:"#FFF4E0",border:"2px solid #E4A800",borderRadius:10,padding:"10px 12px",marginBottom:10}}>
                <div style={{fontSize:13,color:"#854F0B",fontWeight:800,lineHeight:1.4}}>
                  🚨 {pwDate.slice(5).replace("-","/")}은 결정석 가격이 바뀌는 날! 가격이 두 개로 뜨는 보스는 직접 골라주세요.
                </div>
              </div>
            )}
            <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:10}}>
              {slot.bosses.map((b,idx)=>{
                const checked = presetWidgetChecks[idx] !== false;
                const bkey = `preset_${idx}`;
                const { crystal:rawCrystal, changed, phase } = resolveBossPrice(pwDate, b.sec, b.name, b.diff, presetWidgetPricePhaseMap, bkey);
                const crystal = rawCrystal!=null ? Math.floor(rawCrystal/(b.partySize||1)) : 0;
                if(checked) totalCrystal += crystal;
                return (
                  <div key={idx} style={{borderRadius:8,border:`0.5px solid ${checked?"#E4A80050":T.border}`,
                    background:checked?(dark?"#2a2410":"#FFF8E1"):T.bg,overflow:"hidden"}}>
                    <label onClick={()=>setPresetWidgetChecks(p=>({...p,[idx]:!checked}))}
                      style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",cursor:"pointer"}}>
                      <span style={{fontSize:14}}>{checked?"☑":"☐"}</span>
                      <span style={{fontSize:12,fontWeight:500,color:T.text,flex:1}}>
                        {b.name} <span style={{color:"#E4A800"}}>{b.diff}</span>
                        {b.partySize>1&&<span style={{color:T.sub,fontSize:11}}> · {b.partySize}인</span>}
                      </span>
                      {!changed&&(
                        <span style={{fontSize:12,fontWeight:600,color:checked?"#3B6D11":T.sub}}>
                          {rawCrystal!=null?fmt(crystal):"가격없음"}
                        </span>
                      )}
                    </label>
                    {changed&&(
                      <div style={{padding:"0 10px 8px 10px",display:"flex",gap:6}} onClick={e=>e.stopPropagation()}>
                        {[["before","🌙 점검 전"],["after","🔧 점검 후"]].map(([val,label])=>{
                          const sel = phase===val;
                          return (
                            <button key={val} onClick={()=>setPresetWidgetPricePhaseMap(p=>({...p,[bkey]:val}))}
                              style={{flex:1,padding:"6px 4px",borderRadius:8,cursor:"pointer",textAlign:"center",
                                border:`2px solid ${sel?"#E4A800":T.border}`,
                                background:sel?"#E4A800":T.card,
                                color:sel?"#fff":T.text}}>
                              <div style={{fontSize:11,fontWeight:700}}>{sel?"✅ ":""}{label}</div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
              <input type="date" value={presetWidgetDate} onChange={e=>setPresetWidgetDate(e.target.value)}
                style={{...IS,flex:1,fontSize:12,padding:"6px 8px"}}/>
              <span style={{fontSize:13,fontWeight:700,color:"#534AB7",whiteSpace:"nowrap"}}>{fmt(totalCrystal)}</span>
            </div>
            <button onClick={handleSavePresetWidget} disabled={saving}
              style={{width:"100%",background:"#E4A800",color:"#fff",border:"none",borderRadius:8,
                padding:10,fontSize:13,fontWeight:600,cursor:"pointer"}}>
              {saving?"저장 중...":"✅ 선택한 보스 한번에 저장"}
            </button>
          </>
        );
      })()}
      </>
      )}
    </div>
  );

  const HomeContent=(
    <div>
      {/* 모바일에서만 홈 상단에 프리셋 위젯 표시 (PC는 우측 칼럼에 별도 배치) */}
      {isMobile && PresetWidget}
      {/* 대시보드 */}
      <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16,marginBottom:10}}>
        {/* 헤더: 타이틀 + 캐릭터 드롭다운 */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div style={{fontSize:13,fontWeight:500,color:"#534AB7"}}>📊 대시보드</div>
          {/* 캐릭터 셀렉터 드롭다운 */}
          <div style={{position:"relative"}}>
            <select
              value={dashChar}
              onChange={e=>setDashChar(e.target.value)}
              style={{
                padding:"5px 28px 5px 10px",
                borderRadius:8,
                border:`0.5px solid ${T.border}`,
                background:T.inp,
                color:dashChar==="all"?T.sub:"#534AB7",
                fontSize:12,
                cursor:"pointer",
                fontWeight:dashChar==="all"?400:500,
                appearance:"none",
                WebkitAppearance:"none",
                backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23888'/%3E%3C/svg%3E")`,
                backgroundRepeat:"no-repeat",
                backgroundPosition:"right 8px center",
                minWidth:120,
              }}
            >
              <option value="all">🌐 모두 보기</option>
              {favorites.map(f=>(
                <option key={f.nickname} value={f.nickname}>⭐ {f.nickname}</option>
              ))}
            </select>
          </div>
        </div>

        {/* 대시보드 수치 계산 (dashChar 기준 필터) */}
        {(()=>{
          const fRecs   = dashChar==="all" ? records : records.filter(r=>r.nickname===dashChar);
          const fInc    = fRecs.filter(r=>r.kind==="income");
          const fExp    = fRecs.filter(r=>r.kind==="expense");

          const wStart  = thisWeekStart();
          const mStart  = thisMonthStart();
          const tStr    = today();

          const dTotInc  = fInc.reduce((s,r)=>s+calcIncome(r,favorites),0);
          const dTotExp  = fExp.reduce((s,r)=>s+(r.meso||0),0);
          const dMonInc  = fInc.filter(r=>r.date>=mStart).reduce((s,r)=>s+calcIncome(r,favorites),0);
          const dMonExp  = fExp.filter(r=>r.date>=mStart).reduce((s,r)=>s+(r.meso||0),0);
          const dWkInc   = fInc.filter(r=>r.date>=wStart).reduce((s,r)=>s+calcIncome(r,favorites),0);
          const dWkExp   = fExp.filter(r=>r.date>=wStart).reduce((s,r)=>s+(r.meso||0),0);
          const dDayInc  = fInc.filter(r=>r.date===tStr).reduce((s,r)=>s+calcIncome(r,favorites),0);
          const dDayExp  = fExp.filter(r=>r.date===tStr).reduce((s,r)=>s+(r.meso||0),0);
          const dCash    = fExp.reduce((s,r)=>s+(r.cash||0),0);
          const dMile    = fInc.reduce((s,r)=>s+(r.mile||0),0)-fExp.reduce((s,r)=>s+(r.mile||0),0);
          const dCount   = fRecs.length;

          const rows=[
            ["누적", dTotInc, dTotExp],
            ["월간", dMonInc, dMonExp],
            ["주간", dWkInc,  dWkExp],
            ["일간", dDayInc, dDayExp],
          ];

          return (
            <>
              {rows.map(([label,inc,exp])=>(
                <div key={label} style={{display:"grid",gridTemplateColumns:"60px 1fr 1fr 1fr",gap:8,alignItems:"center",padding:"8px 0",borderBottom:`0.5px solid ${T.border}`}}>
                  <div style={{fontSize:12,fontWeight:500,color:T.sub}}>{label}</div>
                  <div>
                    <div style={{fontSize:10,color:T.sub,marginBottom:2}}>수익</div>
                    <div style={{fontSize:isMobile?12:13,fontWeight:500,color:"#3B6D11"}}>{fmt(inc)}</div>
                  </div>
                  <div>
                    <div style={{fontSize:10,color:T.sub,marginBottom:2}}>지출</div>
                    <div style={{fontSize:isMobile?12:13,fontWeight:500,color:"#A32D2D"}}>{fmt(exp)}</div>
                  </div>
                  <div>
                    <div style={{fontSize:10,color:T.sub,marginBottom:2}}>순손익</div>
                    <div style={{fontSize:isMobile?12:13,fontWeight:500,color:inc-exp>0?"#3B6D11":inc-exp<0?"#A32D2D":T.sub}}>{fmtNet(inc-exp)}</div>
                  </div>
                </div>
              ))}
              {/* 재화 행 */}
              <div style={{display:"grid",gridTemplateColumns:"60px 1fr 1fr 1fr",gap:8,alignItems:"center",padding:"8px 0",borderBottom:`0.5px solid ${T.border}`}}>
                <div style={{fontSize:12,fontWeight:500,color:T.sub}}>재화</div>
                <div>
                  <div style={{fontSize:10,color:T.sub,marginBottom:2}}>캐시 지출</div>
                  <div style={{fontSize:isMobile?12:13,fontWeight:500,color:"#A32D2D"}}>{dCash>0?"-":""}{dCash.toLocaleString()}원</div>
                </div>
                <div>
                  <div style={{fontSize:10,color:T.sub,marginBottom:2}}>마일리지</div>
                  <div style={{fontSize:isMobile?12:13,fontWeight:500,color:dMile>0?"#3B6D11":dMile<0?"#A32D2D":T.sub}}>{dMile>0?"+":""}{dMile.toLocaleString()}</div>
                </div>
                <div>
                  <div style={{fontSize:10,color:T.sub,marginBottom:2}}>총 기록</div>
                  <div style={{fontSize:isMobile?12:13,fontWeight:500,color:"#534AB7"}}>{dCount}건</div>
                </div>
              </div>
              {/* 보관함 현황 행 */}
              {(()=>{
                const myInv = dashChar==="all"
                  ? inventory.filter(it=>!it.sold)
                  : inventory.filter(it=>!it.sold&&it.nickname===dashChar);
                const invEst = myInv.reduce((s,it)=>s+(it.estimatedPrice||0),0);
                if(myInv.length===0) return null;
                return(
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 0",cursor:"pointer"}}
                    onClick={()=>setTab("market")}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:11,fontWeight:500,color:T.sub}}>📦 미판매 보관함</span>
                      <span style={{padding:"1px 7px",borderRadius:20,fontSize:10,background:"#534AB722",color:"#534AB7",fontWeight:600}}>{myInv.length}개</span>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:12,color:"#3B6D11",fontWeight:500}}>{invEst>0?`예상 ${fmt(invEst)}`:""}</span>
                      <span style={{fontSize:11,color:"#534AB7"}}>→</span>
                    </div>
                  </div>
                );
              })()}

              {/* 선택된 캐릭터 정보 표시 */}
              {dashChar!=="all"&&(()=>{
                const charInfo=favorites.find(f=>f.nickname===dashChar);
                if(!charInfo) return null;
                return(
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:T.charBg,borderRadius:8,marginTop:10,border:"0.5px solid #534AB744"}}>
                    {charInfo.image&&<CharAvatar src={charInfo.image} alt={charInfo.nickname} size={42}/>}
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:500,color:T.text}}>{charInfo.nickname}</div>
                      <div style={{fontSize:11,color:T.sub}}>{charInfo.job} · Lv.{charInfo.level} · {charInfo.world}</div>
                    </div>
                    <button onClick={()=>handleLoadInfo(charInfo.nickname)} style={{background:"transparent",border:"0.5px solid #534AB7",borderRadius:6,padding:"4px 10px",fontSize:11,color:"#534AB7",cursor:"pointer"}}>📊 INFO</button>
                    <button onClick={()=>selectChar(charInfo)} style={{background:"#534AB7",color:"#fff",border:"none",borderRadius:6,padding:"4px 10px",fontSize:11,cursor:"pointer"}}>수익입력 →</button>
                  </div>
                );
              })()}
            </>
          );
        })()}
      </div>

      {/* 즐겨찾기 */}
      <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16,marginBottom:10}}>
        <div style={{fontSize:13,fontWeight:500,color:"#534AB7",marginBottom:12}}>⭐ 즐겨찾기 캐릭터</div>
        {favorites.length===0?(
          <div style={{textAlign:"center",padding:"24px 0",color:T.sub}}><div style={{fontSize:32,marginBottom:8}}>🔍</div><div>아래에서 캐릭터를 검색해 추가해보세요</div></div>
        ):(
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(auto-fill,minmax(160px,1fr))",gap:10}}>
            {favorites.map(char=>(
              <div key={char.nickname} style={{background:T.charBg,borderRadius:12,border:"0.5px solid #534AB7",padding:14}}>
                <div onClick={()=>selectChar(char)} style={{cursor:"pointer"}}>
                  {char.image&&<CharAvatar src={char.image} alt={char.nickname} size={60}/>}
                  <div style={{fontSize:14,fontWeight:500,color:T.text,marginTop:6}}>{char.nickname}</div>
                  <div style={{fontSize:11,color:T.sub,marginTop:2}}>{char.job}</div>
                  <div style={{fontSize:11,color:T.sub}}>Lv.{char.level} · {char.world}</div>
                </div>
                <div style={{display:"flex",gap:4,marginTop:8}}>
                  <button onClick={()=>selectChar(char)} style={{flex:1,background:"#534AB7",color:"#fff",border:"none",borderRadius:6,padding:"4px 0",fontSize:10,cursor:"pointer"}}>수익입력 →</button>
                  <button onClick={()=>handleLoadInfo(char.nickname)} style={{background:"transparent",border:"0.5px solid #534AB7",borderRadius:6,padding:"4px 8px",fontSize:10,color:"#534AB7",cursor:"pointer"}}>INFO</button>
                  <button onClick={e=>{e.stopPropagation();removeFavorite(char.nickname);}} style={{background:"none",border:"none",cursor:"pointer",color:"#A32D2D",fontSize:12}}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16}}>
        <div style={{fontSize:13,fontWeight:500,color:"#534AB7",marginBottom:12}}>🔍 캐릭터 검색</div>
        <div style={{display:"flex",gap:8,marginBottom:12}}>
          <input type="text" value={searchNick} onChange={e=>setSearchNick(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleSearch()} placeholder="메이플 닉네임 입력" style={{...IS,flex:1}}/>
          <button onClick={handleSearch} style={{background:"#534AB7",color:"#fff",border:"none",borderRadius:8,padding:"0 16px",fontSize:13,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>{searchLoading?"조회 중...":"조회"}</button>
        </div>
        {searchError&&(
          <div style={{marginBottom:8}}>
            <div style={{color:"#A32D2D",fontSize:12,marginBottom:8,lineHeight:1.7,whiteSpace:"pre-line"}}>{searchError}</div>
            {/* 조회 실패 시 수동 등록 옵션 */}
            {(searchError.includes("존재하지 않는") || searchError.includes("사설 서버") || searchError.includes("실패"))&&(
              <div style={{background:T.bg,borderRadius:8,border:`0.5px solid ${T.border}`,padding:"12px 14px"}}>
                <div style={{fontSize:12,fontWeight:600,color:T.sub,marginBottom:8}}>✏️ 직접 입력으로 즐겨찾기 추가</div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                    <div>
                      <div style={{fontSize:10,color:T.sub,marginBottom:3}}>닉네임</div>
                      <input type="text" value={searchNick} onChange={e=>setSearchNick(e.target.value)}
                        placeholder="닉네임" style={{...IS,fontSize:12}}/>
                    </div>
                    <div>
                      <div style={{fontSize:10,color:T.sub,marginBottom:3}}>직업</div>
                      <input type="text" id="manualJob" placeholder="예: 아크" style={{...IS,fontSize:12}}/>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                    <div>
                      <div style={{fontSize:10,color:T.sub,marginBottom:3}}>레벨</div>
                      <input type="number" id="manualLevel" placeholder="예: 260" style={{...IS,fontSize:12}}/>
                    </div>
                    <div>
                      <div style={{fontSize:10,color:T.sub,marginBottom:3}}>서버</div>
                      <select id="manualWorld" style={{...IS,fontSize:12}}>
                        <option>크로아</option><option>베라</option><option>리부트</option>
                        <option>리부트2</option><option>스카니아</option><option>루나</option>
                        <option>엘리시움</option><option>제니스</option><option>크라비스</option>
                        <option>오로라</option><option>아케인</option><option>노바</option>
                        <option>메이플랜드</option><option>기타</option>
                      </select>
                    </div>
                  </div>
                  <button onClick={()=>{
                    const job = document.getElementById("manualJob").value.trim();
                    const level = document.getElementById("manualLevel").value;
                    const world = document.getElementById("manualWorld").value;
                    if(!searchNick.trim()) return alert("닉네임을 입력해주세요");
                    addFavorite({nickname:searchNick.trim(), job:job||"미입력", level:+level||0, world, image:null});
                    setSearchError(""); setSearchNick("");
                  }} style={{background:"#534AB7",color:"#fff",border:"none",borderRadius:8,padding:"8px 0",fontSize:13,fontWeight:500,cursor:"pointer"}}>
                    ⭐ 즐겨찾기 추가
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {searchResult&&(
          <div style={{background:T.charBg,borderRadius:10,padding:14}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
              {searchResult.image&&<CharAvatar src={searchResult.image} alt={searchResult.nickname} size={60}/>}
              <div>
                <div style={{fontSize:16,fontWeight:500,color:T.text}}>{searchResult.nickname}</div>
                <div style={{fontSize:12,color:T.sub}}>{searchResult.world} · {searchResult.job} · Lv.{searchResult.level}</div>
                <div style={{fontSize:12,color:T.sub}}>유니온 {searchResult.unionLevel?.toLocaleString()} · {searchResult.unionGrade}</div>
              </div>
            </div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              <button onClick={()=>addFavorite(searchResult)} style={{flex:1,background:"#534AB7",color:"#fff",border:"none",borderRadius:8,padding:10,fontSize:13,cursor:"pointer"}}>⭐ 즐겨찾기 추가</button>
              <button onClick={()=>handleLoadInfo(searchResult.nickname)} style={{flex:1,background:"transparent",border:"0.5px solid #534AB7",borderRadius:8,padding:10,fontSize:13,color:"#534AB7",cursor:"pointer"}}>📊 INFO</button>
              <button onClick={()=>{addFavorite(searchResult);selectChar(searchResult);}} style={{flex:1,background:"#0F6E56",color:"#fff",border:"none",borderRadius:8,padding:10,fontSize:13,cursor:"pointer"}}>수익입력 →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const IncomeContent=(
    <div>
      {SelBar}
      <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16,marginBottom:10}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div style={{fontSize:13,fontWeight:500,color:"#534AB7"}}>수익 입력</div>
          {/* 보스 사용량 카운터 */}
          {selChar&&incType==="boss"&&(()=>{
            const bn = (name)=>name.split(" (")[0].trim();
            const bRecs=(records||[]).filter(r=>r.kind==="income"&&r.type==="boss"&&r.nickname===selChar.nickname&&Array.isArray(r.bossList));
            const _d = new Date((incDate||today())+"T00:00:00");
            const _day = _d.getDay(); const _diff = _day>=4?_day-4:_day+3;
            _d.setDate(_d.getDate()-_diff); _d.setHours(0,0,0,0);
            const _wStart = localDateStr(_d);
            const _wEnd = (()=>{ const e=new Date(_wStart+"T00:00:00"); e.setDate(e.getDate()+6); return localDateStr(e); })();
            const _mStart = (incDate||today()).slice(0,7)+"-01";
            const [_y,_m] = (incDate||today()).slice(0,7).split("-").map(Number);
            const _mEnd = `${(incDate||today()).slice(0,7)}-${String(new Date(_y,_m,0).getDate()).padStart(2,"0")}`;
            const wkNames=new Set(bRecs.filter(r=>r.date>=_wStart&&r.date<=_wEnd).flatMap(r=>r.bossList?.filter(b=>BOSSES.weekly.some(w=>w.name===b.name))||[]).map(b=>bn(b.name)));
            const mo=bRecs.filter(r=>r.date>=_mStart&&r.date<=_mEnd).reduce((s,r)=>s+(r.bossList?.filter(b=>BOSSES.monthly.some(m=>m.name===b.name)).length||0),0);
            const wk=wkNames.size;
            return(
              <div style={{display:"flex",gap:6}}>
                <span style={{padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:600,border:`0.5px solid ${wk>=12?"#A32D2D":"#534AB7"}`,background:wk>=12?(T.dark?"#2A0F0F":"#FCEBEB"):"#EEEDFE",color:wk>=12?"#A32D2D":"#534AB7"}}>주간 {wk}/12</span>
                <span style={{padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:600,border:`0.5px solid ${mo>=1?"#A32D2D":"#A32D2D44"}`,background:mo>=1?(T.dark?"#2A0F0F":"#FCEBEB"):(T.dark?"#1A0A0A":"#FFF5F5"),color:"#A32D2D"}}>월간 {mo}/1</span>
              </div>
            );
          })()}
        </div>
        <div style={{marginBottom:10}}>
          <DateNavPicker value={incDate} onChange={setIncDate} T={T} label="날짜"/>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:8}}><label style={{fontSize:11,color:T.sub}}>수익 유형</label>
            <select value={incType} onChange={e=>setIncType(e.target.value)} style={IS}>
              <option value="hunt">사냥/일퀘</option><option value="boss">보스</option>
              <option value="event">이벤트/기타</option><option value="drop">물욕템</option><option value="epic">에픽던전주화</option>
            </select>
        </div>
        {incType==="drop"&&(
          <div style={{marginBottom:10}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontSize:11,fontWeight:500,color:T.sub}}>💎 물욕템 목록</div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:10,color:T.sub}}>수수료</span>
                {[3,5].map(f=>(
                  <button key={f} onClick={()=>setMktFee(f)}
                    style={{padding:"3px 10px",borderRadius:10,fontSize:10,fontWeight:600,cursor:"pointer",
                      border:`1px solid ${mktFee===f?"#534AB7":T.border}`,
                      background:mktFee===f?"#534AB7":"transparent",
                      color:mktFee===f?"#fff":T.sub}}>
                    {f}%
                  </button>
                ))}
              </div>
            </div>

            {dropItems.map((it,idx)=>{
              const rawPrice = +it.price||0;
              const qty = +it.qty||1;
              const fee = Math.floor(rawPrice*(mktFee/100))*qty;
              const net = calcNetPrice(rawPrice, qty, mktFee);
              const receipt = net + MKT_DEPOSIT;
              return (
              <div key={idx} style={{background:T.bg,borderRadius:10,border:`0.5px solid ${rawPrice>0?"#534AB7":T.border}`,padding:"10px",marginBottom:6}}>
                {/* 1행: 아이템명 / 판매등록가 / 수량 / 삭제 */}
                <div style={{display:"flex",gap:6,marginBottom:rawPrice>0?8:0,alignItems:"center"}}>
                  <input type="text" value={it.name} placeholder="아이템명"
                    onChange={e=>setDropItems(p=>p.map((x,i)=>i===idx?{...x,name:e.target.value}:x))}
                    style={{...IS,flex:3,minWidth:0}}/>
                  <input type="number" value={it.price} placeholder="판매 등록가"
                    onChange={e=>setDropItems(p=>p.map((x,i)=>i===idx?{...x,price:e.target.value}:x))}
                    style={{...IS,flex:3,minWidth:0}}/>
                  <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
                    <button onClick={()=>setDropItems(p=>p.map((x,i)=>i===idx?{...x,qty:Math.max(1,(+x.qty||1)-1)}:x))}
                      style={{width:24,height:24,borderRadius:5,border:`0.5px solid ${T.border}`,background:T.card,color:T.text,cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                    <span style={{fontSize:12,fontWeight:600,color:T.text,minWidth:20,textAlign:"center"}}>{qty}</span>
                    <button onClick={()=>setDropItems(p=>p.map((x,i)=>i===idx?{...x,qty:(+x.qty||1)+1}:x))}
                      style={{width:24,height:24,borderRadius:5,border:`0.5px solid ${T.border}`,background:T.card,color:T.text,cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                  </div>
                  {dropItems.length>1&&(
                    <button onClick={()=>setDropItems(p=>p.filter((_,i)=>i!==idx))}
                      style={{background:"transparent",border:"none",color:"#A32D2D",fontSize:16,cursor:"pointer",flexShrink:0,padding:"0 2px"}}>✕</button>
                  )}
                </div>

                {/* 2행: 가격 입력 시 즉시 계산 표시 */}
                {rawPrice>0&&(
                  <div style={{background:T.card,borderRadius:8,padding:"8px 10px"}}>
                    {/* 요약 한 줄 */}
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                      <span style={{fontSize:11,color:T.sub}}>
                        {fmt(rawPrice)} × {qty}{qty>1?"개":""} = {fmt(rawPrice*qty)}
                      </span>
                      <span style={{fontSize:13,fontWeight:700,color:"#534AB7"}}>실수령 {fmt(receipt)}</span>
                    </div>
                    {/* 상세 breakdown */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"2px 12px",fontSize:10,color:T.sub}}>
                      <div style={{display:"flex",justifyContent:"space-between"}}>
                        <span>보증금</span><span style={{color:"#A32D2D"}}>−{fmt(MKT_DEPOSIT)}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between"}}>
                        <span>수수료 ({mktFee}%)</span><span style={{color:"#A32D2D"}}>−{fmt(fee)}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between"}}>
                        <span>보증금 반환</span><span style={{color:"#0F6E56"}}>+{fmt(MKT_DEPOSIT)}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between"}}>
                        <span>판매 수익</span><span style={{color:"#3B6D11",fontWeight:600}}>{fmt(net)}</span>
                      </div>
                    </div>
                    {/* 즉시 반영 체크 */}
                    <div style={{borderTop:`0.5px solid ${T.border}`,marginTop:6,paddingTop:6,
                      display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}
                      onClick={()=>setDropItems(p=>p.map((x,i)=>i===idx?{...x,instant:!x.instant}:x))}>
                      <div style={{width:15,height:15,borderRadius:3,flexShrink:0,
                        border:`1.5px solid ${it.instant?"#534AB7":T.sub}`,
                        background:it.instant?"#534AB7":"transparent",
                        display:"flex",alignItems:"center",justifyContent:"center"}}>
                        {it.instant&&<span style={{color:"#fff",fontSize:9}}>✓</span>}
                      </div>
                      <span style={{fontSize:11,color:it.instant?"#534AB7":T.sub,fontWeight:it.instant?600:400}}>
                        즉시 수익 반영 {it.instant?`(+${fmt(receipt)})` : "(미체크 시 보관함만 등록)"}
                      </span>
                    </div>
                  </div>
                )}
                {rawPrice===0&&(
                  <div style={{fontSize:10,color:T.sub,marginTop:4}}>
                    판매 등록가를 입력하면 수수료가 자동 계산됩니다.
                  </div>
                )}
              </div>
              );
            })}

            <button onClick={()=>setDropItems(p=>[...p,{name:"",price:"",qty:1,instant:false}])}
              style={{background:"transparent",border:`0.5px dashed ${T.border}`,borderRadius:8,
                padding:"8px 0",fontSize:12,color:T.sub,cursor:"pointer",width:"100%",marginBottom:8}}>
              + 아이템 추가
            </button>

            {/* 합계 미리보기 */}
            {dropItems.some(it=>it.name.trim()&&+it.price>0)&&(
              <div style={{background:T.prevBg,borderRadius:8,padding:"10px 12px"}}>
                <div style={{fontSize:11,color:T.sub,marginBottom:6}}>
                  등록 예정 ({dropItems.filter(it=>it.name.trim()).length}개)
                </div>
                {dropItems.filter(it=>it.name.trim()).map((it,i)=>{
                  const q=+it.qty||1;
                  const r=+it.price||0;
                  const recv=r>0?calcNetPrice(r,q,mktFee)+MKT_DEPOSIT:0;
                  return(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"3px 0",borderBottom:`0.5px solid ${T.border}`}}>
                    <span style={{color:T.text}}>💎 {it.name}{q>1?` ×${q}`:""}</span>
                    <span style={{color:it.instant?"#534AB7":T.sub,fontSize:11}}>
                      {r>0?(it.instant?`실수령 +${fmt(recv)}`:fmt(r*q)):"가격 미입력"}
                      <span style={{marginLeft:4,fontSize:10}}>{it.instant?"✓반영":"보관함"}</span>
                    </span>
                  </div>
                  );
                })}
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:700,marginTop:8}}>
                  <span style={{color:"#534AB7"}}>즉시 반영 합계</span>
                  <span style={{color:"#534AB7"}}>{fmt(dropItems.filter(it=>it.instant&&it.name.trim()).reduce((s,it)=>{
                    const q=+it.qty||1; return s+calcNetPrice(+it.price||0,q,mktFee)+MKT_DEPOSIT;
                  },0))}</span>
                </div>
              </div>
            )}
          </div>
        )}
        {/* 사냥 시간 — 물욕템 선택 시 숨김 */}
        {incType!=="drop"&&incType==="hunt"&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
            <div style={{display:"flex",flexDirection:"column",gap:3}}>
              <label style={{fontSize:11,color:T.sub}}>사냥 시간</label>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <button onClick={()=>setIncMins(v=>String(Math.max(0,(+v||0)-30)))}
                  style={{width:32,height:32,borderRadius:8,border:`0.5px solid ${T.border}`,background:T.card,color:T.text,fontSize:18,cursor:"pointer",flexShrink:0}}>−</button>
                <div style={{flex:1,textAlign:"center",fontSize:13,fontWeight:600,color:T.text,background:T.inp,borderRadius:8,padding:"6px 0",border:`0.5px solid ${T.border}`}}>
                  {+incMins>0 ? `${incMins}분` : "0분"}
                </div>
                <button onClick={()=>setIncMins(v=>String((+v||0)+30))}
                  style={{width:32,height:32,borderRadius:8,border:`0.5px solid ${T.border}`,background:T.card,color:T.text,fontSize:18,cursor:"pointer",flexShrink:0}}>+</button>
              </div>
              {/* 직접 입력 */}
              <input type="number" value={incMins} onChange={e=>setIncMins(e.target.value)}
                placeholder="직접 입력 (분)" style={{...IS,fontSize:11,color:T.sub}}/>
            </div>
            <div style={{background:T.prevBg,borderRadius:8,padding:"8px 12px"}}><div style={{fontSize:11,color:T.sub}}>분당 메소</div><div style={{fontSize:13,fontWeight:500,color:"#534AB7"}}>{mins>0?fmt(Math.round(mesoV/mins)):"—"}</div></div>
            <div style={{background:T.prevBg,borderRadius:8,padding:"8px 12px"}}><div style={{fontSize:11,color:T.sub}}>분당 솔조각</div><div style={{fontSize:13,fontWeight:500,color:"#0F6E56"}}>{mins>0?(((+incSol1||0)+(+incSol2||0)+(+incSol3||0))/mins).toFixed(2)+"개":"—"}</div></div>
          </div>
        )}

        {/* ── 에픽던전 UI ── */}
        {incType==="epic"&&(()=>{
          const reward = calcEpicReward(epicDungeon, epicExpLv, epicSolLv);
          return (
            <div style={{background:T.card,border:`0.5px solid ${T.border}`,borderRadius:12,padding:14,marginBottom:10}}>
              <div style={{fontSize:12,fontWeight:600,color:"#534AB7",marginBottom:10}}>🔮 에픽던전</div>
              {/* 던전 선택 */}
              <div style={{display:"flex",gap:6,marginBottom:12}}>
                {EPIC_DUNGEONS.map(d=>(
                  <button key={d.key} onClick={()=>{setEpicDungeon(d.key);setEpicExpLv(0);setEpicSolLv(0);}}
                    style={{flex:1,padding:"7px 4px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",
                      border:`1.5px solid ${epicDungeon===d.key?"#534AB7":T.border}`,
                      background:epicDungeon===d.key?"#534AB7":"transparent",
                      color:epicDungeon===d.key?"#fff":T.sub}}>
                    {d.name}
                  </button>
                ))}
              </div>
              {/* EXP 보너스 */}
              <div style={{marginBottom:10}}>
                <div style={{fontSize:11,color:T.sub,marginBottom:6}}>EXP 보너스</div>
                <div style={{display:"flex",gap:6}}>
                  {[0,1,2].map(lv=>(
                    <button key={lv} onClick={()=>setEpicExpLv(lv)}
                      style={{flex:1,padding:"6px 0",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",
                        border:`1px solid ${epicExpLv===lv?"#E4A800":T.border}`,
                        background:epicExpLv===lv?"#E4A800":"transparent",
                        color:epicExpLv===lv?"#fff":T.sub}}>
                      Lv.{lv}
                    </button>
                  ))}
                </div>
              </div>
              {/* 솔에르다 보너스 */}
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:T.sub,marginBottom:6}}>솔에르다 보너스</div>
                <div style={{display:"flex",gap:6}}>
                  {[0,1,2].map(lv=>(
                    <button key={lv} onClick={()=>setEpicSolLv(lv)}
                      style={{flex:1,padding:"6px 0",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",
                        border:`1px solid ${epicSolLv===lv?"#0F6E56":T.border}`,
                        background:epicSolLv===lv?"#0F6E56":"transparent",
                        color:epicSolLv===lv?"#fff":T.sub}}>
                      Lv.{lv}
                    </button>
                  ))}
                </div>
              </div>
              {/* 자동 계산 결과 */}
              {reward&&(
                <div style={{background:T.bg,borderRadius:8,padding:"10px 12px",marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                    <span style={{color:T.sub}}>세라자르 주화</span>
                    <span style={{color:"#E4A800",fontWeight:600}}>{reward.coin}개 → {fmt(reward.mesoIncome)}</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                    <span style={{color:T.sub}}>솔에르다 기운</span>
                    <span style={{color:"#0F6E56",fontWeight:600}}>{reward.solDisplay} / {reward.gunRemainder} ({reward.totalGunEquiv.toLocaleString()}개)</span>
                  </div>
                  {reward.darkGun>0&&(
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.sub}}>
                      <span>ㄴ 짙은 기운 포함</span>
                      <span>{reward.darkGun}개 (기운 {(reward.darkGun*500).toLocaleString()}개)</span>
                    </div>
                  )}
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.sub,marginTop:2}}>
                    <span>솔에르다 조각 (교환불가)</span>
                    <span>{reward.solFragment}개</span>
                  </div>
                </div>
              )}
              {/* 사용 메이플포인트 */}
              <div style={{display:"flex",flexDirection:"column",gap:3}}>
                <label style={{fontSize:11,color:T.sub}}>사용 메이플포인트 (지출로 별도 기록)</label>
                <input type="number" value={epicMp} onChange={e=>setEpicMp(e.target.value)}
                  placeholder={`${reward?.mp?.toLocaleString()||0} MP`} style={IS}/>
              </div>
            </div>
          );
        })()}
        {incType==="boss"&&presets&&(presets.preset1?.bosses?.length>0||presets.preset2?.bosses?.length>0||presets.preset3?.bosses?.length>0)&&(
          <div style={{display:"flex",gap:6,marginBottom:8}}>
            {["preset1","preset2","preset3"].map((slotKey,i)=>{
              const slot = presets[slotKey];
              if(!slot?.bosses?.length) return null;
              return (
                <button key={slotKey} onClick={()=>applyPresetToIncomeBoss(slotKey)}
                  style={{flex:1,background:"transparent",border:"1.5px solid #E4A800",borderRadius:8,
                    padding:"7px 8px",fontSize:11,fontWeight:600,color:"#E4A800",cursor:"pointer",
                    whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  ⭐{i+1} {slot.name||""}
                </button>
              );
            })}
          </div>
        )}
        {incType==="boss"&&<BossSection T={T} isMobile={isMobile} checkedBosses={checkedBosses} setCheckedBosses={setCheckedBosses} bossParty={bossParty} setBossParty={setBossParty} bossPartyMembers={bossPartyMembers} setBossPartyMembers={setBossPartyMembers} bossDiffs={bossDiffs} setBossDiffs={setBossDiffs} records={records} selChar={selChar} incDate={incDate} onLimitToast={(msg)=>showToast(msg,"⚠️")} onDropChange={setBossDropList} bossPricePhaseMap={bossPricePhaseMap} setBossPricePhaseMap={setBossPricePhaseMap}/>}
        {incType!=="drop"&&<>
        <div style={{fontSize:11,fontWeight:500,color:T.sub,margin:"10px 0 6px"}}>솔 에르다</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
          <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>솔 에르다 기운</label><input type="number" value={incSol0} onChange={e=>setIncSol0(e.target.value)} placeholder="0" style={IS}/></div>
          <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>솔조각 (사냥)</label><input type="number" value={incSol3} onChange={e=>setIncSol3(e.target.value)} placeholder="0" style={IS}/></div>
          <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>솔조각 (교환불가)</label><input type="number" value={incSol4} onChange={e=>setIncSol4(e.target.value)} placeholder="0" style={IS_NOEX}/></div>
        </div>
        {/* 드롭 아이템 */}
        {incType==="hunt"&&(
          <div style={{background:T.card,border:`0.5px solid ${T.border}`,borderRadius:12,padding:12,marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:500,color:T.sub,marginBottom:8}}>드롭 아이템</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              {DROP_ITEMS.map(d=>(
                <div key={d.key} style={{display:"flex",flexDirection:"column",gap:3}}>
                  <label style={{fontSize:11,color:T.sub}}>{d.name} <span style={{color:T.border}}>({d.price.toLocaleString()}메소)</span></label>
                  <input type="number" value={dropQty[d.key]} onChange={e=>setDropQty(p=>({...p,[d.key]:e.target.value}))} placeholder="0개" style={IS}/>
                </div>
              ))}
            </div>
            {dropV>0&&(
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,paddingTop:8,borderTop:`0.5px solid ${T.border}`}}>
                <span style={{color:T.sub}}>드롭 수익 합산</span>
                <span style={{color:"#534AB7",fontWeight:500}}>{fmt(dropV)}</span>
              </div>
            )}
          </div>
        )}
        <div style={{fontSize:11,fontWeight:500,color:T.sub,margin:"10px 0 6px"}}>메소 & 재화</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
          <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>메소 획득량</label><NumberInput value={incMeso} onChange={setIncMeso} placeholder="0" IS={IS}/></div>
          <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>코어 잼스톤</label><input type="number" value={incCore} onChange={e=>setIncCore(e.target.value)} placeholder="0" style={IS}/></div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
          <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>기타 수익</label><NumberInput value={incEtc} onChange={setIncEtc} placeholder="0" IS={IS}/></div>
          <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>마일리지 (수기)</label><input type="number" value={incMile} onChange={e=>setIncMile(e.target.value)} placeholder="0" style={IS}/></div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:8}}><label style={{fontSize:11,color:T.sub}}>메모</label><input type="text" value={incNote} onChange={e=>setIncNote(e.target.value)} placeholder="특이사항..." style={IS}/></div>
        </>}
        {/* 메모 — 물욕템일 때도 표시 */}
        {incType==="drop"&&<div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:8}}><label style={{fontSize:11,color:T.sub}}>메모</label><input type="text" value={incNote} onChange={e=>setIncNote(e.target.value)} placeholder="특이사항..." style={IS}/></div>}
        <div style={{background:T.prevBg,borderRadius:8,padding:"10px 12px",margin:"10px 0"}}>
          {incType!=="drop"&&<>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"3px 0"}}><span style={{color:T.sub}}>솔 에르다 환산가</span><span style={{color:"#0F6E56",fontWeight:500}}>{fmt(solV)}</span></div>
          {dropV>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"3px 0"}}><span style={{color:T.sub}}>드롭 아이템</span><span style={{color:"#534AB7",fontWeight:500}}>{fmt(dropV)}</span></div>}
          {epicV>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"3px 0"}}><span style={{color:T.sub}}>세라자르 주화</span><span style={{color:"#E4A800",fontWeight:500}}>{fmt(epicV)}</span></div>}
          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"3px 0"}}><span style={{color:T.sub}}>메소 + 기타</span><span style={{color:"#3B6D11",fontWeight:500}}>{fmt(mesoV)}</span></div>
          {incType==="boss"&&<div style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"3px 0"}}><span style={{color:T.sub}}>보스 결정석</span><span style={{color:"#854F0B",fontWeight:500}}>{fmt(bossTotal)}</span></div>}
          </>}
          {incType==="drop"&&(
            <div style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"3px 0"}}>
              <span style={{color:T.sub}}>즉시 반영 아이템</span>
              <span style={{color:"#534AB7",fontWeight:500}}>{dropItems.filter(it=>it.instant&&it.name.trim()).length}개 · {fmt(dropItems.filter(it=>it.instant&&it.name.trim()).reduce((s,it)=>s+calcNetPrice(+it.price||0,+it.qty||1,mktFee)+MKT_DEPOSIT,0))}</span>
            </div>
          )}
          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"6px 0 3px",borderTop:`0.5px solid ${T.border}`,marginTop:4}}>
            <span style={{fontWeight:500,color:"#534AB7"}}>총 수익</span><span style={{fontSize:17,fontWeight:500,color:"#534AB7"}}>{fmt(totalInc)}</span>
          </div>
        </div>
        <button onClick={handleSaveIncome} disabled={saving} style={{background:saving?"#9993D8":"#534AB7",color:"#fff",border:"none",borderRadius:8,padding:11,fontSize:13,fontWeight:500,cursor:saving?"default":"pointer",width:"100%",marginTop:8}}>{saving?"저장 중...":"수익 저장"}</button>
      </div>
    </div>
  );

  const ExpenseContent=(
    <div>
      {SelBar}
      <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16}}>
        <div style={{fontSize:13,fontWeight:500,color:"#534AB7",marginBottom:12}}>지출 입력</div>
        <div style={{marginBottom:10}}>
          <DateNavPicker value={expDate} onChange={setExpDate} T={T} label="날짜"/>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:8}}><label style={{fontSize:11,color:T.sub}}>지출 유형</label>
            <select value={expType} onChange={e=>{
              if(e.target.value==="market"){
                setTab("market"); setMktTab("buy");
                showToast("경매장 구매 탭으로 이동했어요","🏪");
              } else {
                setExpType(e.target.value);
              }
            }} style={IS}>
              <option value="cube">큐브 강화</option><option value="star">스타포스</option><option value="spell">주문서</option>
              <option value="symbol">심볼업</option><option value="boss-entry">보스 입장권</option>
              <option value="cash">캐시 아이템</option><option value="market">경매장 구매 →</option><option value="etc">기타</option>
            </select>
        </div>
        {expType==="symbol"&&(()=>{
          const areas  = expSymbolForce==="arcane" ? ARCANE_AREAS : expSymbolForce==="grand" ? GRAND_AUTHENTIC_AREAS : AUTHENTIC_AREAS;
          const maxLv  = expSymbolForce==="arcane" ? 20 : 11;
          const emoji  = expSymbolForce==="arcane" ? "🌊" : expSymbolForce==="grand" ? "🔮" : "⚡";
          // 지역별 계산
          const results = areas.map(area => {
            const { cur, tgt } = symLevels[area] || {};
            const c = parseInt(cur)||0, t = parseInt(tgt)||0;
            if (c>=1 && t>c && t<=maxLv) return calcSymbolCost(expSymbolForce, c, t, area, expDate);
            return null;
          });
          const totalMeso   = results.reduce((s,r)=>s+(r?.meso||0),0);
          const totalPieces = results.reduce((s,r)=>s+(r?.pieces||0),0);
          const validCount  = results.filter(Boolean).length;
          return (
          <div style={{marginBottom:8}}>
            {/* 포스 탭 */}
            <div style={{display:"flex",gap:0,marginBottom:10,borderRadius:8,overflow:"hidden",border:`1px solid ${T.border}`}}>
              {[["arcane","🌊 ARCANE"],["authentic","⚡ AUTHENTIC"],["grand","🔮 GRAND"]].map(([v,label])=>(
                <button key={v} onClick={()=>{setExpSymbolForce(v);setSymLevels(initSymLevels(v));}}
                  style={{flex:1,padding:"9px 0",fontSize:11,fontWeight:600,border:"none",cursor:"pointer",
                    background:expSymbolForce===v?"#534AB7":T.card,
                    color:expSymbolForce===v?"#fff":T.sub}}>
                  {label}
                </button>
              ))}
            </div>
            {expSymbolForce==="grand"&&(
              <div style={{fontSize:10,color:T.sub,marginBottom:8,padding:"6px 10px",
                background:T.card,borderRadius:6,border:`0.5px solid ${T.border}`}}>
                ※ 그랜드 어센틱 심볼은 주스탯을 올려주지 않습니다.
              </div>
            )}
            {/* 그리드 — grand는 2칸, 나머지는 3칸 */}
            <div style={{display:"grid",gridTemplateColumns:expSymbolForce==="grand"?"1fr 1fr":"1fr 1fr 1fr",gap:6,marginBottom:10}}>
              {areas.map((area, i) => {
                const lv = symLevels[area] || { cur:"", tgt:"" };
                const r  = results[i];
                const hasResult = r && r.meso > 0;
                const curVal = parseInt(lv.cur)||0;
                return (
                  <div key={area} style={{background:T.card,border:`1px solid ${hasResult?"#534AB7":T.border}`,
                    borderRadius:9,padding:"8px 6px",display:"flex",flexDirection:"column",gap:5}}>
                    <div style={{fontSize:11,fontWeight:700,color:hasResult?"#534AB7":T.sub,textAlign:"center",
                      whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                      {emoji} {area}
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:4}}>
                      <select value={lv.cur}
                        onChange={e=>{
                          const newCur=e.target.value;
                          const newTgt=parseInt(lv.tgt)<=parseInt(newCur)?"":lv.tgt;
                          setSymLevels(prev=>({...prev,[area]:{cur:newCur,tgt:newTgt}}));
                        }}
                        style={{...IS,fontSize:13,padding:"5px 4px",textAlign:"center",fontWeight:500}}>
                        <option value="">현재 레벨</option>
                        {[...Array(maxLv-1)].map((_,idx)=>(
                          <option key={idx+1} value={idx+1}>Lv.{idx+1}</option>
                        ))}
                      </select>
                      <select value={lv.tgt}
                        onChange={e=>setSymLevels(prev=>({...prev,[area]:{...prev[area],tgt:e.target.value}}))}
                        style={{...IS,fontSize:13,padding:"5px 4px",textAlign:"center",fontWeight:500,
                          borderColor:lv.tgt&&parseInt(lv.tgt)===maxLv?"#E4A800":hasResult?"#534AB7":undefined,
                          color:lv.tgt&&parseInt(lv.tgt)===maxLv?"#E4A800":undefined}}>
                        <option value="">목표 레벨</option>
                        {[...Array(maxLv)].map((_,idx)=>idx+1).filter(v=>v>curVal).map(v=>(
                          <option key={v} value={v}>{v===maxLv?`★ Lv.${v}`:`Lv.${v}`}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{fontSize:12,textAlign:"center",fontWeight:700,
                      color:hasResult?"#534AB7":T.border,minHeight:16}}>
                      {hasResult ? fmt(r.meso) : "—"}
                    </div>
                    {hasResult&&(
                      <div style={{fontSize:10,textAlign:"center",color:T.sub}}>
                        🧩 {r.pieces.toLocaleString()}개
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* 합계 */}
            {validCount>0&&(
              <div style={{background:dark?"#1e1e2e":"#f0efff",borderRadius:9,padding:"10px 12px",
                border:`1px solid ${dark?"#534AB740":"#534AB730"}`,marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <span style={{fontSize:12,color:T.sub}}>💰 총 필요 메소</span>
                  <span style={{fontSize:15,fontWeight:700,color:"#534AB7"}}>{fmt(totalMeso)}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:12,color:T.sub}}>🧩 총 필요 조각</span>
                  <span style={{fontSize:13,fontWeight:600,color:"#059669"}}>{totalPieces.toLocaleString()}개</span>
                </div>
              </div>
            )}
          </div>
          );
        })()}
        {expType!=="symbol"&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
            <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>지출 메소</label><NumberInput value={expMeso} onChange={setExpMeso} placeholder="0" IS={IS}/></div>
            <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>넥슨 캐시(원)</label><input type="number" value={expCash} onChange={e=>setExpCash(e.target.value)} placeholder="0" style={IS}/></div>
          </div>
        )}
        {expType!=="symbol"&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
            <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>마일리지 사용</label><input type="number" value={expMile} onChange={e=>setExpMile(e.target.value)} placeholder="0" style={IS}/></div>
            <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>메모</label><input type="text" value={expNote} onChange={e=>setExpNote(e.target.value)} placeholder="무엇에 썼는지..." style={IS}/></div>
          </div>
        )}
        {expType==="symbol"&&(
          <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:8}}>
            <label style={{fontSize:11,color:T.sub}}>메모</label>
            <input type="text" value={expNote} onChange={e=>setExpNote(e.target.value)} placeholder="예) 이번주 심볼 업그레이드" style={IS}/>
          </div>
        )}
        <button onClick={handleSaveExpense} disabled={saving} style={{background:"#A32D2D",color:"#fff",border:"none",borderRadius:8,padding:11,fontSize:13,fontWeight:500,cursor:"pointer",width:"100%",marginTop:8}}>{saving?"저장 중...":"지출 저장"}</button>
      </div>
    </div>
  );

  const handleSaveExchange = async () => {
    if(!exchMeso||!exchMp) return alert("거래 메소와 실지불 메포를 입력해주세요");
    const totalMeso = Math.round(parseFloat(exchMeso) * 100000000);
    const totalMp   = Math.round(parseFloat(exchMp));
    const marketMp  = parseFloat(exchMarket)||0;
    const realRate  = totalMp / parseFloat(exchMeso);  // 실거래 억당 메포
    if(totalMeso<=0||totalMp<=0) return alert("올바른 값을 입력해주세요");
    setSaving(true);
    try {
      const commonFields = {
        exchMeso: parseFloat(exchMeso),   // 거래 억수
        exchMp:   totalMp,                // 실지불 메포
        exchMarket: marketMp,             // 시장가 (1억당 메포)
        exchRealRate: realRate,           // 실거래 단가 (1억당 메포)
        exchDir,
      };
      if(exchDir==="buy") {
        await saveIncome(user.uid,{
          date:exchDate, job:selChar?.job||"", nickname:selChar?.nickname||"",
          type:"exchange", meso:totalMeso, etc:0, sol0:0, sol1:0, sol2:0, sol3:0, sol4:0,
          core:0, mile:0, mins:0, note:exchNote||`메소 구매 (${realRate.toFixed(0)}MP/억)`,
          ...commonFields,
          bossIncome:0, bossList:[], totalSol:0, totalIncome:totalMeso,
        });
        await saveExpense(user.uid,{
          date:exchDate, job:selChar?.job||"", nickname:selChar?.nickname||"",
          type:"exchange", meso:0, cash:totalMp, mile:0,
          note:exchNote||`메소 구매 (${fmt(totalMeso)})`,
          ...commonFields,
        });
      } else {
        await saveIncome(user.uid,{
          date:exchDate, job:selChar?.job||"", nickname:selChar?.nickname||"",
          type:"exchange", meso:0, etc:0, sol0:0, sol1:0, sol2:0, sol3:0, sol4:0,
          core:0, mile:totalMp, mins:0, note:exchNote||`메소 판매 (${realRate.toFixed(0)}MP/억)`,
          ...commonFields,
          bossIncome:0, bossList:[], totalSol:0, totalIncome:0,
        });
        await saveExpense(user.uid,{
          date:exchDate, job:selChar?.job||"", nickname:selChar?.nickname||"",
          type:"exchange", meso:totalMeso, cash:0, mile:0,
          note:exchNote||`메소 판매 (${fmt(totalMeso)})`,
          ...commonFields,
        });
      }
      setExchMeso(""); setExchMp(""); setExchMarket(""); setExchNote("");
      showToast("환전 기록 완료!", "💱");
    } catch(e) { alert("오류: "+e.message); }
    setSaving(false);
  };

  // ── 경매장 저장 핸들러 ────────────────────────────
  const handleSaveMarket = async () => {
    if(!mktDate) return alert("날짜를 선택해주세요");
    setSaving(true);
    try {
      if(mktMode==="inventory") {
        const selectedItems = inventory.filter(it=>mktSelIds.includes(it.id));
        if(selectedItems.length===0) return setSaving(false)||alert("아이템을 선택해주세요");

        // 차액 정산: 수수료 적용 후 실수령액 기준
        let totalDiff   = 0;
        const soldList  = [];
        for(const it of selectedItems) {
          const rawPrice    = +mktPrices[it.id] || it.estimatedPrice || 0;
          const qty         = +mktQtys[it.id] || 1;
          const actualPrice = calcNetPrice(rawPrice, qty, mktFee); // 수수료 적용
          const estPrice    = it.estimatedPrice || 0;
          const diff        = it.instant ? (actualPrice - estPrice) : actualPrice;
          totalDiff   += diff;
          soldList.push({name:it.name, actualPrice, estPrice, diff, instant:it.instant, qty, fee:mktFee});
        }

        await saveIncome(user.uid,{
          date:mktDate, job:selChar?.job||"", nickname:selChar?.nickname||"",
          type:"market-sell",
          meso:totalDiff, etc:0, sol0:0, sol1:0, sol2:0, sol3:0, sol4:0,
          core:0, mile:0, mins:0,
          note:mktNote||selectedItems.map(it=>it.name).join(", "),
          dropList:soldList,
          mktDeposit:MKT_DEPOSIT, mktFee,
          bossIncome:0, bossList:[], totalSol:0, totalIncome:totalDiff,
        });
        for(const it of selectedItems) {
          const rawPrice    = +mktPrices[it.id] || it.estimatedPrice || 0;
          const qty         = +mktQtys[it.id] || 1;
          const actualPrice = calcNetPrice(rawPrice, qty, mktFee);
          await updateInventoryItem(user.uid, it.id, {sold:true, soldDate:mktDate, soldPrice:actualPrice});
        }
        setMktSelIds([]); setMktPrices({}); setMktQtys({});
      } else {
        // 직접 입력 — 수수료 적용 후 실수령액으로 저장
        const validItems = mktItems.filter(it=>it.name.trim()&&+it.price>0);
        if(validItems.length===0) return setSaving(false)||alert("아이템명과 판매가를 입력해주세요");
        const totalPrice = validItems.reduce((s,it)=>s+calcNetPrice(it.price, it.qty||1, mktFee),0);
        await saveIncome(user.uid,{
          date:mktDate, job:selChar?.job||"", nickname:selChar?.nickname||"",
          type:"market-sell",
          meso:totalPrice, etc:0, sol0:0, sol1:0, sol2:0, sol3:0, sol4:0,
          core:0, mile:0, mins:0,
          note:mktNote||validItems.map(it=>it.name).join(", "),
          dropList:validItems.map(it=>({name:it.name.trim(), price:calcNetPrice(it.price,it.qty||1,mktFee), qty:it.qty||1, fee:mktFee})),
          mktDeposit:MKT_DEPOSIT, mktFee,
          bossIncome:0, bossList:[], totalSol:0, totalIncome:totalPrice,
        });
        setMktItems([{name:"",price:"",qty:1}]);
      }
      setMktNote("");
      showToast("경매장 판매 기록 완료!", "🏪");
    } catch(e) { alert("오류: "+e.message); }
    setSaving(false);
  };

  // ── 환전 탭 UI ────────────────────────────────────
  const totalMesoVal  = parseFloat(exchMeso)||0;
  const totalMpVal    = parseFloat(exchMp)||0;
  const totalMarket   = parseFloat(exchMarket)||0;
  const totalMesoAbs  = Math.round(totalMesoVal * 100000000);
  const realRate      = totalMesoVal>0 ? (totalMpVal/totalMesoVal) : 0;
  const marketDiff    = totalMarket>0 ? (totalMarket - realRate) : 0;

  const ExchangeContent=(
    <div>
      {SelBar}
      <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16,marginBottom:10}}>
        <div style={{fontSize:13,fontWeight:500,color:"#534AB7",marginBottom:4}}>💱 환전 기록</div>
        <div style={{fontSize:11,color:T.sub,marginBottom:14,lineHeight:1.7}}>
          메소 ↔ 캐시 교환 내역을 기록합니다.<br/>
          저장 시 수익과 지출에 자동으로 동시 반영됩니다.
        </div>

        {/* 날짜 */}
        <div style={{marginBottom:12}}>
          <DateNavPicker value={exchDate} onChange={setExchDate} T={T} label="날짜"/>
        </div>

        {/* 교환 방향 */}
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,color:T.sub,marginBottom:6,fontWeight:500}}>교환 방향</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {[
              ["buy",  "💰 캐시 → 메소", "캐시로 메소 구매", "#534AB7"],
              ["sell", "📤 메소 → 캐시", "메소로 캐시 구매", "#3B6D11"],
            ].map(([val,label,sub,color])=>(
              <button key={val} onClick={()=>setExchDir(val)}
                style={{padding:"12px 10px",borderRadius:10,border:`1.5px solid ${exchDir===val?color:T.border}`,background:exchDir===val?(T.dark?`${color}22`:T.charBg):T.card,cursor:"pointer",textAlign:"left"}}>
                <div style={{fontSize:13,fontWeight:600,color:exchDir===val?color:T.text,marginBottom:2}}>{label}</div>
                <div style={{fontSize:11,color:T.sub}}>{sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* 3칸 입력 */}
        <div style={{background:T.bg,borderRadius:10,border:`0.5px solid ${T.border}`,padding:"14px",marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:600,color:T.sub,marginBottom:10}}>거래 정보 입력</div>
          <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:10}}>
            {/* 1. 거래 메소 */}
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:11,color:T.sub}}>① 거래 메소 (억 단위)</label>
              <div style={{display:"flex",alignItems:"center",gap:4}}>
                <input type="number" value={exchMeso} onChange={e=>setExchMeso(e.target.value)}
                  placeholder="예: 3" style={{...IS,flex:1}}/>
                <span style={{fontSize:11,color:T.sub,flexShrink:0}}>억</span>
              </div>
            </div>
            {/* 2. 실지불 메포 */}
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:11,color:T.sub}}>② 실제 {exchDir==="buy"?"지불":"수령"} 메이플포인트</label>
              <div style={{display:"flex",alignItems:"center",gap:4}}>
                <input type="number" value={exchMp} onChange={e=>setExchMp(e.target.value)}
                  placeholder="예: 6000" style={{...IS,flex:1}}/>
                <span style={{fontSize:11,color:T.sub,flexShrink:0}}>MP</span>
              </div>
            </div>
            {/* 3. 시장가 */}
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:11,color:T.sub}}>③ 당시 시장가 (선택 — 1억당 메포)</label>
              <div style={{display:"flex",alignItems:"center",gap:4}}>
                <input type="number" value={exchMarket} onChange={e=>setExchMarket(e.target.value)}
                  placeholder="예: 2100" style={{...IS,flex:1}}/>
                <span style={{fontSize:11,color:T.sub,flexShrink:0}}>MP/억</span>
              </div>
            </div>
          </div>

          {/* 미리보기 */}
          {totalMesoVal>0&&totalMpVal>0&&(
            <div style={{background:T.card,borderRadius:8,border:`0.5px solid #534AB744`,padding:"12px 14px"}}>
              <div style={{fontSize:11,fontWeight:600,color:"#534AB7",marginBottom:8}}>📋 거래 미리보기</div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"4px 0",borderBottom:`0.5px solid ${T.border}`}}>
                  <span style={{color:T.sub}}>{exchDir==="buy"?"💰 획득 메소":"💸 지출 메소"}</span>
                  <span style={{color:exchDir==="buy"?"#3B6D11":"#A32D2D",fontWeight:700}}>{exchDir==="buy"?"+":"-"}{fmt(totalMesoAbs)}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"4px 0",borderBottom:`0.5px solid ${T.border}`}}>
                  <span style={{color:T.sub}}>{exchDir==="buy"?"💸 지불 메포":"💰 수령 메포"}</span>
                  <span style={{color:exchDir==="buy"?"#A32D2D":"#3B6D11",fontWeight:700}}>{exchDir==="buy"?"-":"+"}{Math.round(totalMpVal).toLocaleString()} MP</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"4px 0",borderBottom:`0.5px solid ${T.border}`}}>
                  <span style={{color:T.sub}}>📊 실거래 단가</span>
                  <span style={{color:T.text,fontWeight:700}}>{realRate.toFixed(0).toLocaleString ? Math.round(realRate).toLocaleString() : Math.round(realRate)} MP/억</span>
                </div>
                {totalMarket>0&&(
                  <>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"4px 0",borderBottom:`0.5px solid ${T.border}`}}>
                      <span style={{color:T.sub}}>🏪 시장가</span>
                      <span style={{color:T.sub}}>{totalMarket.toLocaleString()} MP/억</span>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"4px 0"}}>
                      <span style={{color:T.sub}}>시장가 대비</span>
                      <span style={{color:marketDiff>0?"#3B6D11":"#A32D2D",fontWeight:700}}>
                        {marketDiff>0?"▼ ":"▲ "}{Math.abs(Math.round(marketDiff)).toLocaleString()} MP/억 {marketDiff>0?"이득":"손해"}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 메모 */}
        <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:14}}>
          <label style={{fontSize:11,color:T.sub}}>메모 (선택)</label>
          <input type="text" value={exchNote} onChange={e=>setExchNote(e.target.value)}
            placeholder="예: 스우 큐브용 메소 구매" style={IS}/>
        </div>

        {/* 저장 버튼 */}
        <button onClick={handleSaveExchange} disabled={saving||!exchMeso||!exchMp}
          style={{background:exchDir==="buy"?"#534AB7":"#3B6D11",color:"#fff",border:"none",borderRadius:8,padding:12,fontSize:13,fontWeight:500,cursor:"pointer",width:"100%",opacity:(!exchMeso||!exchMp)?0.5:1}}>
          {saving?"저장 중...":`💱 환전 저장 — ${exchDir==="buy"
            ?`+${fmt(totalMesoAbs)} / -${Math.round(totalMpVal).toLocaleString()}MP`
            :`-${fmt(totalMesoAbs)} / +${Math.round(totalMpVal).toLocaleString()}MP`}`}
        </button>
      </div>
    </div>
  );

  // ── 경매장 탭 UI ──────────────────────────────────
  const unsoldInventory = inventory.filter(it=>!it.sold);

  // calcNetPrice는 전역 선언 (App.js 상단)

  const mktSellTotal = mktMode==="inventory"
    ? inventory.filter(it=>mktSelIds.includes(it.id)).reduce((s,it)=>{
        const price = +mktPrices[it.id]||it.estimatedPrice||0;
        const qty   = +mktQtys[it.id]||1;
        return s + calcNetPrice(price, qty, mktFee);
      },0)
    : mktItems.filter(it=>it.name.trim()&&it.price).reduce((s,it)=>s+calcNetPrice(it.price,it.qty||1,mktFee),0);

  const MarketContent=(
    <div>
      {SelBar}

      {/* 판매 / 구매 탭 */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:10}}>
        {[["sell","💰 판매"],["buy","💸 구매"]].map(([k,l])=>(
          <button key={k} onClick={()=>setMktTab(k)}
            style={{padding:"10px 0",borderRadius:10,border:`1.5px solid ${mktTab===k?"#534AB7":T.border}`,
              background:mktTab===k?"#534AB7":T.card,color:mktTab===k?"#fff":T.sub,
              fontSize:13,fontWeight:mktTab===k?600:400,cursor:"pointer"}}>
            {l}
          </button>
        ))}
      </div>

      {/* ── 판매 탭 ── */}
      {mktTab==="sell"&&(
        <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16,marginBottom:10}}>
          <div style={{fontSize:13,fontWeight:500,color:"#534AB7",marginBottom:4}}>🏪 경매장 판매 기록</div>
          <div style={{fontSize:11,color:T.sub,marginBottom:14,lineHeight:1.7}}>
            파밍한 물욕템 판매 내역을 기록합니다.<br/>
            보관함에서 선택하거나 직접 입력할 수 있습니다.
          </div>

          <div style={{marginBottom:12}}>
            <DateNavPicker value={mktDate} onChange={setMktDate} T={T} label="판매 날짜"/>
          </div>

          {/* 수수료 */}
          <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:14,background:T.bg,borderRadius:8,padding:"10px 12px"}}>
            <span style={{fontSize:12,color:T.sub,flexShrink:0}}>수수료</span>
            {[3,5].map(f=>(
              <label key={f} style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",fontSize:13,fontWeight:mktFee===f?600:400,color:mktFee===f?"#534AB7":T.text}}>
                <input type="radio" name="mktFee" checked={mktFee===f} onChange={()=>setMktFee(f)} style={{accentColor:"#534AB7",width:15,height:15}}/>
                {f}%
              </label>
            ))}
            <span style={{fontSize:11,color:T.sub,marginLeft:"auto"}}>실수령 = 판매가 × {100-mktFee}% + 보증금 반환</span>
          </div>

          {/* 모드 선택 */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
            {[["inventory","📦 보관함에서 선택",`미판매 ${unsoldInventory.length}개`],
              ["direct","✏️ 직접 입력","바로 기록"]].map(([val,label,sub])=>(
              <button key={val} onClick={()=>setMktMode(val)}
                style={{padding:"12px 10px",borderRadius:10,border:`1.5px solid ${mktMode===val?"#534AB7":T.border}`,
                  background:mktMode===val?"#534AB722":T.card,cursor:"pointer",textAlign:"left"}}>
                <div style={{fontSize:13,fontWeight:600,color:mktMode===val?"#534AB7":T.text,marginBottom:2}}>{label}</div>
                <div style={{fontSize:11,color:T.sub}}>{sub}</div>
              </button>
            ))}
          </div>

          {/* 보관함 선택 모드 */}
          {mktMode==="inventory"&&(
            <div style={{marginBottom:12}}>
              {unsoldInventory.length===0?(
                <div style={{textAlign:"center",padding:"24px 0",color:T.sub,fontSize:12,background:T.bg,borderRadius:8}}>
                  <div style={{fontSize:28,marginBottom:6}}>📦</div>
                  <div>보관함이 비어있습니다</div>
                  <div style={{fontSize:11,marginTop:4}}>수익 탭 → 물욕템에서 아이템을 등록해주세요</div>
                </div>
              ):(
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {unsoldInventory.map(it=>{
                    const sel = mktSelIds.includes(it.id);
                    const qty = +mktQtys[it.id]||1;
                    const price = +mktPrices[it.id]||it.estimatedPrice||0;
                    const net = calcNetPrice(price, qty, mktFee);
                    return(
                      <div key={it.id}
                        style={{background:sel?"#534AB711":T.bg,borderRadius:8,border:`0.5px solid ${sel?"#534AB7":T.border}`,padding:"10px 12px",cursor:"pointer"}}
                        onClick={()=>setMktSelIds(p=>sel?p.filter(id=>id!==it.id):[...p,it.id])}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div style={{width:18,height:18,borderRadius:4,border:`1.5px solid ${sel?"#534AB7":T.sub}`,background:sel?"#534AB7":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                            {sel&&<span style={{color:"#fff",fontSize:11}}>✓</span>}
                          </div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:500,color:T.text}}>💎 {it.name}</div>
                            <div style={{fontSize:11,color:T.sub}}>{it.obtainDate} · {it.nickname||"공통"} · 예상가 {it.estimatedPrice?fmt(it.estimatedPrice):"미입력"}</div>
                          </div>
                        </div>
                        {sel&&(
                          <div style={{marginTop:8}} onClick={e=>e.stopPropagation()}>
                            <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:6}}>
                              <span style={{fontSize:11,color:T.sub,flexShrink:0,width:50}}>수량</span>
                              <button onClick={()=>setMktQtys(p=>({...p,[it.id]:Math.max(1,(+p[it.id]||1)-1)}))}
                                style={{width:26,height:26,borderRadius:6,border:`0.5px solid ${T.border}`,background:T.card,color:T.text,cursor:"pointer",fontSize:14}}>−</button>
                              <input type="number" value={mktQtys[it.id]??1} min="1"
                                onChange={e=>setMktQtys(p=>({...p,[it.id]:Math.max(1,+e.target.value||1)}))}
                                style={{width:52,textAlign:"center",border:`0.5px solid ${T.border}`,borderRadius:6,padding:"3px 4px",background:T.inp,color:T.text,fontSize:13,fontWeight:600}}/>
                              <button onClick={()=>setMktQtys(p=>({...p,[it.id]:(+p[it.id]||1)+1}))}
                                style={{width:26,height:26,borderRadius:6,border:`0.5px solid ${T.border}`,background:T.card,color:T.text,cursor:"pointer",fontSize:14}}>+</button>
                            </div>
                            <div style={{display:"flex",gap:6,alignItems:"center"}}>
                              <span style={{fontSize:11,color:T.sub,flexShrink:0,width:50}}>개당가격</span>
                              <input type="number" value={mktPrices[it.id]??it.estimatedPrice??""}
                                placeholder={it.estimatedPrice?String(it.estimatedPrice):"0"}
                                onChange={e=>setMktPrices(p=>({...p,[it.id]:e.target.value}))}
                                style={{...IS,flex:1}}/>
                            </div>
                            {price>0&&(
                              <div style={{marginTop:8,background:T.card,borderRadius:8,padding:"8px 10px",border:`0.5px solid ${T.border}`}}>
                                <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}>
                                  <span style={{color:T.sub}}>판매등록가</span>
                                  <span style={{color:T.text}}>{fmt(price*qty)}</span>
                                </div>
                                <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}>
                                  <span style={{color:T.sub}}>보증금 (등록 시 선지급)</span>
                                  <span style={{color:"#A32D2D"}}>−{fmt(MKT_DEPOSIT)}</span>
                                </div>
                                <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}>
                                  <span style={{color:T.sub}}>수수료 ({mktFee}%)</span>
                                  <span style={{color:"#A32D2D"}}>−{fmt(Math.floor(price*(mktFee/100))*qty)}</span>
                                </div>
                                <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:4}}>
                                  <span style={{color:T.sub}}>보증금 반환</span>
                                  <span style={{color:"#0F6E56"}}>+{fmt(MKT_DEPOSIT)}</span>
                                </div>
                                <div style={{borderTop:`0.5px solid ${T.border}`,paddingTop:6}}>
                                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}>
                                    <span style={{color:T.sub}}>판매 수익</span>
                                    <span style={{color:"#3B6D11",fontWeight:600}}>{fmt(calcNetPrice(price,qty,mktFee))}</span>
                                  </div>
                                  <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}>
                                    <span style={{color:"#534AB7",fontWeight:600}}>실수령액</span>
                                    <span style={{color:"#534AB7",fontWeight:700}}>{fmt(calcNetPrice(price,qty,mktFee)+MKT_DEPOSIT)}</span>
                                  </div>
                                </div>
                                <div style={{fontSize:9,color:T.sub,marginTop:6,lineHeight:1.6}}>
                                  ※ 보증금은 인게임에서 선지급되며 판매 성공 시 반환됩니다.
                                  판매 수익은 수수료 차감 후 순수익, 실수령액은 보증금 반환 포함 총 수령액입니다.
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* 직접 입력 모드 */}
          {mktMode==="direct"&&(
            <div style={{marginBottom:12}}>
              {mktItems.map((it,idx)=>(
                <div key={idx} style={{marginBottom:8,background:T.bg,borderRadius:8,padding:"10px 12px",border:`0.5px solid ${T.border}`}}>
                  <div style={{display:"flex",gap:6,marginBottom:6,alignItems:"center"}}>
                    <input type="text" value={it.name} placeholder="아이템명"
                      onChange={e=>setMktItems(p=>p.map((x,i)=>i===idx?{...x,name:e.target.value}:x))}
                      style={{...IS,flex:2}}/>
                    {mktItems.length>1&&(
                      <button onClick={()=>setMktItems(p=>p.filter((_,i)=>i!==idx))}
                        style={{background:"transparent",border:`0.5px solid ${T.border}`,color:"#A32D2D",borderRadius:6,padding:"5px 8px",fontSize:12,cursor:"pointer",flexShrink:0}}>✕</button>
                    )}
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:6}}>
                    <span style={{fontSize:11,color:T.sub,flexShrink:0,width:40}}>수량</span>
                    <button onClick={()=>setMktItems(p=>p.map((x,i)=>i===idx?{...x,qty:Math.max(1,(x.qty||1)-1)}:x))}
                      style={{width:26,height:26,borderRadius:6,border:`0.5px solid ${T.border}`,background:T.card,color:T.text,cursor:"pointer",fontSize:14}}>−</button>
                    <input type="number" value={it.qty||1} min="1"
                      onChange={e=>setMktItems(p=>p.map((x,i)=>i===idx?{...x,qty:Math.max(1,+e.target.value||1)}:x))}
                      style={{width:52,textAlign:"center",border:`0.5px solid ${T.border}`,borderRadius:6,padding:"3px 4px",background:T.inp,color:T.text,fontSize:13,fontWeight:600}}/>
                    <button onClick={()=>setMktItems(p=>p.map((x,i)=>i===idx?{...x,qty:(x.qty||1)+1}:x))}
                      style={{width:26,height:26,borderRadius:6,border:`0.5px solid ${T.border}`,background:T.card,color:T.text,cursor:"pointer",fontSize:14}}>+</button>
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:6}}>
                    <span style={{fontSize:11,color:T.sub,flexShrink:0,width:40}}>개당가</span>
                    <input type="number" value={it.price} placeholder="판매가"
                      onChange={e=>setMktItems(p=>p.map((x,i)=>i===idx?{...x,price:e.target.value}:x))}
                      style={{...IS,flex:1}}/>
                  </div>
                  {it.price&&+it.price>0&&(()=>{
                    const _qty=it.qty||1;
                    const _price=+it.price||0;
                    const _net=calcNetPrice(_price,_qty,mktFee);
                    const _fee=Math.floor(_price*(mktFee/100))*_qty;
                    return(
                      <div style={{background:T.card,borderRadius:8,padding:"8px 10px",border:`0.5px solid ${T.border}`}}>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}>
                          <span style={{color:T.sub}}>판매등록가</span>
                          <span style={{color:T.text}}>{fmt(_price*_qty)}</span>
                        </div>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}>
                          <span style={{color:T.sub}}>보증금 (등록 시 선지급)</span>
                          <span style={{color:"#A32D2D"}}>−{fmt(MKT_DEPOSIT)}</span>
                        </div>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}>
                          <span style={{color:T.sub}}>수수료 ({mktFee}%)</span>
                          <span style={{color:"#A32D2D"}}>−{fmt(_fee)}</span>
                        </div>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:4}}>
                          <span style={{color:T.sub}}>보증금 반환</span>
                          <span style={{color:"#0F6E56"}}>+{fmt(MKT_DEPOSIT)}</span>
                        </div>
                        <div style={{borderTop:`0.5px solid ${T.border}`,paddingTop:6}}>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}>
                            <span style={{color:T.sub}}>판매 수익</span>
                            <span style={{color:"#3B6D11",fontWeight:600}}>{fmt(_net)}</span>
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}>
                            <span style={{color:"#534AB7",fontWeight:600}}>실수령액</span>
                            <span style={{color:"#534AB7",fontWeight:700}}>{fmt(_net+MKT_DEPOSIT)}</span>
                          </div>
                        </div>
                        <div style={{fontSize:9,color:T.sub,marginTop:6,lineHeight:1.6}}>
                          ※ 보증금은 인게임에서 선지급되며 판매 성공 시 반환됩니다.
                          판매 수익은 수수료 차감 후 순수익, 실수령액은 보증금 반환 포함 총 수령액입니다.
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
              <button onClick={()=>setMktItems(p=>[...p,{name:"",price:"",qty:1}])}
                style={{background:"transparent",border:`0.5px dashed ${T.border}`,borderRadius:8,padding:"7px 0",fontSize:12,color:T.sub,cursor:"pointer",width:"100%"}}>
                + 아이템 추가
              </button>
            </div>
          )}

          {/* 합계 */}
          {mktSellTotal>0&&(
            <div style={{background:T.prevBg,borderRadius:8,padding:"10px 12px",marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                <span style={{color:T.sub}}>판매 수익 합계</span>
                <span style={{color:"#3B6D11",fontWeight:600}}>{fmt(mktSellTotal)}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:600}}>
                <span style={{color:"#534AB7"}}>💰 실수령액 합계 (보증금 반환 포함)</span>
                <span style={{color:"#534AB7"}}>{fmt(mktSellTotal+MKT_DEPOSIT)}</span>
              </div>
              <div style={{fontSize:10,color:T.sub,marginTop:4}}>
                ※ 보증금 {fmt(MKT_DEPOSIT)} 인게임 선지급 후 판매 시 반환
              </div>
            </div>
          )}

          <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:12}}>
            <label style={{fontSize:11,color:T.sub}}>메모 (선택)</label>
            <input type="text" value={mktNote} onChange={e=>setMktNote(e.target.value)}
              placeholder="예: 블랙빈 마스크 판매" style={IS}/>
          </div>

          <button onClick={handleSaveMarket} disabled={saving}
            style={{background:"#3B6D11",color:"#fff",border:"none",borderRadius:8,padding:12,fontSize:13,fontWeight:500,cursor:"pointer",width:"100%",opacity:saving?0.6:1}}>
            {saving?"저장 중...":`🏪 판매 기록 저장${mktSellTotal>0?` (+${fmt(mktSellTotal)})`:""}`}
          </button>
        </div>
      )}

      {/* ── 구매 탭 ── */}
      {mktTab==="buy"&&(
        <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16,marginBottom:10}}>
          <div style={{fontSize:13,fontWeight:500,color:"#A32D2D",marginBottom:4}}>💸 경매장 구매 기록</div>
          <div style={{fontSize:11,color:T.sub,marginBottom:14,lineHeight:1.7}}>
            경매장에서 구매한 내역을 지출로 기록합니다.
          </div>
          <div style={{marginBottom:12}}>
            <DateNavPicker value={mktDate} onChange={setMktDate} T={T} label="구매 날짜"/>
          </div>
          {/* 캐릭터 선택 */}
          <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:10}}>
            <label style={{fontSize:11,color:T.sub}}>캐릭터 (선택)</label>
            <select value={selChar?.nickname||""}
              onChange={e=>setSelChar(favorites.find(f=>f.nickname===e.target.value)||null)}
              style={IS}>
              <option value="">공통 (캐릭터 미지정)</option>
              {favorites.map(f=><option key={f.nickname} value={f.nickname}>{f.nickname}</option>)}
            </select>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12}}>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:11,color:T.sub}}>아이템명</label>
              <input type="text" value={mktBuyName} onChange={e=>setMktBuyName(e.target.value)}
                placeholder="예: 검은 큐브" style={IS}/>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:11,color:T.sub}}>수량</label>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <button onClick={()=>setMktBuyQty(q=>Math.max(1,q-1))}
                  style={{width:32,height:32,borderRadius:6,border:`0.5px solid ${T.border}`,background:T.card,color:T.text,cursor:"pointer",fontSize:16}}>−</button>
                <input type="number" value={mktBuyQty} min="1"
                  onChange={e=>setMktBuyQty(Math.max(1,+e.target.value||1))}
                  style={{width:60,textAlign:"center",border:`0.5px solid ${T.border}`,borderRadius:6,padding:"5px 4px",background:T.inp,color:T.text,fontSize:13,fontWeight:600}}/>
                <button onClick={()=>setMktBuyQty(q=>q+1)}
                  style={{width:32,height:32,borderRadius:6,border:`0.5px solid ${T.border}`,background:T.card,color:T.text,cursor:"pointer",fontSize:16}}>+</button>
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:11,color:T.sub}}>개당 구매 가격 (메소)</label>
              <input type="number" value={mktBuyPrice} onChange={e=>setMktBuyPrice(e.target.value)}
                placeholder="0" style={IS}/>
              {mktBuyPrice&&+mktBuyPrice>0&&(
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginTop:2}}>
                  <span style={{color:T.sub}}>총 메소 ({mktBuyQty}개)</span>
                  <span style={{color:"#A32D2D",fontWeight:600}}>−{fmt(+mktBuyPrice * mktBuyQty)}</span>
                </div>
              )}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:11,color:T.sub}}>메이플포인트 (타서버 구매 시, 선택)</label>
              <input type="number" value={mktBuyMp} onChange={e=>setMktBuyMp(e.target.value)}
                placeholder="0 MP" style={IS}/>
              {mktBuyMp&&+mktBuyMp>0&&mktBuyPrice&&+mktBuyPrice>0&&(
                <div style={{background:T.card,borderRadius:8,padding:"8px 10px",border:`0.5px solid ${T.border}`,marginTop:2}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}>
                    <span style={{color:T.sub}}>총 지출 메소</span>
                    <span style={{color:"#A32D2D",fontWeight:600}}>−{fmt(+mktBuyPrice * mktBuyQty)}</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}>
                    <span style={{color:T.sub}}>총 지출 메포</span>
                    <span style={{color:"#A32D2D",fontWeight:600}}>−{(+mktBuyMp).toLocaleString()} MP</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,borderTop:`0.5px solid ${T.border}`,paddingTop:4,marginTop:2}}>
                    <span style={{color:"#534AB7",fontWeight:600}}>1억당 메포</span>
                    <span style={{color:"#534AB7",fontWeight:700}}>
                      {(+mktBuyPrice*mktBuyQty)>0
                        ? Math.round(+mktBuyMp / ((+mktBuyPrice*mktBuyQty)/100000000)).toLocaleString()
                        : 0} MP/억
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:11,color:T.sub}}>메모 (선택)</label>
              <input type="text" value={mktBuyNote} onChange={e=>setMktBuyNote(e.target.value)}
                placeholder="예: 주간 물약 구매" style={IS}/>
            </div>
          </div>
          <button onClick={async()=>{
            if(!user){showToast("로그인 후 이용할 수 있습니다","🔐");return;}
            if(!mktBuyPrice||+mktBuyPrice<=0){showToast("구매 가격을 입력해주세요","⚠️");return;}
            setSaving(true);
            await saveExpense(user.uid,{
              date:mktDate, type:"market",
              nickname:selChar?.nickname||"", job:selChar?.job||"", world:selChar?.world||"",
              meso:+mktBuyPrice * mktBuyQty,
              note:mktBuyNote||(mktBuyName?`경매장 구매: ${mktBuyName}${mktBuyQty>1?` x${mktBuyQty}`:""}`:  "경매장 구매"),
              cash:0, mile:0, mp:+mktBuyMp||0,
              mktBuyMpPerHundredM: (+mktBuyMp>0&&+mktBuyPrice*mktBuyQty>0)
                ? Math.round(+mktBuyMp/((+mktBuyPrice*mktBuyQty)/100000000)) : 0,
            });
            setMktBuyName(""); setMktBuyPrice(""); setMktBuyQty(1); setMktBuyMp(""); setMktBuyNote("");
            showToast("경매장 구매 기록 완료!","💸");
            setSaving(false);
          }} disabled={saving}
            style={{background:"#A32D2D",color:"#fff",border:"none",borderRadius:8,padding:12,fontSize:13,fontWeight:500,cursor:"pointer",width:"100%",opacity:saving?0.6:1}}>
            {saving?"저장 중...":`💸 구매 기록 저장${mktBuyPrice&&+mktBuyPrice>0?` (−${fmt(+mktBuyPrice*mktBuyQty)})`:""}`}
          </button>
        </div>
      )}

      {/* 보관함 현황 */}
      {unsoldInventory.length>0&&(
        <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16}}>
          <div style={{fontSize:13,fontWeight:500,color:"#534AB7",marginBottom:10}}>📦 미판매 보관함 ({unsoldInventory.length}개)</div>
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            {unsoldInventory.map(it=>(
              <div key={it.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:T.bg,borderRadius:8,border:`0.5px solid ${T.border}`}}>
                <span style={{fontSize:12,flex:1,color:T.text}}>💎 {it.name}</span>
                <span style={{fontSize:11,color:T.sub}}>{it.obtainDate}</span>
                <span style={{fontSize:11,color:it.estimatedPrice?"#3B6D11":T.sub}}>{it.estimatedPrice?fmt(it.estimatedPrice):"가격 미입력"}</span>
                <button onClick={()=>updateInventoryItem(user?.uid,it.id,{sold:true,soldDate:today(),soldPrice:0})}
                  style={{background:"transparent",border:`0.5px solid ${T.border}`,color:T.sub,borderRadius:6,padding:"3px 7px",fontSize:10,cursor:"pointer"}}>판매완료</button>
                <button onClick={()=>{if(window.confirm(`"${it.name}" 삭제할까요?`))deleteInventoryItem(user?.uid,it.id);}}
                  style={{background:"transparent",border:"none",color:"#A32D2D",fontSize:12,cursor:"pointer"}}>🗑️</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const logAllNicks = [...new Set(records.map(r=>r.nickname||"공통").filter(Boolean))];
  const INC_TYPE_ICON  = {hunt:"🏃",boss:"⚔️",event:"🎉",drop:"💎",epic:"🔮",exchange:"💱","market-sell":"🏪"};
  const EXP_TYPE_ICON  = {cube:"🎲",star:"⭐",spell:"📜",symbol:"📈","boss-entry":"🎫",cash:"💳",market:"🏪",etc:"📦",exchange:"💱"};
  const INC_TYPE_LABEL = {hunt:"사냥",boss:"보스",event:"이벤트",drop:"물욕템",epic:"에픽던전",exchange:"환전","market-sell":"경매장판매"};
  const EXP_TYPE_COLOR = {cube:"#7C3AED",star:"#D97706",spell:"#059669","boss-entry":"#DC2626",symbol:"#2563EB",cash:"#DB2777",market:"#0891B2",etc:"#6B7280",exchange:"#534AB7"};

  // 카드 뷰 렌더 (아코디언 펼치기)
  const renderCardView = () => (
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      {groupedLogs.slice(0,80).map(r=>{
        // ── 심볼업 그룹 카드 ──
        if (r._isSymbolGroup) {
          const forceLabel = r.symbolForce==="arcane"?"🌊 아케인포스":r.symbolForce==="grand"?"🔮 그랜드 어센틱":"⚡ 어센틱포스";
          const isOpen = expandedLogId===r.id;
          return (
            <div key={r.id} style={{background:T.card,borderRadius:12,border:`0.5px solid ${isOpen?"#2563EB":T.border}`,overflow:"hidden",boxShadow:dark?"none":"0 1px 3px rgba(0,0,0,0.05)"}}>
              {/* 헤더 */}
              <div style={{display:"flex",alignItems:"center",cursor:"pointer"}} onClick={()=>setExpandedLogId(isOpen?null:r.id)}>
                <div style={{width:4,background:"#A32D2D",alignSelf:"stretch",flexShrink:0,borderRadius:"12px 0 0 0"}}/>
                <div style={{flex:1,padding:"11px 12px",display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                  <span style={{fontSize:11,color:T.sub,flexShrink:0,minWidth:78}}>{r.date}</span>
                  <span style={{fontSize:13,fontWeight:500,color:T.text,flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.nickname||"공통"}</span>
                  <span style={{padding:"2px 8px",borderRadius:20,fontSize:10,fontWeight:600,background:"#FCEBEB",color:"#A32D2D",flexShrink:0}}>📈 심볼업</span>
                  <span style={{fontSize:11,color:"#2563EB",fontWeight:600,flexShrink:0}}>{forceLabel}</span>
                  <span style={{fontSize:14,fontWeight:700,color:"#A32D2D",flexShrink:0,whiteSpace:"nowrap"}}>-{fmt(r.meso)}</span>
                </div>
                <div style={{padding:"0 12px",color:T.sub,fontSize:12}}>{isOpen?"▲":"▼"}</div>
              </div>
              {/* 펼침: 지역별 상세 */}
              {isOpen&&(
                <div style={{padding:"0 12px 12px 16px",borderTop:`0.5px solid ${T.border}`}}>
                  <div style={{paddingTop:10,display:"flex",flexDirection:"column",gap:5}}>
                    {r._items.map(item=>(
                      <div key={item.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                        padding:"6px 10px",borderRadius:8,background:dark?"#1a1a2e":"#f0f4ff",
                        border:`0.5px solid ${dark?"#2563EB33":"#2563EB22"}`}}>
                        <span style={{fontSize:12,color:"#2563EB",fontWeight:600,flex:1}}>
                          {r.symbolForce==="arcane"?"🌊":"⚡"} {item.symbolArea}
                          {item.symbolCur&&item.symbolTgt&&<span style={{fontSize:10,color:T.sub,fontWeight:400,marginLeft:6}}>Lv.{item.symbolCur}→{item.symbolTgt}</span>}
                        </span>
                        <span style={{fontSize:13,fontWeight:700,color:"#A32D2D",marginRight:10}}>-{fmt(item.meso||0)}</span>
                        <button onClick={e=>{e.stopPropagation();openDetail(item,true);}}
                          style={{fontSize:11,padding:"3px 8px",borderRadius:5,border:"1px solid #534AB7",
                            background:"transparent",color:"#534AB7",cursor:"pointer",marginRight:4}}>✏️ 수정</button>
                        <button onClick={e=>{e.stopPropagation();handleDeleteFromList(e,item.id);}}
                          style={{fontSize:11,padding:"3px 8px",borderRadius:5,border:"1px solid #A32D2D",
                            background:"transparent",color:"#A32D2D",cursor:"pointer"}}>🗑️</button>
                      </div>
                    ))}
                    <div style={{display:"flex",justifyContent:"space-between",padding:"6px 10px",borderTop:`0.5px solid ${T.border}`,marginTop:2}}>
                      <span style={{fontSize:12,fontWeight:600,color:T.sub}}>지출 합계</span>
                      <span style={{fontSize:14,fontWeight:700,color:"#A32D2D"}}>-{fmt(r.meso)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        }

        const isInc   = r.kind==="income";
        const amount  = isInc?calcIncome(r,favorites):(r.meso||0);
        const typeIcon= isInc?(INC_TYPE_ICON[r.type]||"💰"):(EXP_TYPE_ICON[r.type]||"💸");
        const typeLabel=isInc?(INC_TYPE_LABEL[r.type]||r.type):(EXP_TYPE_MAP[r.type]||r.type)||"";
        const accent  = isInc?"#3B6D11":"#A32D2D";
        const isOpen  = expandedLogId===r.id;
        const toggle  = ()=>setExpandedLogId(isOpen?null:r.id);

        // ── 수익 세부 데이터 ──
        const solRows=[
          ["솔에르다 기운",r.sol0,"개"],["솔조각 (이벤트)",r.sol1,"개"],
          ["솔조각 (일반)",r.sol2,"개"],["솔조각 (사냥)",r.sol3,"개"],
          ["솔조각 (교환불가)",r.sol4,"개"],
        ].filter(([,v])=>v>0);
        const mesoRows=[
          ["메소 획득량",r.meso,"meso"],["코어 잼스톤",r.core,"개"],
          ["기타 수익",r.etc,"meso"],["마일리지",r.mile,""],
          ["사냥 시간",r.mins,"분"],
        ].filter(([,v])=>v>0);

        // ── 지출 세부 데이터 ──
        const expRows=[
          ["지출 메소",r.meso,"meso"],["넥슨 캐시",r.cash,"원"],["마일리지",r.mile,""],
        ].filter(([,v])=>v>0);
        const symbolLabel = r.type==="symbol"
          ? (r.symbolForce==="arcane"?"🌊 아케인포스":r.symbolForce==="authentic"?"⚡ 어센틱포스":r.symbolForce==="grand"?"🔮 그랜드 어센틱":"") + (r.symbolArea?` · ${r.symbolArea}`:"")
          : "";

        return (
          <div key={r.id} style={{background:T.card,borderRadius:12,border:`0.5px solid ${isOpen?accent:T.border}`,overflow:"hidden",boxShadow:dark?"none":"0 1px 3px rgba(0,0,0,0.05)",transition:"border-color 0.15s"}}>
            {/* ── 헤더 행 (항상 표시) ── */}
            <div style={{display:"flex",alignItems:"center",cursor:"pointer"}} onClick={toggle}>
              {/* 왼쪽 컬러바 */}
              <div style={{width:4,background:accent,alignSelf:"stretch",flexShrink:0,borderRadius:"12px 0 0 0"}}/>
              <div style={{flex:1,padding:"11px 12px",display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                {/* 날짜 */}
                <span style={{fontSize:11,color:T.sub,flexShrink:0,fontVariantNumeric:"tabular-nums",minWidth:78}}>{r.date}</span>
                {/* 캐릭터 */}
                <span style={{fontSize:13,fontWeight:500,color:T.text,flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.nickname||"공통"}</span>
                {/* 유형 뱃지 */}
                <span style={{padding:"2px 8px",borderRadius:20,fontSize:10,fontWeight:600,background:isInc?"#EAF3DE":"#FCEBEB",color:accent,flexShrink:0,whiteSpace:"nowrap"}}>{typeIcon} {typeLabel}</span>
                {/* 보스 주간/월간 카운터 (보스 기록 카드에만) */}
                {isInc&&r.type==="boss"&&(()=>{
                  const bn=(name)=>name.split(" (")[0].trim();
                  const bRecs=records.filter(x=>x.kind==="income"&&x.type==="boss"&&x.nickname===r.nickname&&Array.isArray(x.bossList));
                  // 해당 기록 날짜 기준 주 시작일
                  const _d=new Date((r.date||today())+"T00:00:00");
                  const _day=_d.getDay(); const _diff=_day>=4?_day-4:_day+3;
                  _d.setDate(_d.getDate()-_diff); _d.setHours(0,0,0,0);
                  const _wStart=localDateStr(_d);
                  const _mStart=(r.date||today()).slice(0,7)+"-01";
                  const wkNames=new Set(bRecs.filter(x=>x.date>=_wStart).flatMap(x=>x.bossList?.filter(b=>BOSSES.weekly.some(w=>w.name===b.name))||[]).map(b=>bn(b.name)));
                  const mo=bRecs.filter(x=>x.date>=_mStart).reduce((s,x)=>s+(x.bossList?.filter(b=>BOSSES.monthly.some(m=>m.name===b.name)).length||0),0);
                  const wk=wkNames.size;
                  return(
                    <div style={{display:"flex",gap:4,flexShrink:0}}>
                      {isPatchTransitionDate(r.date)&&phaseBadgeInfo(r.pricePhase)&&(()=>{
                        const pb = phaseBadgeInfo(r.pricePhase);
                        return (
                          <span style={{padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:700,whiteSpace:"nowrap",
                            background:pb.bg,color:pb.fg}}>
                            {pb.short}
                          </span>
                        );
                      })()}
                      <span style={{padding:"1px 6px",borderRadius:20,fontSize:9,fontWeight:700,background:wk>=12?"#FCEBEB":"#EEEDFE",color:wk>=12?"#A32D2D":"#534AB7",border:`0.5px solid ${wk>=12?"#A32D2D":"#534AB7"}`,whiteSpace:"nowrap"}}>주{wk}/12</span>
                      <span style={{padding:"1px 6px",borderRadius:20,fontSize:9,fontWeight:700,background:mo>=1?"#FCEBEB":"#FFF5F5",color:"#A32D2D",border:`0.5px solid ${mo>=1?"#A32D2D":"#A32D2D44"}`,whiteSpace:"nowrap"}}>월{mo}/1</span>
                    </div>
                  );
                })()}
                {/* 금액 */}
                <span style={{fontSize:14,fontWeight:700,color:accent,flexShrink:0,fontVariantNumeric:"tabular-nums",whiteSpace:"nowrap"}}>{isInc?"+":"-"}{fmt(amount)}</span>
              </div>
              {/* 오른쪽: 수정·삭제·펼치기 */}
              <div style={{display:"flex",alignItems:"center",gap:4,padding:"0 10px",flexShrink:0}} onClick={e=>e.stopPropagation()}>
                <button onClick={()=>openDetail(r,true)}
                  style={{background:"#534AB7",color:"#fff",border:"none",borderRadius:6,padding:"5px 9px",fontSize:11,cursor:"pointer",fontWeight:500,whiteSpace:"nowrap"}}>✏️</button>
                <button onClick={(e)=>handleDeleteFromList(e,r.id)}
                  style={{background:"transparent",border:`0.5px solid ${T.border}`,color:"#A32D2D",borderRadius:6,padding:"5px 7px",fontSize:11,cursor:"pointer"}}>🗑️</button>
                {/* 펼치기 화살표 */}
                <div onClick={toggle} style={{color:T.sub,fontSize:13,cursor:"pointer",padding:"2px 2px",transform:isOpen?"rotate(90deg)":"rotate(0deg)",transition:"transform 0.2s",userSelect:"none"}}>▶</div>
              </div>
            </div>

            {/* ── 닫힌 상태 미리보기 ── */}
            {!isOpen&&(
              <div style={{padding:"0 12px 8px 16px",display:"flex",flexDirection:"column",gap:3}}>
                {/* 물욕템 / 경매장 아이템명 미리보기 */}
                {(r.type==="drop"||r.type==="market-sell")&&r.dropList?.length>0&&(
                  <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                    {r.dropList.map((it,i)=>(
                      <span key={i} style={{fontSize:11,padding:"1px 7px",borderRadius:20,background:dark?"#1E1E1E":"#F5F5F5",border:`0.5px solid ${T.border}`,color:T.text}}>
                        💎 {it.name}
                        {r.type==="market-sell"&&it.instant&&it.estPrice>0&&
                          <span style={{color:"#3B6D11",marginLeft:4}}>+{fmt(it.diff||0)}</span>}
                        {r.type==="drop"&&it.price>0&&
                          <span style={{color:it.instant?"#534AB7":T.sub,marginLeft:4}}>{it.instant?fmt(it.price):"보관함"}</span>}
                      </span>
                    ))}
                  </div>
                )}
                {/* 메모 미리보기 */}
                {r.note&&(
                  <div style={{display:"flex",alignItems:"center",gap:4}}>
                    <span style={{fontSize:11}}>💬</span>
                    <span style={{fontSize:11,color:dark?"#A89FE0":"#6B5DC8",fontStyle:"italic",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.note}</span>
                  </div>
                )}
              </div>
            )}

            {/* ── 펼쳐진 세부사항 ── */}
            {isOpen&&(
              <div style={{borderTop:`0.5px solid ${T.border}`,background:dark?"#141414":"#FAFAFA",padding:"12px 16px"}}>

                {/* 수익 상세 */}
                {isInc&&(
                  <div>
                    {/* 보스 목록 */}
                    {r.bossList?.length>0&&(
                      <div style={{marginBottom:10}}>
                        <div style={{fontSize:11,fontWeight:600,color:"#534AB7",marginBottom:5}}>⚔️ 보스 결정석</div>
                        <div style={{display:"flex",flexDirection:"column",gap:3}}>
                          {r.bossList.map((b,i)=>(
                            <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"4px 8px",background:dark?"#1E1E1E":"#fff",borderRadius:6,border:`0.5px solid ${T.border}`}}>
                              <span style={{color:T.text}}>{b.name}{b.party>1?<span style={{fontSize:10,color:T.sub}}> ({b.party}인)</span>:""}</span>
                              <span style={{color:"#854F0B",fontWeight:600}}>{fmt(b.crystal)}</span>
                            </div>
                          ))}
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,fontWeight:600,padding:"5px 8px",background:dark?"#1A1A0A":"#FFFBE6",borderRadius:6}}>
                            <span style={{color:"#854F0B"}}>결정석 합계</span>
                            <span style={{color:"#854F0B"}}>{fmt(r.bossIncome||0)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 물욕템 / 경매장 아이템 목록 */}
                    {(r.type==="drop"||r.type==="market-sell")&&r.dropList?.length>0&&(
                      <div style={{marginBottom:10}}>
                        <div style={{fontSize:11,fontWeight:600,color:"#854F0B",marginBottom:8}}>
                          {r.type==="drop"?"💎 물욕템 목록":"🏪 판매 아이템 목록"}
                        </div>
                        {/* 테이블 헤더 */}
                        <div style={{display:"grid",gridTemplateColumns:r.type==="market-sell"?"2fr 1fr 1fr 1fr":"2fr 1fr 1fr",gap:4,padding:"4px 8px",marginBottom:3}}>
                          <span style={{fontSize:10,fontWeight:600,color:T.sub}}>아이템명</span>
                          <span style={{fontSize:10,fontWeight:600,color:T.sub,textAlign:"right"}}>예상 기록가</span>
                          {r.type==="market-sell"&&<span style={{fontSize:10,fontWeight:600,color:T.sub,textAlign:"right"}}>실거래가</span>}
                          <span style={{fontSize:10,fontWeight:600,color:T.sub,textAlign:"right"}}>반영 수익</span>
                        </div>
                        {/* 아이템 행 */}
                        {r.dropList.map((it,i)=>(
                          <div key={i} style={{display:"grid",gridTemplateColumns:r.type==="market-sell"?"2fr 1fr 1fr 1fr":"2fr 1fr 1fr",gap:4,padding:"5px 8px",background:dark?"#1E1E1E":"#fff",borderRadius:6,border:`0.5px solid ${T.border}`,marginBottom:3,alignItems:"center"}}>
                            <div style={{display:"flex",alignItems:"center",gap:5,minWidth:0}}>
                              <span style={{fontSize:12,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>💎 {it.name}</span>
                              {r.type==="drop"&&(
                                <span style={{fontSize:9,padding:"1px 5px",borderRadius:10,flexShrink:0,
                                  background:it.instant?"#534AB722":"#F5F5F5",
                                  color:it.instant?"#534AB7":T.sub}}>
                                  {it.instant?"즉시반영":"보관함"}
                                </span>
                              )}
                            </div>
                            <span style={{fontSize:11,color:T.sub,textAlign:"right"}}>
                              {it.price||it.estimatedPrice||it.estPrice
                                ? fmt(it.estimatedPrice||it.estPrice||it.price||0)
                                : "—"}
                            </span>
                            {r.type==="market-sell"&&(
                              <span style={{fontSize:11,color:"#3B6D11",fontWeight:600,textAlign:"right"}}>
                                {fmt(it.actualPrice||it.price||0)}
                              </span>
                            )}
                            <span style={{fontSize:11,fontWeight:600,textAlign:"right",
                              color:r.type==="market-sell"?"#3B6D11":it.instant?"#534AB7":T.sub}}>
                              {r.type==="market-sell"
                                ? (it.instant&&it.estPrice>0 ? `+${fmt(it.diff||0)}` : `+${fmt(it.actualPrice||it.price||0)}`)
                                : (it.instant ? `+${fmt(it.price||0)}` : "미반영")}
                            </span>
                          </div>
                        ))}
                        {/* 합계 행 */}
                        {r.type==="market-sell"&&(
                          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:4,padding:"5px 8px",background:dark?"#1A1A0A":"#FFFBE6",borderRadius:6,marginTop:2}}>
                            <span style={{fontSize:11,fontWeight:600,color:"#854F0B"}}>합계</span>
                            <span style={{fontSize:11,color:T.sub,textAlign:"right"}}>{fmt(r.dropList.reduce((s,it)=>s+(it.estimatedPrice||it.estPrice||0),0))}</span>
                            <span style={{fontSize:11,color:"#3B6D11",fontWeight:600,textAlign:"right"}}>{fmt(r.dropList.reduce((s,it)=>s+(it.actualPrice||it.price||0),0))}</span>
                            <span style={{fontSize:11,color:"#3B6D11",fontWeight:700,textAlign:"right"}}>+{fmt(r.totalIncome||0)}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 솔에르다 */}
                    {solRows.length>0&&(
                      <div style={{marginBottom:10}}>
                        <div style={{fontSize:11,fontWeight:600,color:"#0F6E56",marginBottom:5}}>✨ 솔에르다</div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
                          {solRows.map(([l,v,u])=>(
                            <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"4px 8px",background:dark?"#1E1E1E":"#fff",borderRadius:6,border:`0.5px solid ${T.border}`,fontSize:12}}>
                              <span style={{color:T.sub}}>{l}</span>
                              <span style={{color:"#0F6E56",fontWeight:600}}>{v.toLocaleString()}{u}</span>
                            </div>
                          ))}
                        </div>
                        {((r.sol1||0)+(r.sol2||0)+(r.sol3||0))>0&&(
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,fontWeight:600,padding:"5px 8px",marginTop:4,background:dark?"#0A1A0F":"#EDFAF4",borderRadius:6}}>
                            <span style={{color:"#0F6E56"}}>솔에르다 환산가</span>
                            <span style={{color:"#0F6E56"}}>{fmt(r.totalSol||0)}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 메소·기타 */}
                    {mesoRows.length>0&&(
                      <div style={{marginBottom:10}}>
                        <div style={{fontSize:11,fontWeight:600,color:"#3B6D11",marginBottom:5}}>💰 메소 & 재화</div>
                        <div style={{display:"flex",flexDirection:"column",gap:3}}>
                          {mesoRows.map(([l,v,u])=>(
                            <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"4px 8px",background:dark?"#1E1E1E":"#fff",borderRadius:6,border:`0.5px solid ${T.border}`,fontSize:12}}>
                              <span style={{color:T.sub}}>{l}</span>
                              <span style={{color:"#3B6D11",fontWeight:600}}>{u==="meso"?fmt(v):v.toLocaleString()+(u?u:"")}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 총 수익 합계 */}
                    <div style={{display:"flex",justifyContent:"space-between",padding:"8px 10px",background:dark?"#1E1A2E":"#F0EEFF",borderRadius:8,border:`0.5px solid #534AB744`,fontSize:13,fontWeight:700}}>
                      <span style={{color:"#534AB7"}}>💰 총 수익</span>
                      <span style={{color:"#534AB7"}}>{fmt(calcIncome(r,favorites))}</span>
                    </div>
                  </div>
                )}

                {/* 지출 상세 */}
                {!isInc&&(
                  <div>
                    {/* 심볼업 지역 */}
                    {symbolLabel&&(
                      <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 10px",background:dark?"#1A1A2E":"#EEF2FF",borderRadius:8,marginBottom:8,fontSize:12}}>
                        <span style={{fontWeight:600,color:"#2563EB"}}>{symbolLabel}</span>
                      </div>
                    )}
                    {/* 지출 항목들 */}
                    {expRows.length>0&&(
                      <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:8}}>
                        {expRows.map(([l,v,u])=>(
                          <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"4px 8px",background:dark?"#1E1E1E":"#fff",borderRadius:6,border:`0.5px solid ${T.border}`,fontSize:12}}>
                            <span style={{color:T.sub}}>{l}</span>
                            <span style={{color:"#A32D2D",fontWeight:600}}>{u==="meso"?fmt(v):(u==="원"?v.toLocaleString()+"원":v.toLocaleString())}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* 지출 합계 */}
                    <div style={{display:"flex",justifyContent:"space-between",padding:"8px 10px",background:dark?"#1E0F0F":"#FEF2F2",borderRadius:8,border:`0.5px solid #A32D2D44`,fontSize:13,fontWeight:700}}>
                      <span style={{color:"#A32D2D"}}>💸 지출 합계</span>
                      <span style={{color:"#A32D2D"}}>-{fmt(r.meso||0)}</span>
                    </div>
                  </div>
                )}

                {/* 메모 */}
                {r.note&&(
                  <div style={{display:"flex",alignItems:"flex-start",gap:6,padding:"8px 10px",background:dark?"#1A1A2E":"#F5F3FF",borderRadius:8,border:`0.5px solid ${dark?"#2A2A4A":"#D8D4F5"}`,marginTop:10}}>
                    <span style={{fontSize:13,flexShrink:0}}>💬</span>
                    <span style={{fontSize:12,color:dark?"#B0A6E8":"#534AB7",lineHeight:1.5,wordBreak:"break-all"}}>{r.note}</span>
                  </div>
                )}

                {/* 수정 버튼 (세부사항 하단) */}
                <div style={{display:"flex",gap:6,marginTop:12}}>
                  <button onClick={()=>openDetail(r,true)}
                    style={{flex:1,background:"#534AB7",color:"#fff",border:"none",borderRadius:8,padding:"9px 0",fontSize:12,cursor:"pointer",fontWeight:500}}>✏️ 수정하기</button>
                  <button onClick={(e)=>handleDeleteFromList(e,r.id)}
                    style={{background:"transparent",border:"1px solid #A32D2D",color:"#A32D2D",borderRadius:8,padding:"9px 14px",fontSize:12,cursor:"pointer"}}>🗑️ 삭제</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {groupedLogs.length>80&&<div style={{textAlign:"center",padding:"12px 0",color:T.sub,fontSize:11}}>최근 80건 표시 중 · 총 {groupedLogs.length}건</div>}
    </div>
  );

  // 테이블 뷰 렌더 (PC/모바일 공용, 가로 스크롤)
  const renderTableView = () => {
    const showInc = logTab!=="expense" && logTab!=="hunt" && logTab!=="market" && logTab!=="etc" ? true
      : logTab==="income" ? true : logTab==="all" ? true
      : ["hunt","market","etc","boss"].includes(logTab) ? true : false;
    const showExp = logTab==="expense"||logTab==="all"||logTab==="etc";
    return (
      <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,overflow:"hidden"}}>
        <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:isMobile?700:900}}>
            <thead>
              <tr style={{background:dark?"#222":"#F0EEFF",borderBottom:`1px solid ${T.border}`}}>
                <th style={{padding:"9px 10px",textAlign:"left",color:"#534AB7",fontWeight:600,whiteSpace:"nowrap",width:90}}>날짜</th>
                <th style={{padding:"9px 8px",textAlign:"left",color:"#534AB7",fontWeight:600,whiteSpace:"nowrap",width:80}}>캐릭터</th>
                <th style={{padding:"9px 8px",textAlign:"center",color:"#534AB7",fontWeight:600,whiteSpace:"nowrap",width:56}}>구분</th>
                <th style={{padding:"9px 8px",textAlign:"center",color:"#534AB7",fontWeight:600,whiteSpace:"nowrap",width:80}}>유형</th>
                {/* 수익 전용 컬럼 */}
                {showInc&&<>
                  <th style={{padding:"9px 8px",textAlign:"right",color:"#3B6D11",fontWeight:600,whiteSpace:"nowrap",width:60}}>솔기운</th>
                  <th style={{padding:"9px 8px",textAlign:"right",color:"#3B6D11",fontWeight:600,whiteSpace:"nowrap",width:55}}>이벤트</th>
                  <th style={{padding:"9px 8px",textAlign:"right",color:"#3B6D11",fontWeight:600,whiteSpace:"nowrap",width:55}}>일반</th>
                  <th style={{padding:"9px 8px",textAlign:"right",color:"#3B6D11",fontWeight:600,whiteSpace:"nowrap",width:55}}>사냥솔</th>
                  <th style={{padding:"9px 8px",textAlign:"right",color:"#3B6D11",fontWeight:600,whiteSpace:"nowrap",width:55}}>교불가</th>
                  <th style={{padding:"9px 8px",textAlign:"right",color:"#3B6D11",fontWeight:600,whiteSpace:"nowrap",width:55}}>코어잼</th>
                  <th style={{padding:"9px 8px",textAlign:"right",color:"#3B6D11",fontWeight:600,whiteSpace:"nowrap",width:55}}>사냥(분)</th>
                </>}
                {/* 지출 전용 컬럼 */}
                {showExp&&<>
                  <th style={{padding:"9px 8px",textAlign:"left",color:"#A32D2D",fontWeight:600,whiteSpace:"nowrap",width:100}}>지출 상세</th>
                </>}
                <th style={{padding:"9px 8px",textAlign:"right",color:T.sub,fontWeight:600,whiteSpace:"nowrap",width:100}}>금액</th>
                <th style={{padding:"9px 8px",textAlign:"left",color:T.sub,fontWeight:600,whiteSpace:"nowrap",minWidth:100}}>메모</th>
                <th style={{padding:"9px 8px",textAlign:"center",color:T.sub,fontWeight:600,whiteSpace:"nowrap",width:70}}>편집</th>
              </tr>
            </thead>
            <tbody>
              {groupedLogs.slice(0,80).map((r,i)=>{
                // 심볼업 그룹은 테이블에서도 단일 행으로
                if (r._isSymbolGroup) {
                  const forceLabel = r.symbolForce==="arcane"?"🌊 아케인":r.symbolForce==="grand"?"🔮 그랜드":"⚡ 어센틱";
                  const areas = r._items.map(x=>x.symbolArea).join(", ");
                  return (
                    <tr key={r.id} style={{background:i%2===0?T.card:(dark?"#181818":"#FAFAFA")}}>
                      <td style={{padding:"8px",fontSize:11,color:T.sub,whiteSpace:"nowrap"}}>{r.date}</td>
                      <td style={{padding:"8px",fontSize:12,color:T.text}}>{r.nickname||"공통"}</td>
                      <td style={{padding:"8px"}}><span style={{padding:"2px 7px",borderRadius:20,fontSize:10,fontWeight:600,background:"#FCEBEB",color:"#A32D2D"}}>📈 심볼업</span></td>
                      <td style={{padding:"8px",fontSize:11,color:"#2563EB"}}>{forceLabel} · {areas}</td>
                      <td style={{padding:"8px",textAlign:"right",fontSize:13,fontWeight:700,color:"#A32D2D",whiteSpace:"nowrap"}}>-{fmt(r.meso)}</td>
                      <td style={{padding:"8px",fontSize:11,color:T.sub}}>{r.note||""}</td>
                      <td style={{padding:"8px",textAlign:"center"}}><span style={{fontSize:10,color:T.sub}}>—</span></td>
                    </tr>
                  );
                }
                const isInc=r.kind==="income";
                const amount=isInc?calcIncome(r,favorites):(r.meso||0);
                const accentColor=isInc?"#3B6D11":"#A32D2D";
                const rowBg=i%2===0?T.card:(dark?"#181818":"#FAFAFA");
                const typeIcon=isInc?(INC_TYPE_ICON[r.type]||"💰"):(EXP_TYPE_ICON[r.type]||"💸");
                const typeLabel=isInc?(INC_TYPE_LABEL[r.type]||r.type):(EXP_TYPE_MAP[r.type]||r.type)||"";

                // 지출 상세 문자열
                let expDetailCell="-";
                if(!isInc){
                  if(r.type==="symbol"){
                    const fl=r.symbolForce==="arcane"?"🌊아케인":r.symbolForce==="authentic"?"⚡어센틱":r.symbolForce==="grand"?"🔮그랜드":"";
                    expDetailCell=(fl?fl+" ":"")+( r.symbolArea||"");
                  } else {
                    const ps=[];
                    if(r.meso>0) ps.push(fmt(r.meso));
                    if(r.cash>0) ps.push(r.cash.toLocaleString()+"원");
                    expDetailCell=ps.join(" / ")||"-";
                  }
                }

                // 보스 상세 (툴팁 대신 hover title)
                const bossTitle=isInc&&r.bossList?.length>0?r.bossList.map(b=>`${b.name}${b.party>1?` (${b.party}인)`:""}: ${fmt(b.crystal)}`).join("\n"):"";

                return(
                  <tr key={r.id} style={{background:rowBg,borderBottom:`0.5px solid ${T.border}`}}>
                    <td style={{padding:"8px 10px",color:T.sub,whiteSpace:"nowrap",fontVariantNumeric:"tabular-nums"}}>{r.date}</td>
                    <td style={{padding:"8px 8px",fontWeight:500,color:T.text,maxWidth:80,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={r.nickname||"공통"}>{r.nickname||"공통"}</td>
                    <td style={{padding:"8px 8px",textAlign:"center"}}>
                      <span style={{padding:"2px 7px",borderRadius:20,fontSize:10,fontWeight:600,background:isInc?"#EAF3DE":"#FCEBEB",color:accentColor,whiteSpace:"nowrap",display:"inline-block"}}>{isInc?"수익":"지출"}</span>
                    </td>
                    <td style={{padding:"8px 8px",textAlign:"center",whiteSpace:"nowrap"}} title={bossTitle||undefined}>
                      <span style={{fontSize:12,whiteSpace:"nowrap"}}>{typeIcon} <span style={{fontSize:10,color:isInc?"#3B6D11":EXP_TYPE_COLOR[r.type]||T.sub,fontWeight:500}}>{typeLabel}</span></span>
                    </td>
                    {/* 수익 전용 셀 */}
                    {showInc&&<>
                      <td style={{padding:"8px 8px",textAlign:"right",color:r.sol0>0?"#0F6E56":T.sub,fontVariantNumeric:"tabular-nums"}}>{r.sol0>0?r.sol0:""}</td>
                      <td style={{padding:"8px 8px",textAlign:"right",color:r.sol1>0?"#0F6E56":T.sub,fontVariantNumeric:"tabular-nums"}}>{r.sol1>0?r.sol1:""}</td>
                      <td style={{padding:"8px 8px",textAlign:"right",color:r.sol2>0?"#0F6E56":T.sub,fontVariantNumeric:"tabular-nums"}}>{r.sol2>0?r.sol2:""}</td>
                      <td style={{padding:"8px 8px",textAlign:"right",color:r.sol3>0?"#0F6E56":T.sub,fontVariantNumeric:"tabular-nums"}}>{r.sol3>0?r.sol3:""}</td>
                      <td style={{padding:"8px 8px",textAlign:"right",color:r.sol4>0?"#888":T.sub,fontVariantNumeric:"tabular-nums"}}>{r.sol4>0?r.sol4:""}</td>
                      <td style={{padding:"8px 8px",textAlign:"right",color:r.core>0?"#7C3AED":T.sub,fontVariantNumeric:"tabular-nums"}}>{r.core>0?r.core:""}</td>
                      <td style={{padding:"8px 8px",textAlign:"right",color:r.mins>0?"#2563EB":T.sub,fontVariantNumeric:"tabular-nums"}}>{r.mins>0?r.mins:""}</td>
                    </>}
                    {/* 지출 전용 셀 */}
                    {showExp&&<td style={{padding:"8px 8px",color:isInc?T.sub:EXP_TYPE_COLOR[r.type]||T.sub,fontSize:11,whiteSpace:"nowrap"}}>{isInc?"-":expDetailCell}</td>}
                    {/* 금액 */}
                    <td style={{padding:"8px 8px",textAlign:"right",fontWeight:600,color:accentColor,whiteSpace:"nowrap",fontVariantNumeric:"tabular-nums"}}>{isInc?"+":"-"}{fmt(amount)}</td>
                    {/* 메모 */}
                    <td style={{padding:"8px 8px",maxWidth:140}}>
                      {r.note?(
                        <span style={{display:"inline-flex",alignItems:"center",gap:3,background:dark?"#1A1A2E":"#F5F3FF",borderRadius:5,padding:"2px 6px",fontSize:11,color:dark?"#A89FE0":"#534AB7"}}>
                          💬 {r.note}
                        </span>
                      ):""}
                    </td>
                    {/* 편집 버튼 */}
                    <td style={{padding:"6px 8px",textAlign:"center",whiteSpace:"nowrap"}}>
                      <button onClick={()=>openDetail(r,true)} style={{background:"#534AB7",color:"#fff",border:"none",borderRadius:5,padding:"3px 8px",fontSize:10,cursor:"pointer",marginRight:3}}>✏️</button>
                      <button onClick={(e)=>handleDeleteFromList(e,r.id)} style={{background:"transparent",border:`0.5px solid ${T.border}`,color:"#A32D2D",borderRadius:5,padding:"3px 6px",fontSize:10,cursor:"pointer"}}>🗑️</button>
                    </td>
                  </tr>
                );
              })}
              {/* 합계 행 */}
              {filteredLogs.length>0&&(()=>{
                const sumInc=filteredLogs.filter(r=>r.kind==="income").reduce((s,r)=>s+calcIncome(r,favorites),0);
                const sumExp=filteredLogs.filter(r=>r.kind==="expense").reduce((s,r)=>s+(r.meso||0),0);
                const net=sumInc-sumExp;
                return(
                  <tr style={{background:dark?"#1E1A2E":"#F0EEFF",borderTop:`1.5px solid #534AB7`}}>
                    <td colSpan={4} style={{padding:"8px 10px",fontWeight:600,fontSize:12,color:"#534AB7"}}>합계 ({filteredLogs.length}건)</td>
                    {showInc&&<td colSpan={7}/>}
                    {showExp&&<td/>}
                    <td style={{padding:"8px 8px",textAlign:"right",fontWeight:600,fontSize:12,color:net>=0?"#3B6D11":"#A32D2D",whiteSpace:"nowrap"}}>
                      {logTab==="all"?`순손익 ${net>=0?"+":""}${fmt(net)}`:logTab==="income"?`+${fmt(sumInc)}`:`-${fmt(sumExp)}`}
                    </td>
                    <td colSpan={2}/>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
        {filteredLogs.length>80&&<div style={{textAlign:"center",padding:"10px 0",color:T.sub,fontSize:11,borderTop:`0.5px solid ${T.border}`}}>최근 80건 표시 중 · 총 {filteredLogs.length}건</div>}
      </div>
    );
  };

  const LogContent=(
    <div>
      {/* ── 필터 영역 ── */}
      <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:14,marginBottom:10}}>
        {/* 수익/지출/전체 탭 + 뷰 토글 */}
        <div style={{display:"flex",gap:4,marginBottom:10,alignItems:"center"}}>
          <div style={{display:"flex",gap:4,flex:1}}>
            {[["all","전체"],["boss","⚔️ 보스"],["hunt","🏃 사냥"],["market","🏪 경매장"],["etc","📦 기타"]].map(([k,l])=>(
              <button key={k} onClick={()=>setLogTab(k)} style={{flex:1,padding:"7px 0",border:`0.5px solid ${logTab===k?"#534AB7":T.border}`,background:logTab===k?"#534AB7":T.card,color:logTab===k?"#fff":T.sub,borderRadius:8,fontSize:11,cursor:"pointer",fontWeight:logTab===k?500:400}}>{l}</button>
            ))}
          </div>
          {/* 카드/테이블 토글 */}
          <div style={{display:"flex",border:`0.5px solid ${T.border}`,borderRadius:8,overflow:"hidden",flexShrink:0}}>
            <button onClick={()=>setLogView("card")} title="카드 뷰" style={{padding:"7px 10px",background:logView==="card"?"#534AB7":T.card,color:logView==="card"?"#fff":T.sub,border:"none",cursor:"pointer",fontSize:14}}>▤</button>
            <button onClick={()=>setLogView("table")} title="테이블 뷰" style={{padding:"7px 10px",background:logView==="table"?"#534AB7":T.card,color:logView==="table"?"#fff":T.sub,border:"none",cursor:"pointer",fontSize:14}}>⊞</button>
          </div>
        </div>

        {/* 캐릭터 필터 */}
        {logAllNicks.length>0&&(
          <div style={{marginBottom:10}}>
            <div style={{fontSize:11,color:T.sub,marginBottom:5,fontWeight:500}}>👤 캐릭터</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
              <button onClick={()=>setLogCharF("")} style={{padding:"3px 10px",borderRadius:20,fontSize:11,cursor:"pointer",border:`0.5px solid ${logCharF===""?"#534AB7":T.border}`,background:logCharF===""?"#534AB7":T.card,color:logCharF===""?"#fff":T.sub}}>전체</button>
              {logAllNicks.map(nick=>(
                <button key={nick} onClick={()=>setLogCharF(logCharF===nick?"":nick)}
                  style={{padding:"3px 10px",borderRadius:20,fontSize:11,cursor:"pointer",border:`0.5px solid ${logCharF===nick?"#534AB7":T.border}`,background:logCharF===nick?"#534AB7":T.card,color:logCharF===nick?"#fff":T.sub,display:"flex",alignItems:"center",gap:3}}>
                  {favorites.find(f=>f.nickname===nick)&&<span style={{fontSize:9}}>⭐</span>}{nick}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 날짜 필터 */}
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {/* 날짜 모드 / 월 모드 토글 */}
          <div style={{display:"flex",gap:4}}>
            <button onClick={()=>{setLogMonF(""); if(!logDateF) setLogDateF(today());}}
              style={{flex:1,padding:"6px 0",borderRadius:8,fontSize:11,cursor:"pointer",border:`0.5px solid ${!logMonF?"#534AB7":T.border}`,background:!logMonF?"#534AB722":T.card,color:!logMonF?"#534AB7":T.sub,fontWeight:!logMonF?600:400}}>
              📅 날짜 선택
            </button>
            <button onClick={()=>{setLogDateF(""); if(!logMonF) setLogMonF(today().slice(0,7));}}
              style={{flex:1,padding:"6px 0",borderRadius:8,fontSize:11,cursor:"pointer",border:`0.5px solid ${logMonF?"#534AB7":T.border}`,background:logMonF?"#534AB722":T.card,color:logMonF?"#534AB7":T.sub,fontWeight:logMonF?600:400}}>
              🗓️ 월 선택
            </button>
            <button onClick={()=>{setLogDateF("");setLogMonF("");setLogCharF("");setLogTab("all");setLogTypeF("");}}
              style={{padding:"6px 12px",borderRadius:8,fontSize:11,color:T.sub,cursor:"pointer",border:`0.5px solid ${T.border}`,background:"transparent",whiteSpace:"nowrap"}}>초기화</button>
          </div>
          {/* 날짜 선택 모드 */}
          {!logMonF&&(
            <DateNavPicker
              value={logDateF||today()}
              onChange={v=>{setLogDateF(v);setLogMonF("");}}
              T={T}
            />
          )}
          {/* 월 선택 모드 */}
          {logMonF&&(
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <button onClick={()=>{const [y,m]=logMonF.split("-").map(Number);const d=new Date(y,m-2,1);setLogMonF(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`);}}
                style={{width:32,height:36,borderRadius:8,border:`0.5px solid ${T.border}`,background:T.card,color:T.text,fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>‹</button>
              <div style={{flex:1,height:36,borderRadius:8,border:`0.5px solid #534AB7`,background:T.inp,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:600,color:"#534AB7"}}>
                {logMonF.replace("-","년 ")}월
              </div>
              <button onClick={()=>{const [y,m]=logMonF.split("-").map(Number);const d=new Date(y,m,1);setLogMonF(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`);}}
                style={{width:32,height:36,borderRadius:8,border:`0.5px solid ${T.border}`,background:T.card,color:T.text,fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>›</button>
              <button onClick={()=>setLogMonF(today().slice(0,7))}
                style={{height:36,padding:"0 10px",borderRadius:8,border:`0.5px solid #534AB7`,background:"#534AB722",color:"#534AB7",fontSize:12,fontWeight:600,cursor:"pointer",flexShrink:0,whiteSpace:"nowrap"}}>이번달</button>
            </div>
          )}
        </div>

        {/* 활성 필터 요약 뱃지 */}
        {(logCharF||logDateF||logMonF||logTab!=="all")&&(
          <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:8}}>
            {logTab!=="all"&&<span style={{background:"#534AB722",borderRadius:20,padding:"2px 8px",fontSize:10,color:"#534AB7"}}>
              {{"all":"전체","boss":"⚔️ 보스","hunt":"🏃 사냥","market":"🏪 경매장","etc":"📦 기타","income":"💰 수익","expense":"💸 지출"}[logTab]||logTab}
            </span>}
            {logCharF&&<span style={{background:"#534AB722",borderRadius:20,padding:"2px 8px",fontSize:10,color:"#534AB7"}}>캐릭터: {logCharF}</span>}
            {logDateF&&<span style={{background:"#534AB722",borderRadius:20,padding:"2px 8px",fontSize:10,color:"#534AB7"}}>날짜: {logDateF}</span>}
            {logMonF&&<span style={{background:"#534AB722",borderRadius:20,padding:"2px 8px",fontSize:10,color:"#534AB7"}}>월: {logMonF}</span>}
          </div>
        )}
      </div>

      {/* ── 요약 수치 바 ── */}
      {filteredLogs.length>0&&(()=>{
        const sumInc=filteredLogs.filter(r=>r.kind==="income").reduce((s,r)=>s+calcIncome(r,favorites),0);
        const sumExp=filteredLogs.filter(r=>r.kind==="expense").reduce((s,r)=>s+(r.meso||0),0);
        const net=sumInc-sumExp;
        // 에르다 조각 합계
        const sumSol0=filteredLogs.filter(r=>r.kind==="income").reduce((s,r)=>s+(r.sol0||0),0);
        const sumSol1=filteredLogs.filter(r=>r.kind==="income").reduce((s,r)=>s+(r.sol1||0),0);
        const sumSol2=filteredLogs.filter(r=>r.kind==="income").reduce((s,r)=>s+(r.sol2||0),0);
        const sumSol3=filteredLogs.filter(r=>r.kind==="income").reduce((s,r)=>s+(r.sol3||0),0);
        const sumSol4=filteredLogs.filter(r=>r.kind==="income").reduce((s,r)=>s+(r.sol4||0),0);
        const hasSol = sumSol0+sumSol1+sumSol2+sumSol3+sumSol4 > 0;
        return(
          <div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:hasSol?6:10}}>
            {[["💰 수익",sumInc,"#3B6D11","#EAF3DE"],["💸 지출",sumExp,"#A32D2D","#FCEBEB"],["📊 순손익",net,net>=0?"#3B6D11":"#A32D2D",net>=0?"#EAF3DE":"#FCEBEB"]].map(([l,v,c,bg])=>(
              <div key={l} style={{background:T.card,borderRadius:10,border:`0.5px solid ${T.border}`,padding:"10px 12px"}}>
                <div style={{fontSize:10,color:T.sub,marginBottom:3}}>{l} · {filteredLogs.length}건</div>
                <div style={{fontSize:isMobile?12:13,fontWeight:600,color:c,fontVariantNumeric:"tabular-nums"}}>{l==="📊 순손익"?(net>=0?"+":"")+fmt(net):fmt(v)}</div>
              </div>
            ))}
          </div>
          {/* 에르다 조각 합계 카드 */}
          {hasSol&&(
            <div style={{background:T.card,borderRadius:10,border:`0.5px solid #0F6E5644`,padding:"10px 14px",marginBottom:10}}>
              <div style={{fontSize:10,color:"#0F6E56",fontWeight:600,marginBottom:6}}>✨ 에르다 조각 합계</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:"6px 16px"}}>
                {[["기운",sumSol0],["이벤트",sumSol1],["일반",sumSol2],["사냥",sumSol3],["교환불가",sumSol4]].filter(([,v])=>v>0).map(([l,v])=>(
                  <div key={l} style={{fontSize:12,color:T.text}}>
                    <span style={{color:T.sub,fontSize:11}}>{l} </span>
                    <span style={{fontWeight:600,color:"#0F6E56"}}>{v.toLocaleString()}개</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
        );
      })()}

      {/* ── 기록 없음 ── */}
      {filteredLogs.length===0&&logTab!=="boss"?(
        <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,textAlign:"center",padding:"40px 0",color:T.sub}}>
          <div style={{fontSize:36,marginBottom:10}}>📋</div>
          <div style={{fontSize:13}}>기록이 없습니다</div>
          <div style={{fontSize:11,marginTop:4}}>수익 또는 지출을 먼저 기록해보세요</div>
        </div>
      ):logTab==="boss"?(
        // ── 보스 탭: 주단위 뷰 ──
        (()=>{
          // logBossWeek = 해당 주 시작일(목)
          const wStart = logBossWeek;
          const wEnd   = (()=>{ const d=new Date(wStart+"T00:00:00"); d.setDate(d.getDate()+6); return localDateStr(d); })();
          // eslint-disable-next-line no-unused-vars
          const prevWeek = ()=>{ const d=new Date(wStart+"T00:00:00"); d.setDate(d.getDate()-7); setLogBossWeek(localDateStr(d)); };
          // eslint-disable-next-line no-unused-vars
          const nextWeek = ()=>{ const d=new Date(wStart+"T00:00:00"); d.setDate(d.getDate()+7); setLogBossWeek(localDateStr(d)); };

          // ── 독립 state 기반 년/월/주 네비 ──
          const navYear  = logBossNavYear;
          const navMonth = logBossNavMonth;

          // 전체 보기 여부
          const isAllView = navYear===0 || navMonth===0;

          // 보스 기록 필터 (전체 보기면 날짜 범위 없음)
          const bossRecs = records.filter(r=>
            r.kind==="income" && r.type==="boss" &&
            (isAllView ? true : (r.date>=wStart && r.date<=wEnd)) &&
            Array.isArray(r.bossList) &&
            // 년도 전체 보기면 해당 년도만
            (navYear===0 ? true : r.date.startsWith(String(navYear))) &&
            (!logCharF||(r.nickname||"공통")===logCharF)
          );

          // 날짜별 그룹핑 (같은 날짜의 record들 묶기)
          const dateGroups = {};
          bossRecs.forEach(r=>{
            if(!dateGroups[r.date]) dateGroups[r.date]=[];
            dateGroups[r.date].push(r);
          });
          const sortedDates = Object.keys(dateGroups).sort();

          // 주간 보스 총 마리수 & 결정석 합계
          const allBossNames = new Set();
          let totalCrystal = 0;
          bossRecs.forEach(r=>{
            r.bossList?.forEach(b=>{
              if(BOSSES.weekly.some(w=>w.name===b.name)) allBossNames.add(b.name.split(" (")[0]);
              totalCrystal += Math.floor((b.crystal||0));
            });
          });

          const DAY_KO = ["일","월","화","수","목","금","토"];

          // 해당 월의 모든 주 목록 생성 (목~수 기준)
          const getWeeksOfMonth = (year, month) => {
            const weeks = [];
            // 해당 월 1일이 속한 주의 시작일부터
            const firstDay = new Date(year, month-1, 1);
            const fDay = firstDay.getDay();
            const fDiff = fDay>=4 ? fDay-4 : fDay+3;
            const firstWStart = new Date(firstDay);
            firstWStart.setDate(firstDay.getDate()-fDiff);

            let cur = new Date(firstWStart);
            // 해당 월에 걸치는 모든 주 추가
            while(true) {
              const ws = localDateStr(cur);
              const we = (()=>{ const d=new Date(cur); d.setDate(d.getDate()+6); return localDateStr(d); })();
              // 이 주가 해당 월과 겹치는지 확인
              const weMonth = parseInt(we.slice(5,7));
              const wsMonth = parseInt(ws.slice(5,7));
              const weYear  = parseInt(we.slice(0,4));
              const wsYear  = parseInt(ws.slice(0,4));
              if((wsYear===year&&wsMonth===month)||(weYear===year&&weMonth===month)) {
                weeks.push({ws, we});
              }
              if((weYear>year)||(weYear===year&&weMonth>month)) break;
              cur.setDate(cur.getDate()+7);
            }
            return weeks;
          };

          // 전체 보기 처리
          const weeksOfMonth = (()=>{
            if(navYear===0) {
              // 년도 전체 보기: 모든 기록의 주 목록
              const allWStarts = new Set(
                records.filter(r=>r.kind==="income"&&r.type==="boss"&&(!logCharF||(r.nickname||"공통")===logCharF))
                  .map(r=>{ const d=new Date(r.date+"T00:00:00"); const day=d.getDay(); const diff=day>=4?day-4:day+3; d.setDate(d.getDate()-diff); return localDateStr(d); })
              );
              return [...allWStarts].sort().map(ws=>({
                ws, we:(()=>{ const d=new Date(ws+"T00:00:00"); d.setDate(d.getDate()+6); return localDateStr(d); })()
              }));
            }
            if(navMonth===0) {
              return [1,2,3,4,5,6,7,8,9,10,11,12].flatMap(m=>getWeeksOfMonth(navYear,m))
                .filter((w,i,arr)=>arr.findIndex(x=>x.ws===w.ws)===i)
                .sort((a,b)=>a.ws.localeCompare(b.ws));
            }
            return getWeeksOfMonth(navYear, navMonth);
          })();

          // 해당 월의 기록 있는 주 Set
          const weeksWithRec = new Set(
            records.filter(r=>r.kind==="income"&&r.type==="boss"&&Array.isArray(r.bossList)&&
              (!logCharF||(r.nickname||"공통")===logCharF))
              .map(r=>{ // 해당 record의 주 시작일
                const d=new Date(r.date+"T00:00:00"); const day=d.getDay();
                const diff=day>=4?day-4:day+3; d.setDate(d.getDate()-diff);
                return localDateStr(d);
              })
          );

          // 년도 목록: 첫 기록 년도 ~ 내년 (미래 월도 선택 가능)
          const firstRecYear = records.filter(r=>r.kind==="income"&&r.type==="boss").map(r=>+r.date.slice(0,4)).sort()[0] || +today().slice(0,4);
          const thisYear = +today().slice(0,4);
          const allYears = [];
          for(let y=thisYear+1; y>=firstRecYear; y--) allYears.push(y);

          return(
            <div>
              {/* ── 주차 네비게이션 ── */}
              <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:"12px 14px",marginBottom:8}}>

                {/* 상단: 년/월/주 선택 + 배지 */}
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                  {/* 년 드롭다운 */}
                  <select value={navYear} onChange={e=>{
                    const y=+e.target.value;
                    setLogBossNavYear(y);
                    if(y===0) {
                      // 전체 보기: 월도 전체로, 첫 기록 주로 이동
                      setLogBossNavMonth(0);
                      const firstRec = records.filter(r=>r.kind==="income"&&r.type==="boss").sort((a,b)=>a.date.localeCompare(b.date))[0];
                      if(firstRec) {
                        const d=new Date(firstRec.date+"T00:00:00"); const day=d.getDay(); const diff=day>=4?day-4:day+3;
                        d.setDate(d.getDate()-diff); setLogBossWeek(localDateStr(d));
                      }
                    } else {
                      const targetM = navMonth===0?1:navMonth;
                      const d=new Date(y, targetM-1, 1);
                      const day=d.getDay(); const diff=day>=4?day-4:day+3;
                      d.setDate(d.getDate()-diff); setLogBossWeek(localDateStr(d));
                    }
                  }} style={{padding:"5px 8px",borderRadius:8,border:`0.5px solid ${T.border}`,background:T.inp,color:T.text,fontSize:12,cursor:"pointer",outline:"none"}}>
                    <option value={0}>전체 보기</option>
                    {allYears.map(y=><option key={y} value={y}>{y}년</option>)}
                  </select>

                  <select value={navMonth} onChange={e=>{
                    const m=+e.target.value;
                    setLogBossNavMonth(m);
                    // 전체 보기면 해당 년도 첫 주, 아니면 해당 월 첫 주
                    const targetM = m===0 ? 1 : m;
                    const d=new Date(navYear, targetM-1, 1);
                    const day=d.getDay(); const diff=day>=4?day-4:day+3;
                    d.setDate(d.getDate()-diff);
                    setLogBossWeek(localDateStr(d));
                  }} style={{padding:"5px 8px",borderRadius:8,border:`0.5px solid ${T.border}`,background:T.inp,color:T.text,fontSize:12,cursor:"pointer",outline:"none"}}>
                    <option value={0}>전체 보기</option>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m=><option key={m} value={m}>{m}월</option>)}
                  </select>

                  {/* 주차 드롭다운 */}
                  <select value={weeksOfMonth.some(w=>w.ws===wStart)?wStart:weeksOfMonth[0]?.ws||wStart}
                    onChange={e=>setLogBossWeek(e.target.value)}
                    style={{flex:1,padding:"5px 8px",borderRadius:8,border:`0.5px solid #534AB7`,background:T.inp,color:"#534AB7",fontSize:12,cursor:"pointer",fontWeight:600,minWidth:0,outline:"none"}}>
                    {weeksOfMonth.length>1&&<option value="all" disabled>── 전체 {weeksOfMonth.length}주 ──</option>}
                    {weeksOfMonth.map(({ws,we})=>{
                      const hasRec = weeksWithRec.has(ws);
                      const isCur  = ws===wStart;
                      return(
                        <option key={ws} value={ws}>
                          {isCur?"▶ ":"  "}{ws.slice(0,4)!==navYear.toString()&&navYear===0?ws.slice(2,7).replace("-","/"):ws.slice(5).replace("-","/")} ~ {we.slice(5).replace("-","/")} {hasRec?"●":"○"}
                        </option>
                      );
                    })}
                  </select>

                  {/* 배지 */}
                  <span style={{padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:600,background:"#EEEDFE",color:"#534AB7",border:"0.5px solid #534AB7",flexShrink:0}}>
                    {allBossNames.size}/12
                  </span>
                  <span style={{padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:600,background:totalCrystal>0?"#EAF3DE":"#F5F5F5",color:totalCrystal>0?"#3B6D11":T.sub,flexShrink:0}}>
                    {fmt(totalCrystal)}
                  </span>
                </div>

                {/* 하단: ◀ 이전주 / 이번주 / 다음주 ▶ — 전체 보기일 때 숨김 */}
                {!isAllView&&<div style={{display:"flex",gap:6}}>
                  <button onClick={()=>{
                    const d=new Date(wStart+"T00:00:00"); d.setDate(d.getDate()-7);
                    const ws=localDateStr(d);
                    setLogBossNavYear(+ws.slice(0,4));
                    setLogBossNavMonth(+ws.slice(5,7));
                    setLogBossWeek(ws);
                  }} style={{flex:1,padding:"5px 0",borderRadius:8,border:`0.5px solid ${T.border}`,background:T.card,color:T.sub,fontSize:11,cursor:"pointer"}}>◀ 이전 주</button>
                  <button onClick={()=>{
                    const ws=thisWeekStart();
                    setLogBossNavYear(+ws.slice(0,4));
                    setLogBossNavMonth(+today().slice(5,7));
                    setLogBossWeek(ws);
                  }} style={{flex:1,padding:"5px 0",borderRadius:8,border:`0.5px solid #534AB7`,background:"#EEEDFE",color:"#534AB7",fontSize:11,cursor:"pointer",fontWeight:600}}>이번 주</button>
                  <button onClick={()=>{
                    const d=new Date(wStart+"T00:00:00"); d.setDate(d.getDate()+7);
                    const ws=localDateStr(d);
                    setLogBossNavYear(+ws.slice(0,4));
                    setLogBossNavMonth(+ws.slice(5,7));
                    setLogBossWeek(ws);
                  }} style={{flex:1,padding:"5px 0",borderRadius:8,border:`0.5px solid ${T.border}`,background:T.card,color:T.sub,fontSize:11,cursor:"pointer"}}>다음 주 ▶</button>
                </div>}
              </div>

              {/* 기록 없음 */}
              {sortedDates.length===0&&(
                <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,textAlign:"center",padding:"32px 0",color:T.sub}}>
                  <div style={{fontSize:32,marginBottom:8}}>⚔️</div>
                  <div style={{fontSize:13,marginBottom:16}}>{isAllView?"보스 기록이 없습니다":"이 주에 보스 기록이 없습니다"}</div>
                  {!isAllView&&<button onClick={()=>{
                    setTab("income"); setIncType("boss"); setIncDate(wStart);
                  }} style={{background:"#534AB7",color:"#fff",border:"none",borderRadius:8,padding:"8px 20px",fontSize:12,fontWeight:500,cursor:"pointer"}}>
                    + 이 주 보스 기록 추가
                  </button>}
                </div>
              )}
              {/* 날짜별 그룹 */}
              {sortedDates.map(date=>{
                const dayRecs = dateGroups[date];
                const d = new Date(date+"T00:00:00");
                const dayLabel = `${date.slice(5).replace("-","/")} ${DAY_KO[d.getDay()]}요일`;
                const dayTotal = dayRecs.reduce((s,r)=>s+(r.bossList?.reduce((ss,b)=>ss+(b.crystal||0),0)||0),0);

                return(
                  <div key={date} style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,marginBottom:8,overflow:"hidden"}}>
                    {/* 날짜 헤더 */}
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderBottom:`0.5px solid ${T.border}`,background:T.bg}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:12,fontWeight:700,color:T.text}}>━ {dayLabel}</span>
                        {dayRecs.length>1&&<span style={{fontSize:10,color:T.sub}}>(기록 {dayRecs.length}건)</span>}
                        {isPatchTransitionDate(date)&&(
                          <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:20,background:"#534AB7",color:"#fff",whiteSpace:"nowrap"}}>
                            ⚡ 패치일
                          </span>
                        )}
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:11,fontWeight:600,color:"#854F0B"}}>{fmt(dayTotal)}</span>
                      </div>
                    </div>

                    {/* 해당 날짜 record들 */}
                    {dayRecs.map(r=>(
                      <div key={r.id} style={{padding:"8px 14px",borderBottom:`0.5px solid ${T.border}`}}>
                        {/* 닉네임 + 수정 버튼 항상 표시 */}
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <span style={{fontSize:10,color:T.sub}}>{r.nickname||"공통"}</span>
                            {isPatchTransitionDate(r.date)&&phaseBadgeInfo(r.pricePhase)&&(()=>{
                              const pb = phaseBadgeInfo(r.pricePhase);
                              return (
                                <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:20,whiteSpace:"nowrap",
                                  background:pb.bg,color:pb.fg}}>
                                  {pb.label}{r.pricePhase==="mixed"?"":" 가격"}
                                </span>
                              );
                            })()}
                          </div>
                          <button onClick={()=>openDetail(r,true)}
                            style={{background:"transparent",border:`0.5px solid ${T.border}`,borderRadius:6,padding:"2px 7px",fontSize:10,color:T.sub,cursor:"pointer"}}>✏️</button>
                        </div>
                        {/* 보스 리스트 */}
                        <div style={{display:"flex",flexDirection:"column",gap:4}}>
                          {r.bossList?.map((b,i)=>{
                            const isWeekly  = BOSSES.weekly.some(w=>w.name===b.name);
                            const isMonthly = BOSSES.monthly.some(m=>m.name===b.name);
                            const secColor  = isMonthly?"#A32D2D":isWeekly?"#534AB7":"#3B6D11";
                            const itemPb = b.phase ? phaseBadgeInfo(b.phase) : null;
                            return(
                              <div key={i} style={{display:"flex",alignItems:"center",gap:8}}>
                                <span style={{width:6,height:6,borderRadius:"50%",background:secColor,flexShrink:0}}/>
                                <span style={{fontSize:12,color:T.text,flex:1}}>{b.name}</span>
                                {itemPb&&(
                                  <span style={{fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:10,whiteSpace:"nowrap",
                                    background:itemPb.bg,color:itemPb.fg}}>
                                    {itemPb.short}
                                  </span>
                                )}
                                {b.party>1&&(
                                  b.members?.filter(Boolean).length>0
                                    ? <span style={{fontSize:10,color:"#534AB7"}}>{b.party}인 · {b.members.filter(Boolean).join(", ")}</span>
                                    : <span style={{fontSize:10,color:T.sub}}>{b.party}인</span>
                                )}
                                <span style={{fontSize:11,fontWeight:600,color:"#854F0B",flexShrink:0}}>{fmt(b.crystal||0)}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })()
      ):(
        logView==="card" ? renderCardView() : renderTableView()
      )}
    </div>
  );

  const SettingsContent=(
    <div>
      {/* 기본 설정 */}
      <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16,marginBottom:10}}>
        <div style={{fontSize:13,fontWeight:500,color:"#534AB7",marginBottom:12}}>⚙️ 설정</div>
        {[
          ["🌙 다크 모드",<button onClick={()=>setDark(d=>!d)} style={{width:44,height:24,borderRadius:12,background:dark?"#534AB7":"#CCC",border:"none",cursor:"pointer",position:"relative",flexShrink:0}}><div style={{position:"absolute",top:3,left:dark?22:2,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left .2s"}}></div></button>],
          ["👤 로그인 계정",<span style={{fontSize:12,color:T.sub}}>{user?.email||"로그인 필요"}</span>],
          ["📊 데이터 현황",<span style={{fontSize:12,color:T.sub}}>수익 {incRecs.length}건 · 지출 {expRecs.length}건 · 즐겨찾기 {favorites.length}명</span>],
          ["🔒 데이터 격리",<span style={{fontSize:11,background:"#EAF3DE",color:"#3B6D11",padding:"2px 8px",borderRadius:20}}>내 계정만 접근 가능</span>],
          ["📱 화면 모드",<span style={{fontSize:12,color:T.sub}}>{isMobile?"모바일":"PC"} 레이아웃</span>],
          ...(isAdmin?[["👑 관리자",<span style={{fontSize:11,background:"#FAEEDA",color:"#854F0B",padding:"2px 8px",borderRadius:20}}>활성화됨</span>]]:[]),
        ].map(([label,control],i)=>(
          <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:`0.5px solid ${T.border}`}}>
            <span style={{fontSize:13,color:T.text}}>{label}</span>{control}
          </div>
        ))}
        {user&&<button onClick={()=>signOut(auth)} style={{background:"#A32D2D",color:"#fff",border:"none",borderRadius:8,padding:11,fontSize:13,fontWeight:500,cursor:"pointer",width:"100%",marginTop:16}}>로그아웃</button>}
        {!user&&<button onClick={handleLogin} style={{background:"#534AB7",color:"#fff",border:"none",borderRadius:8,padding:11,fontSize:13,fontWeight:500,cursor:"pointer",width:"100%",marginTop:16}}>🔐 Google 계정으로 로그인</button>}
      </div>

      {/* 데이터 관리: 초기화 · 시즌 보관함 */}
      {user&&<DataManager records={records} favorites={favorites} T={T} isMobile={isMobile} fmt={fmt} calcIncome={calcIncome} calcSolIncome={calcSolIncome} showToast={showToast}/>}

      {/* 캐릭터별 수익 계산 방식 설정 */}
      {favorites.length>0&&(
        <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16,marginBottom:10}}>
          <div style={{fontSize:13,fontWeight:500,color:"#534AB7",marginBottom:4}}>📐 캐릭터별 수익 계산 방식</div>
          <div style={{fontSize:11,color:T.sub,marginBottom:14,lineHeight:1.7}}>
            <strong style={{color:"#534AB7"}}>A유형</strong> — 솔에르다·물욕템 수량 입력 시 환산가가 수익에 자동 합산됩니다.<br/>
            <strong style={{color:"#3B6D11"}}>B유형</strong> — 수량만 메모하고 수익에 합산하지 않습니다. 직접 판매 후 수익을 따로 입력하는 방식입니다.
          </div>

          {favorites.map((fav,i)=>{
            // 현재 Firebase에 저장된 값 (기본값 "" 통일)
            const savedSolType       = fav.solType||"A";
            const savedSolApplyDate  = fav.solApplyDate||"";
            const savedDropType      = fav.dropType||"A";
            const savedDropApplyDate = fav.dropApplyDate||"";

            // draft: 편집 중인 임시값. 없으면 저장값으로 초기화
            const draft = favDraft[fav.nickname] || {
              solType: savedSolType, solApplyDate: savedSolApplyDate||today(),
              dropType: savedDropType, dropApplyDate: savedDropApplyDate||today(),
            };
            const setDraft = (patch) => setFavDraft(p=>({
              ...p,
              [fav.nickname]: {...(favDraft[fav.nickname]||{
                solType:savedSolType, solApplyDate:savedSolApplyDate||today(),
                dropType:savedDropType, dropApplyDate:savedDropApplyDate||today(),
              }), ...patch}
            }));

            // 저장된 값과 draft 비교
            const isDirty = draft.solType!==savedSolType
              || (draft.solType==="B" && draft.solApplyDate!==(savedSolApplyDate||today()))
              || draft.dropType!==savedDropType
              || (draft.dropType==="B" && draft.dropApplyDate!==(savedDropApplyDate||today()));

            const handleConfirm = async () => {
              const next = favorites.map(f=>f.nickname===fav.nickname
                ? {...f,
                    solType:draft.solType,
                    solApplyDate:draft.solType==="B"?draft.solApplyDate:"",
                    dropType:draft.dropType,
                    dropApplyDate:draft.dropType==="B"?draft.dropApplyDate:"",
                  }
                : f
              );
              try {
                await saveFavorites(user.uid, next);
                setFavorites(next);
                setFavDraft(p=>{const n={...p}; delete n[fav.nickname]; return n;});
                showToast(`${fav.nickname} 설정이 저장됐습니다!`, "✅");
              } catch(e) {
                alert("저장 실패: "+e.message);
              }
            };

            return (
              <div key={fav.nickname} style={{background:T.bg,borderRadius:10,border:`0.5px solid ${isDirty?"#534AB7":T.border}`,padding:"14px",marginBottom:i<favorites.length-1?8:0,transition:"border-color 0.2s"}}>
                {/* 캐릭터 이름 */}
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                  {fav.image&&<CharAvatar src={fav.image} alt={fav.nickname} size={34}/>}
                  <span style={{fontSize:13,fontWeight:600,color:T.text,flex:1}}>{fav.nickname}</span>
                  <span style={{fontSize:11,color:T.sub}}>{fav.job} · Lv.{fav.level}</span>
                </div>

                {/* 솔에르다 설정 */}
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:600,color:T.sub,marginBottom:6}}>✨ 솔에르다</div>
                  <div style={{display:"flex",gap:6,marginBottom:draft.solType==="B"?8:0}}>
                    {["A","B"].map(t=>(
                      <button key={t} onClick={()=>setDraft({solType:t,solApplyDate:draft.solApplyDate||today()})}
                        style={{flex:1,padding:"7px 0",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",
                          border:`1.5px solid ${draft.solType===t?(t==="A"?"#534AB7":"#3B6D11"):T.border}`,
                          background:draft.solType===t?(t==="A"?"#534AB722":"#EAF3DE"):T.card,
                          color:draft.solType===t?(t==="A"?"#534AB7":"#3B6D11"):T.sub}}>
                        {t}유형 {t==="A"?"(환산 합산)":"(메모만)"}
                      </button>
                    ))}
                  </div>
                  {draft.solType==="B"&&(
                    <div>
                      <div style={{fontSize:11,color:T.sub,marginBottom:4}}>적용 시작일 <span style={{color:"#3B6D11"}}>(이 날짜부터 B유형 적용)</span></div>
                      <DateNavPicker value={draft.solApplyDate||today()} onChange={v=>setDraft({solApplyDate:v})} T={T}/>
                    </div>
                  )}
                </div>

                {/* 물욕템 설정 */}
                <div style={{marginBottom:isDirty?12:0}}>
                  <div style={{fontSize:11,fontWeight:600,color:T.sub,marginBottom:6}}>💎 물욕템</div>
                  <div style={{display:"flex",gap:6,marginBottom:draft.dropType==="B"?8:0}}>
                    {["A","B"].map(t=>(
                      <button key={t} onClick={()=>setDraft({dropType:t,dropApplyDate:draft.dropApplyDate||today()})}
                        style={{flex:1,padding:"7px 0",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",
                          border:`1.5px solid ${draft.dropType===t?(t==="A"?"#534AB7":"#3B6D11"):T.border}`,
                          background:draft.dropType===t?(t==="A"?"#534AB722":"#EAF3DE"):T.card,
                          color:draft.dropType===t?(t==="A"?"#534AB7":"#3B6D11"):T.sub}}>
                        {t}유형 {t==="A"?"(수익 합산)":"(메모만)"}
                      </button>
                    ))}
                  </div>
                  {draft.dropType==="B"&&(
                    <div>
                      <div style={{fontSize:11,color:T.sub,marginBottom:4}}>적용 시작일 <span style={{color:"#3B6D11"}}>(이 날짜부터 B유형 적용)</span></div>
                      <DateNavPicker value={draft.dropApplyDate||today()} onChange={v=>setDraft({dropApplyDate:v})} T={T}/>
                    </div>
                  )}
                </div>

                {/* 확인 버튼 — 변경 시에만 표시 */}
                {isDirty&&(
                  <div style={{display:"flex",gap:8,marginTop:4}}>
                    <button onClick={()=>setFavDraft(p=>{const n={...p};delete n[fav.nickname];return n;})}
                      style={{flex:1,padding:"8px 0",borderRadius:8,fontSize:12,border:`0.5px solid ${T.border}`,background:T.card,color:T.sub,cursor:"pointer"}}>
                      취소
                    </button>
                    <button onClick={handleConfirm}
                      style={{flex:2,padding:"8px 0",borderRadius:8,fontSize:12,fontWeight:600,border:"none",background:"#534AB7",color:"#fff",cursor:"pointer"}}>
                      ✅ 설정 저장
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {favorites.length===0&&(
        <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:20,textAlign:"center",color:T.sub,fontSize:13}}>
          즐겨찾기 캐릭터를 먼저 추가하면<br/>캐릭터별 수익 계산 방식을 설정할 수 있어요.
        </div>
      )}

      {/* ── 건의함 ── */}
      <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:16,marginBottom:10}}>
        <div style={{fontSize:13,fontWeight:500,color:"#534AB7",marginBottom:4}}>📬 건의함</div>
        <div style={{fontSize:11,color:T.sub,marginBottom:14,lineHeight:1.7}}>
          불편한 점이나 개선 요청을 남겨주세요.<br/>작성자 본인과 관리자만 확인할 수 있습니다.
        </div>

        {/* 건의 작성 폼 */}
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
          <input type="text" value={inqTitle} onChange={e=>setInqTitle(e.target.value)}
            placeholder="제목" style={{...IS}}/>
          <textarea value={inqContent} onChange={e=>setInqContent(e.target.value)}
            placeholder="내용을 입력해주세요..." rows={4}
            style={{...IS,resize:"vertical",lineHeight:1.6}}/>
          <button onClick={async()=>{
            if(!inqTitle.trim()||!inqContent.trim()) return alert("제목과 내용을 입력해주세요");
            await addInquiry(user.uid, user?.email||"", inqTitle.trim(), inqContent.trim());
            setInqTitle(""); setInqContent("");
            showToast("건의가 접수됐습니다!", "📬");
          }} style={{background:"#534AB7",color:"#fff",border:"none",borderRadius:8,padding:"10px 0",fontSize:13,fontWeight:500,cursor:"pointer"}}>
            📬 건의 접수
          </button>
        </div>

        {/* 내 건의 목록 */}
        {inquiries.length>0&&(
          <div>
            <div style={{fontSize:11,fontWeight:600,color:T.sub,marginBottom:8}}>내 건의 목록</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {inquiries.map(inq=>(
                <div key={inq.id} style={{background:T.bg,borderRadius:10,border:`0.5px solid ${T.border}`,overflow:"hidden"}}>
                  <div onClick={()=>setInqOpen(inqOpen===inq.id?null:inq.id)}
                    style={{padding:"10px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:8}}>
                    <span style={{padding:"2px 8px",borderRadius:20,fontSize:10,fontWeight:600,
                      background:inq.status==="closed"?"#EAF3DE":"#EEEDFE",
                      color:inq.status==="closed"?"#3B6D11":"#534AB7",flexShrink:0}}>
                      {inq.status==="closed"?"답변완료":"접수중"}
                    </span>
                    <span style={{fontSize:12,fontWeight:500,color:T.text,flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{inq.title}</span>
                    <span style={{fontSize:10,color:T.sub,flexShrink:0}}>{inq.createdAt?.toDate?.()?.toLocaleDateString("ko-KR")||""}</span>
                    <span style={{fontSize:11,color:T.sub}}>{inqOpen===inq.id?"▲":"▼"}</span>
                  </div>
                  {inqOpen===inq.id&&(
                    <div style={{padding:"0 12px 12px",borderTop:`0.5px solid ${T.border}`}}>
                      <div style={{fontSize:13,color:T.text,lineHeight:1.7,marginTop:10,whiteSpace:"pre-wrap"}}>{inq.content}</div>
                      {inq.adminReply&&(
                        <div style={{background:dark?"#1A1A2E":"#F5F3FF",borderRadius:8,padding:"10px 12px",marginTop:10,border:`0.5px solid #534AB744`}}>
                          <div style={{fontSize:10,fontWeight:600,color:"#534AB7",marginBottom:4}}>💬 관리자 답변</div>
                          <div style={{fontSize:13,color:T.text,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{inq.adminReply}</div>
                        </div>
                      )}
                      {/* 관리자 답글 입력 */}
                      {isAdmin&&(
                        <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:6}}>
                          <textarea value={inqReply} onChange={e=>setInqReply(e.target.value)}
                            placeholder="답변 입력..." rows={3}
                            style={{...IS,resize:"vertical"}}/>
                          <div style={{display:"flex",gap:6}}>
                            <button onClick={async()=>{
                              if(!inqReply.trim()) return;
                              await replyInquiry(inq.id, inqReply.trim());
                              setInqReply(""); showToast("답변이 등록됐습니다!", "✅");
                            }} style={{flex:2,background:"#534AB7",color:"#fff",border:"none",borderRadius:8,padding:"8px 0",fontSize:12,fontWeight:500,cursor:"pointer"}}>
                              답변 등록
                            </button>
                            <button onClick={async()=>{
                              if(!window.confirm("삭제할까요?")) return;
                              await deleteInquiry(inq.id);
                            }} style={{flex:1,background:"transparent",border:"0.5px solid #A32D2D",color:"#A32D2D",borderRadius:8,padding:"8px 0",fontSize:12,cursor:"pointer"}}>
                              삭제
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}


      </div>
    </div>
  );

  const tabContent={
    home:HomeContent, income:IncomeContent, expense:ExpenseContent,
    exchange:ExchangeContent, market:MarketContent,
    log:LogContent, stats:<StatsHub records={records} favorites={favorites} T={T} isMobile={isMobile} selChar={selChar} fmt={fmt} calcIncome={calcIncome} calcSolIncome={calcSolIncome} legacy={<StatsTab records={records} favorites={favorites} T={T} isMobile={isMobile}/>}/>,
    settings:SettingsContent,
  };

  const Topbar=(
    <div style={{background:T.card,borderBottom:`0.5px solid ${T.border}`,padding:`0 ${isMobile?14:24}px`,display:"flex",alignItems:"center",gap:10,height:isMobile?48:52,position:"sticky",top:0,zIndex:100}}>
      <span style={{fontSize:isMobile?18:20}}>🍁</span>
      <span style={{fontSize:isMobile?14:16,fontWeight:500,flex:1,color:T.text}}>메이플 파밍 트래커</span>
      <a href="/notice" target="_blank" rel="noopener noreferrer" style={{background:"transparent",border:`0.5px solid ${T.border}`,borderRadius:8,padding:"5px 10px",fontSize:12,color:T.sub,cursor:"pointer",display:"flex",alignItems:"center",gap:5,textDecoration:"none"}}>
        📢 공지{pinnedCount>0&&<span style={{background:"#A32D2D",color:"#fff",borderRadius:20,padding:"0 5px",fontSize:10,fontWeight:500}}>{pinnedCount}</span>}
      </a>
      <button onClick={()=>setShowDonation(true)} style={{background:"transparent",border:`0.5px solid #E4A80066`,borderRadius:8,padding:"5px 10px",fontSize:12,color:"#E4A800",cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
        ☕ 후원
      </button>
      {isAdmin&&(
        <button onClick={()=>setShowAdminInq(true)} style={{background:"transparent",border:`0.5px solid ${T.border}`,borderRadius:8,padding:"5px 10px",fontSize:12,color:T.sub,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
          📬 건의{allInquiries.filter(i=>i.status==="open").length>0&&<span style={{background:"#534AB7",color:"#fff",borderRadius:20,padding:"0 5px",fontSize:10,fontWeight:500}}>{allInquiries.filter(i=>i.status==="open").length}</span>}
        </button>
      )}
      {isAdmin&&(
        <button onClick={()=>setShowGifts(true)} style={{background:"transparent",border:`0.5px solid #E4A80033`,borderRadius:8,padding:"5px 10px",fontSize:12,color:"#E4A800",cursor:"pointer"}}>
          🎁 후원내역
        </button>
      )}
      {isAdmin&&(
        <button onClick={()=>setShowNoticeManager(true)}
          style={{background:"transparent",border:`0.5px solid #534AB766`,borderRadius:8,padding:"5px 10px",fontSize:12,color:"#534AB7",cursor:"pointer",fontWeight:600}}>
          📋 공지관리
        </button>
      )}
      {isAdmin&&(
        <button onClick={()=>{setNoticeForm(true);setEditNoticeId(null);setNTitle("");setNContent("");setNSummary("");setNCategory("patch");setNPinned(false);setNThumb("");setNThumbFile(null);}}
          style={{background:"#534AB7",border:"none",borderRadius:8,padding:"5px 10px",fontSize:12,color:"#fff",cursor:"pointer",fontWeight:600}}>
          ✏️ 공지작성
        </button>
      )}
      {!isMobile&&user&&<span style={{fontSize:12,color:T.sub}}>{user.displayName}</span>}
      <button onClick={()=>setDark(d=>!d)} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:isMobile?16:18,padding:"4px 6px"}}>{dark?"☀️":"🌙"}</button>
      {!isMobile&&(user
        ? <button onClick={()=>signOut(auth)} style={{background:"transparent",color:T.sub,border:`0.5px solid ${T.border}`,borderRadius:6,padding:"5px 12px",fontSize:12,cursor:"pointer"}}>로그아웃</button>
        : <button onClick={handleLogin} style={{background:"#534AB7",color:"#fff",border:"none",borderRadius:6,padding:"5px 12px",fontSize:12,cursor:"pointer",fontWeight:500}}>🔐 로그인</button>
      )}
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:T.bg,color:T.text,fontFamily:"Arial,sans-serif",fontSize:13,display:"flex",flexDirection:"column"}}>
      {Topbar}

      {/* ── 비로그인 안내 배너 ── */}
      {!user&&(
        <div style={{background:"#534AB7",color:"#fff",fontSize:12,textAlign:"center",padding:"8px 16px",display:"flex",alignItems:"center",justifyContent:"center",gap:12,flexShrink:0}}>
          <span>🔐 로그인하면 데이터가 저장되고 모든 기능을 이용할 수 있습니다</span>
          <button onClick={handleLogin} style={{background:"#fff",color:"#534AB7",border:"none",borderRadius:6,padding:"3px 12px",fontSize:12,fontWeight:600,cursor:"pointer"}}>로그인</button>
        </div>
      )}

      {/* ── 토스트 알림 ── */}
      {toast&&(
        <div style={{position:"fixed",bottom: isMobile?80:24,left:"50%",transform:"translateX(-50%)",zIndex:700,background:dark?"#2A2A3E":"#534AB7",color:"#fff",borderRadius:12,padding:"12px 20px",fontSize:13,fontWeight:500,boxShadow:"0 4px 20px rgba(0,0,0,0.25)",display:"flex",alignItems:"center",gap:8,whiteSpace:"nowrap",pointerEvents:"none",animation:"fadeInUp 0.3s ease"}}>
          <span style={{fontSize:18}}>{toast.icon}</span>
          <span>{toast.msg}</span>
        </div>
      )}

      {/* ── 자정 경과 알림 모달 ── */}
      {midnightAlert&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.65)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:T.card,borderRadius:16,border:`1px solid #534AB7`,width:"100%",maxWidth:360,padding:24}}>
            <div style={{fontSize:28,textAlign:"center",marginBottom:10}}>🌙</div>
            <div style={{fontSize:15,fontWeight:600,color:T.text,textAlign:"center",marginBottom:6}}>자정이 지났습니다</div>
            <div style={{fontSize:12,color:T.sub,textAlign:"center",marginBottom:20,lineHeight:1.6}}>
              입력한 날짜({midnightAlert.kind==="income"?incDate:expDate})와<br/>현재 날짜({today()})가 다릅니다.<br/>저장 옵션을 선택해주세요.
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <button
                onClick={()=>{
                  if(midnightAlert.kind==="income"){setIncDate(today());_doSaveIncome(today());}
                  else{setExpDate(today());_doSaveExpense(today());}
                }}
                style={{background:"#534AB7",color:"#fff",border:"none",borderRadius:10,padding:"12px 0",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                📅 오늘 날짜로 저장 ({today()})
              </button>
              <button
                onClick={()=>{
                  if(midnightAlert.kind==="income") _doSaveIncome(incDate);
                  else _doSaveExpense(expDate);
                }}
                style={{background:T.card,color:T.text,border:`0.5px solid ${T.border}`,borderRadius:10,padding:"12px 0",fontSize:13,fontWeight:500,cursor:"pointer"}}>
                📝 기록일 기준으로 저장 ({midnightAlert.kind==="income"?incDate:expDate})
              </button>
              <button
                onClick={()=>setMidnightAlert(null)}
                style={{background:"transparent",color:T.sub,border:"none",padding:"8px 0",fontSize:12,cursor:"pointer"}}>
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {showNotices&&(
        <Modal T={T} title="📢 공지사항 / 패치노트" onClose={()=>{setShowNotices(false);setOpenNoticeId(null);}}>
          {/* 카테고리 필터 */}
          {(()=>{
            const allStaticNotices = getNotices();
            const allDbNotices = [...notices].sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0));
            const filteredStatic = noticeFilter==="all" ? allStaticNotices : allStaticNotices.filter(n=>n.type===noticeFilter);
            const filteredDb = noticeFilter==="all" ? allDbNotices : allDbNotices.filter(n=>(n.category||"notice")===noticeFilter);
            return (<>
                <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
                  {[["all","전체"],["patch","🔧 패치노트"],["notice","📢 공지"],["event","🎉 이벤트"]].map(([v,l])=>(
                    <button key={v} onClick={()=>setNoticeFilter(v)}
                      style={{padding:"5px 14px",borderRadius:20,fontSize:11,fontWeight:600,cursor:"pointer",
                        border:`1px solid ${noticeFilter===v?"#534AB7":T.border}`,
                        background:noticeFilter===v?"#534AB7":"transparent",
                        color:noticeFilter===v?"#fff":T.sub}}>
                      {l}
                    </button>
                  ))}
                </div>
                {/* DB 공지 (최신순) */}
                {filteredDb.map(n=>(
                  <div key={n.id} style={{marginBottom:8}}>
                    <div onClick={()=>setOpenNoticeId(openNoticeId===n.id?null:n.id)}
                      style={{padding:"10px 14px",borderRadius:10,cursor:"pointer",
                        background:openNoticeId===n.id?T.charBg:T.bg,
                        border:`0.5px solid ${openNoticeId===n.id?"#534AB7":T.border}`}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                        {n.pinned&&<span style={{background:"#A32D2D",color:"#fff",borderRadius:20,padding:"1px 7px",fontSize:10,fontWeight:600}}>📌 중요</span>}
                        <span style={{fontSize:10,padding:"1px 8px",borderRadius:20,fontWeight:600,
                          background:(n.category||"notice")==="patch"?"#534AB722":(n.category||"notice")==="event"?"#E4A80022":"#A32D2D22",
                          color:(n.category||"notice")==="patch"?"#534AB7":(n.category||"notice")==="event"?"#E4A800":"#A32D2D"}}>
                          {(n.category||"notice")==="patch"?"🔧 패치노트":(n.category||"notice")==="event"?"🎉 이벤트":"📢 공지"}
                        </span>
                        <span style={{fontSize:10,color:T.sub,marginLeft:"auto"}}>{n.createdAt?.toDate?.()?.toLocaleDateString("ko-KR")||""}</span>
                      </div>
                      <div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:2}}>{n.title}</div>
                      {n.summary&&<div style={{fontSize:11,color:T.sub}}>{n.summary}</div>}
                    </div>
                    {openNoticeId===n.id&&(
                      <div style={{background:T.card,border:`0.5px solid #534AB733`,borderRadius:"0 0 10px 10px",padding:"14px 16px",fontSize:12,color:T.text,lineHeight:1.9,whiteSpace:"pre-wrap"}}>
                        {n.content}
                        <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:12}}>
                          {isAdmin&&<>
                            <button onClick={()=>openNoticeEdit(n)} style={{background:"transparent",border:`0.5px solid ${T.border}`,borderRadius:6,padding:"5px 12px",fontSize:12,color:T.sub,cursor:"pointer"}}>수정</button>
                            <button onClick={()=>handleDeleteNotice(n.id)} style={{background:"transparent",border:"0.5px solid #A32D2D",borderRadius:6,padding:"5px 12px",fontSize:12,color:"#A32D2D",cursor:"pointer"}}>삭제</button>
                          </>}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {/* noticeData.js 정적 공지 */}
                {filteredStatic.map(n=>(
                  <div key={n.id} style={{marginBottom:8}}>
                    <div onClick={()=>setOpenNoticeId(openNoticeId===n.id?null:n.id)}
                      style={{padding:"12px 14px",borderRadius:10,cursor:"pointer",
                        background:openNoticeId===n.id?T.charBg:T.bg,
                        border:`0.5px solid ${openNoticeId===n.id?"#534AB7":T.border}`}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                        {n.pinned&&<span style={{background:"#A32D2D",color:"#fff",borderRadius:20,padding:"1px 7px",fontSize:10,fontWeight:600}}>📌 중요</span>}
                        <span style={{fontSize:10,padding:"1px 8px",borderRadius:20,fontWeight:600,
                          background:n.type==="patch"?"#534AB722":n.type==="notice"?"#A32D2D22":"#E4A80022",
                          color:n.type==="patch"?"#534AB7":n.type==="notice"?"#A32D2D":"#E4A800"}}>
                          {n.type==="patch"?"🔧 패치노트":n.type==="notice"?"📢 공지":"🎉 이벤트"}
                        </span>
                        <span style={{fontSize:10,color:T.sub,marginLeft:"auto"}}>{n.date}</span>
                      </div>
                      <div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:2}}>{n.title}</div>
                      <div style={{fontSize:11,color:T.sub}}>{n.summary}</div>
                    </div>
                    {openNoticeId===n.id&&(
                      <div style={{background:T.card,border:`0.5px solid #534AB733`,borderRadius:"0 0 10px 10px",
                        padding:"14px 16px",fontSize:12,color:T.text,lineHeight:1.9,whiteSpace:"pre-wrap"}}>
                        {n.content}
                      </div>
                    )}
                  </div>
                ))}
                {filteredStatic.length===0&&filteredDb.length===0&&(
                  <div style={{textAlign:"center",padding:"32px 0",color:T.sub}}>게시글이 없습니다.</div>
                )}
            </>);
          })()}
        </Modal>
      )}

      {showAdminInq&&isAdmin&&(
        <Modal T={T} title="📬 건의함 관리" onClose={()=>{setShowAdminInq(false);setInqOpen(null);setInqReply("");}}>
          {allInquiries.length===0&&(
            <div style={{textAlign:"center",padding:"32px 0",color:T.sub}}>접수된 건의가 없습니다</div>
          )}
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {allInquiries.map(inq=>(
              <div key={inq.id} style={{background:T.bg,borderRadius:10,border:`0.5px solid ${T.border}`,overflow:"hidden"}}>
                <div onClick={()=>setInqOpen(inqOpen===inq.id?null:inq.id)}
                  style={{padding:"10px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:8}}>
                  <span style={{padding:"2px 8px",borderRadius:20,fontSize:10,fontWeight:600,
                    background:inq.status==="closed"?"#EAF3DE":"#FCEBEB",
                    color:inq.status==="closed"?"#3B6D11":"#A32D2D",flexShrink:0}}>
                    {inq.status==="closed"?"답변완료":"미답변"}
                  </span>
                  <span style={{fontSize:11,color:T.sub,flexShrink:0}}>{inq.email?.split("@")[0]||"익명"}</span>
                  <span style={{fontSize:12,fontWeight:500,color:T.text,flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{inq.title}</span>
                  <span style={{fontSize:10,color:T.sub,flexShrink:0}}>{inq.createdAt?.toDate?.()?.toLocaleDateString("ko-KR")||""}</span>
                  <span style={{fontSize:11,color:T.sub}}>{inqOpen===inq.id?"▲":"▼"}</span>
                </div>
                {inqOpen===inq.id&&(
                  <div style={{padding:"0 12px 12px",borderTop:`0.5px solid ${T.border}`}}>
                    {/* 유저 이메일 */}
                    <div style={{fontSize:11,color:T.sub,marginTop:8,marginBottom:6}}>작성자: {inq.email||"알 수 없음"}</div>
                    {/* 내용 */}
                    <div style={{fontSize:13,color:T.text,lineHeight:1.7,whiteSpace:"pre-wrap",background:T.card,borderRadius:8,padding:"10px 12px",marginBottom:10}}>{inq.content}</div>
                    {/* 기존 답변 */}
                    {inq.adminReply&&(
                      <div style={{background:dark?"#1A1A2E":"#F5F3FF",borderRadius:8,padding:"10px 12px",marginBottom:10,border:`0.5px solid #534AB744`}}>
                        <div style={{fontSize:10,fontWeight:600,color:"#534AB7",marginBottom:4}}>💬 내 답변</div>
                        <div style={{fontSize:13,color:T.text,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{inq.adminReply}</div>
                      </div>
                    )}
                    {/* 답글 입력 */}
                    <textarea value={inqReply} onChange={e=>setInqReply(e.target.value)}
                      placeholder="답변을 입력하세요..." rows={3}
                      style={{...IS,resize:"vertical",marginBottom:8}}/>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={async()=>{
                        if(!inqReply.trim()) return;
                        await replyInquiry(inq.id, inqReply.trim());
                        setInqReply(""); setInqOpen(null);
                        showToast("답변이 등록됐습니다!", "✅");
                      }} style={{flex:2,background:"#534AB7",color:"#fff",border:"none",borderRadius:8,padding:"8px 0",fontSize:12,fontWeight:500,cursor:"pointer"}}>
                        💬 답변 등록
                      </button>
                      <button onClick={async()=>{
                        if(!window.confirm("삭제할까요?")) return;
                        await deleteInquiry(inq.id);
                        setInqOpen(null);
                      }} style={{flex:1,background:"transparent",border:"0.5px solid #A32D2D",color:"#A32D2D",borderRadius:8,padding:"8px 0",fontSize:12,cursor:"pointer"}}>
                        🗑️ 삭제
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}

      {noticeForm&&isAdmin&&(
        <Modal T={T} title={editNoticeId?"✏️ 공지 수정":"✏️ 새 게시글 작성"} onClose={()=>{setNoticeForm(false);setEditNoticeId(null);}}>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {/* 카테고리 */}
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:11,color:T.sub}}>카테고리</label>
              <div style={{display:"flex",gap:6}}>
                {[["patch","🔧 패치노트"],["notice","📢 공지"],["event","🎉 이벤트"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setNCategory(v)}
                    style={{flex:1,padding:"8px 0",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",
                      border:`1.5px solid ${nCategory===v?"#534AB7":T.border}`,
                      background:nCategory===v?"#534AB7":"transparent",
                      color:nCategory===v?"#fff":T.sub}}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {/* 제목 */}
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:11,color:T.sub}}>제목 *</label>
              <input type="text" value={nTitle} onChange={e=>setNTitle(e.target.value)}
                placeholder="게시글 제목" style={IS}/>
            </div>
            {/* 요약 */}
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:11,color:T.sub}}>요약 (목록에 표시, 미입력 시 본문 앞부분 자동)</label>
              <input type="text" value={nSummary} onChange={e=>setNSummary(e.target.value)}
                placeholder="한 줄 요약..." style={IS}/>
            </div>
            {/* 썸네일 */}
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:11,color:T.sub}}>썸네일 이미지 (선택)</label>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",
                background:T.bg,border:`0.5px dashed ${T.border}`,borderRadius:8,padding:"10px 14px",fontSize:11,color:T.sub}}>
                🖼️ {nThumbFile?nThumbFile.name:nThumb?"현재 이미지 있음":"이미지 파일 선택"}
                <input type="file" accept="image/*" style={{display:"none"}}
                  onChange={e=>{setNThumbFile(e.target.files?.[0]||null);}}/>
              </label>
              {(nThumb||nThumbFile)&&(
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  {nThumb&&!nThumbFile&&<img src={nThumb} alt="썸네일" style={{height:40,borderRadius:6,border:`0.5px solid ${T.border}`}}/>}
                  <button onClick={()=>{setNThumb("");setNThumbFile(null);}}
                    style={{background:"transparent",border:"none",color:"#A32D2D",fontSize:12,cursor:"pointer"}}>✕ 제거</button>
                </div>
              )}
            </div>
            {/* 본문 */}
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:11,color:T.sub}}>본문 * (마크다운: ## 제목, **굵게**, - 목록)</label>
              <textarea value={nContent} onChange={e=>setNContent(e.target.value)}
                placeholder={"## 제목\n\n내용을 입력하세요.\n\n- 항목 1\n- 항목 2"}
                rows={14} style={{...IS,resize:"vertical",lineHeight:1.7,fontFamily:"monospace",fontSize:12}}/>
            </div>
            {/* 본문 미리보기 */}
            {nContent&&(
              <div style={{background:T.bg,border:`0.5px solid ${T.border}`,borderRadius:8,padding:"12px 14px",maxHeight:200,overflow:"auto"}}>
                <div style={{fontSize:10,color:T.sub,marginBottom:6}}>📄 미리보기</div>
                <div style={{fontSize:12,color:T.text,lineHeight:1.8,whiteSpace:"pre-wrap"}}>{nContent}</div>
              </div>
            )}
            {/* 옵션 */}
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <input type="checkbox" id="pinned" checked={nPinned} onChange={e=>setNPinned(e.target.checked)}
                style={{width:16,height:16,accentColor:"#534AB7"}}/>
              <label htmlFor="pinned" style={{fontSize:13,color:T.text,cursor:"pointer"}}>📌 상단 고정 (중요 공지)</label>
            </div>
            {/* 버튼 */}
            <button onClick={handleSaveNotice}
              style={{background:"#534AB7",color:"#fff",border:"none",borderRadius:10,padding:"13px 0",fontSize:14,fontWeight:700,cursor:"pointer"}}>
              {editNoticeId?"✅ 수정 완료":"📝 게시글 등록"}
            </button>
          </div>
        </Modal>
      )}

      {presetModalOpen&&presetWidgetCharNick&&(
        <Modal T={T} title="⭐ 프리셋 설정" onClose={()=>setPresetModalOpen(false)} wide>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:12,color:T.sub,marginBottom:4}}>캐릭터</div>
            <div style={{fontSize:14,fontWeight:600,color:T.text}}>{presetWidgetCharNick}</div>
          </div>
          <div style={{display:"flex",gap:6,marginBottom:14}}>
            {["preset1","preset2","preset3"].map((slotKey,i)=>(
              <button key={slotKey} onClick={()=>loadPresetSlotToEditor(slotKey)}
                style={{flex:1,padding:"9px 0",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",
                  border:`1.5px solid ${presetEditSlot===slotKey?"#E4A800":T.border}`,
                  background:presetEditSlot===slotKey?"#E4A800":"transparent",
                  color:presetEditSlot===slotKey?"#fff":T.sub}}>
                프리셋 {i+1}
              </button>
            ))}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:12}}>
            <label style={{fontSize:11,color:T.sub}}>프리셋 이름</label>
            <input type="text" value={presetNameInput} onChange={e=>setPresetNameInput(e.target.value)}
              placeholder="예) 주말 데일리" style={IS}/>
          </div>
          <BossSection T={T} isMobile={isMobile}
            checkedBosses={pmCheckedBosses} setCheckedBosses={setPmCheckedBosses}
            bossParty={pmBossParty} setBossParty={setPmBossParty}
            bossPartyMembers={pmBossPartyMembers} setBossPartyMembers={setPmBossPartyMembers}
            bossDiffs={pmBossDiffs} setBossDiffs={setPmBossDiffs}
            records={[]} selChar={{nickname:presetWidgetCharNick}} incDate={today()} isEditMode={true}
            onLimitToast={(msg)=>showToast(msg,"⚠️")}/>
          <button onClick={handleSavePresetSlot}
            style={{width:"100%",background:"#E4A800",color:"#fff",border:"none",borderRadius:8,
              padding:12,fontSize:14,fontWeight:600,cursor:"pointer",marginTop:8}}>
            💾 프리셋 저장
          </button>
        </Modal>
      )}

      {/* ── 공지관리 모달 ── */}
      {showNoticeManager&&isAdmin&&(
        <Modal T={T} title="📋 공지 관리" onClose={()=>{setShowNoticeManager(false);setNoticeChecked({});}}>
          {/* 일괄 변경 툴바 */}
          <div style={{background:T.bg,borderRadius:10,border:`0.5px solid ${T.border}`,padding:"10px 14px",marginBottom:12}}>
            <div style={{fontSize:11,color:T.sub,marginBottom:8}}>
              선택됨: {Object.values(noticeChecked).filter(Boolean).length}개
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              <select value={bulkCategory} onChange={e=>setBulkCategory(e.target.value)} style={{...IS,flex:1}}>
                {NOTICE_CATEGORIES.map(c=>(
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <button onClick={handleBulkCategory}
                style={{background:"#534AB7",color:"#fff",border:"none",borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:600,cursor:"pointer",flexShrink:0}}>
                일괄 변경
              </button>
              <button onClick={()=>setNoticeChecked(Object.fromEntries(sortNotices(notices).map(n=>[n.id,true])))}
                style={{background:"transparent",border:`0.5px solid ${T.border}`,color:T.sub,borderRadius:8,padding:"8px 10px",fontSize:11,cursor:"pointer"}}>
                전체선택
              </button>
              <button onClick={()=>setNoticeChecked({})}
                style={{background:"transparent",border:`0.5px solid ${T.border}`,color:T.sub,borderRadius:8,padding:"8px 10px",fontSize:11,cursor:"pointer"}}>
                선택해제
              </button>
            </div>
          </div>

          {/* 공지 목록 */}
          {sortNotices(notices).length===0?(
            <div style={{textAlign:"center",padding:"32px 0",color:T.sub}}>등록된 공지가 없습니다</div>
          ):(
            sortNotices(notices).map(n=>{
              const cat = getCategoryInfo(n.category||"notice");
              const checked = !!noticeChecked[n.id];
              const isEditing = editingNotice?.id === n.id;
              return (
                <div key={n.id} style={{marginBottom:8,border:`1px solid ${checked?"#534AB7":T.border}`,borderRadius:10,overflow:"hidden"}}>
                  {/* 헤더 행 */}
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",background:T.bg,cursor:"pointer"}}
                    onClick={()=>setNoticeChecked(p=>({...p,[n.id]:!p[n.id]}))}>
                    <div style={{width:16,height:16,borderRadius:4,flexShrink:0,
                      border:`1.5px solid ${checked?"#534AB7":T.sub}`,
                      background:checked?"#534AB7":"transparent",
                      display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {checked&&<span style={{color:"#fff",fontSize:10}}>✓</span>}
                    </div>
                    <span style={{fontSize:10,padding:"2px 8px",borderRadius:20,fontWeight:600,
                      background:cat.bg,color:cat.color,flexShrink:0}}>{cat.label}</span>
                    {n.pinned&&<span style={{fontSize:10,background:"#A32D2D",color:"#fff",borderRadius:20,padding:"1px 6px",flexShrink:0}}>📌</span>}
                    <span style={{fontSize:12,color:T.text,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {cleanTitle(n.title)}
                    </span>
                    <span style={{fontSize:10,color:T.sub,flexShrink:0}}>{n.createdAt?.toDate?.()?.toLocaleDateString("ko-KR")||""}</span>
                  </div>
                  {/* 액션 버튼 */}
                  <div style={{display:"flex",gap:6,padding:"6px 12px",background:T.card,borderTop:`0.5px solid ${T.border}`}}>
                    <button onClick={()=>openNoticeEdit(n)}
                      style={{background:"#534AB7",color:"#fff",border:"none",borderRadius:6,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                      ✏️ 전면수정
                    </button>
                    <button onClick={()=>setEditingNotice(isEditing?null:{...n})}
                      style={{background:"transparent",border:`0.5px solid ${T.border}`,color:T.sub,borderRadius:6,padding:"5px 12px",fontSize:11,cursor:"pointer"}}>
                      {isEditing?"▲ 닫기":"🔧 빠른수정"}
                    </button>
                    <button onClick={()=>handleDeleteNotice(n.id)}
                      style={{background:"transparent",border:"0.5px solid #A32D2D",color:"#A32D2D",borderRadius:6,padding:"5px 12px",fontSize:11,cursor:"pointer",marginLeft:"auto"}}>
                      🗑️ 삭제
                    </button>
                  </div>
                  {/* 빠른수정 패널 */}
                  {isEditing&&(
                    <div style={{padding:"12px",background:T.prevBg,borderTop:`0.5px solid ${T.border}`,display:"flex",flexDirection:"column",gap:8}}>
                      <div style={{display:"flex",gap:6}}>
                        <select value={editingNotice.category||"notice"}
                          onChange={e=>setEditingNotice(p=>({...p,category:e.target.value}))}
                          style={{...IS,flex:1}}>
                          {NOTICE_CATEGORIES.map(c=>(
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                        <div style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}
                          onClick={()=>setEditingNotice(p=>({...p,pinned:!p.pinned}))}>
                          <div style={{width:14,height:14,borderRadius:3,border:`1.5px solid ${editingNotice.pinned?"#534AB7":T.sub}`,
                            background:editingNotice.pinned?"#534AB7":"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                            {editingNotice.pinned&&<span style={{color:"#fff",fontSize:9}}>✓</span>}
                          </div>
                          <span style={{fontSize:11,color:T.sub}}>고정</span>
                        </div>
                      </div>
                      <input type="text" value={editingNotice.title||""}
                        onChange={e=>setEditingNotice(p=>({...p,title:e.target.value}))}
                        placeholder="제목" style={IS}/>
                      <input type="text" value={editingNotice.summary||""}
                        onChange={e=>setEditingNotice(p=>({...p,summary:e.target.value}))}
                        placeholder="요약 (선택)" style={IS}/>
                      <button onClick={async()=>{
                        try {
                          await updateNotice(editingNotice.id, editingNotice.title, editingNotice.content||"", editingNotice.pinned||false, editingNotice);
                          setEditingNotice(null);
                          showToast("수정 완료!","✅");
                        } catch(e) { alert("오류: "+e.message); }
                      }} style={{background:"#3B6D11",color:"#fff",border:"none",borderRadius:8,padding:"8px 0",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                        💾 저장
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </Modal>
      )}

      {/* ── 후원 팝업 ── */}
      {showDonation&&(
        <Modal T={T} title="☕ 개발자 후원하기" onClose={()=>{setShowDonation(false);setGiftFile(null);setGiftNote("");}}>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{fontSize:11,color:T.sub,lineHeight:1.8,textAlign:"center"}}>
              메이플 파밍 트래커는 광고 없이 운영되는 <b>완전 무료</b> 서비스입니다.<br/>
              후원은 서버 유지 및 기능 개선에 큰 힘이 됩니다. 감사합니다! 🙏
            </div>
            {/* 카카오페이 */}
            <a href={DONATION_INFO.kakaopay.url} target="_blank" rel="noopener noreferrer"
              style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                background:"#FEE500",color:"#3A1D1D",borderRadius:10,
                padding:"13px 16px",fontSize:14,fontWeight:700,textDecoration:"none",
                boxShadow:"0 2px 8px #FEE50044"}}>
              {DONATION_INFO.kakaopay.label}
            </a>
            <div style={{fontSize:10,color:T.sub,textAlign:"center",marginTop:-6}}>{DONATION_INFO.kakaopay.note}</div>
            {/* 계좌이체 */}
            <div style={{background:T.bg,borderRadius:10,border:`0.5px solid ${T.border}`,padding:"10px 14px"}}>
              <div style={{fontSize:11,color:T.sub,marginBottom:4}}>{DONATION_INFO.account.label}</div>
              <div style={{fontSize:13,color:T.text,fontWeight:600,userSelect:"all",letterSpacing:1}}>
                {DONATION_INFO.account.bank} {DONATION_INFO.account.number} {DONATION_INFO.account.holder} ({DONATION_INFO.account.type})
              </div>
              <div style={{fontSize:10,color:T.sub,marginTop:2}}>{DONATION_INFO.account.copyHint}</div>
            </div>
            {/* 구분선 */}
            <div style={{borderTop:`0.5px solid ${T.border}`}}/>
            {/* 기프티콘 */}
            <div style={{fontSize:12,fontWeight:600,color:T.text}}>{DONATION_INFO.gift.label}</div>
            <div style={{fontSize:10,color:T.sub,lineHeight:1.7,marginTop:-6}}>
              {DONATION_INFO.gift.description}<br/>
              <span style={{color:"#534AB7",fontWeight:600}}>{DONATION_INFO.gift.adminOnlyNote}</span>
            </div>
            <input type="text" value={giftNote} onChange={e=>setGiftNote(e.target.value)}
              placeholder="메시지 (선택)" style={IS}/>
            <div style={{position:"relative"}}>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",
                background:T.bg,border:`0.5px dashed ${giftFile?"#534AB7":T.border}`,borderRadius:8,
                padding:"12px 14px",fontSize:12,color:giftFile?"#534AB7":T.sub,
                fontWeight:giftFile?600:400}}>
                📎 {giftFile?`${giftFile.name} ✓`:"기프티콘 QR/바코드 사진 첨부"}
                <input type="file" accept="image/*" style={{display:"none"}}
                  onChange={e=>setGiftFile(e.target.files?.[0]||null)}/>
              </label>
              {giftFile&&<button onClick={()=>setGiftFile(null)}
                style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",
                  background:"transparent",border:"none",color:"#A32D2D",cursor:"pointer",fontSize:16}}>✕</button>}
            </div>
            {giftFile&&(
              <img src={URL.createObjectURL(giftFile)} alt="미리보기"
                style={{width:"100%",borderRadius:8,border:`0.5px solid ${T.border}`,maxHeight:200,objectFit:"contain"}}/>
            )}
            <button onClick={handleGiftSubmit} disabled={giftSending||!giftFile}
              style={{background:giftFile?"#E4A800":"#888",color:"#fff",border:"none",
                borderRadius:10,padding:"13px 0",fontSize:13,fontWeight:700,cursor:"pointer",
                opacity:giftSending?0.6:1}}>
              {giftSending?"전송 중...":"☕ 기프티콘 후원 전송하기"}
            </button>
          </div>
        </Modal>
      )}

      {/* ── 관리자 후원 내역 ── */}
      {showGifts&&isAdmin&&(
        <Modal T={T} title="☕ 후원 내역" onClose={()=>setShowGifts(false)}>
          {gifts.length===0?(
            <div style={{textAlign:"center",padding:"32px 0",color:T.sub}}>아직 후원 내역이 없어요</div>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {gifts.map((g,i)=>(
                <div key={i} style={{background:T.bg,borderRadius:10,border:`0.5px solid ${T.border}`,padding:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.sub,marginBottom:6}}>
                    <span>{g.email||g.uid?.slice(0,8)}</span>
                    <span>{g.createdAt?.toDate?.()?.toLocaleDateString("ko-KR")||""}</span>
                  </div>
                  {g.note&&<div style={{fontSize:12,color:T.text,marginBottom:8}}>💬 {g.note}</div>}
                  {g.fileUrl&&(
                    <a href={g.fileUrl} target="_blank" rel="noopener noreferrer">
                      <img src={g.fileUrl} alt="기프티콘" style={{width:"100%",borderRadius:8,border:`0.5px solid ${T.border}`,maxHeight:200,objectFit:"contain"}}/>
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {/* ── 새 공지 팝업 ── */}
      {noticePopup&&(
        <div style={{position:"fixed",inset:0,background:"#00000088",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:T.card,borderRadius:16,border:`1px solid #534AB766`,padding:24,maxWidth:400,width:"100%",boxShadow:"0 8px 32px #00000066"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:18}}>📢</span>
                <span style={{fontSize:13,fontWeight:700,color:"#534AB7"}}>새 공지사항</span>
              </div>
              <button onClick={()=>{
                if(noPopupCheck) localStorage.setItem("lastSeenNoticeId", noticePopup.id);
                setNoticePopup(null); setNoPopupCheck(false);
              }} style={{background:"transparent",border:"none",color:T.sub,fontSize:18,cursor:"pointer",lineHeight:1}}>✕</button>
            </div>
            {/* 공지 내용 */}
            <div style={{marginBottom:16}}>
              <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:8}}>
                <span style={{padding:"2px 10px",borderRadius:20,fontSize:11,fontWeight:600,
                  background:noticePopup.type==="patch"?"#534AB722":noticePopup.type==="event"?"#E4A80022":"#A32D2D22",
                  color:noticePopup.type==="patch"?"#534AB7":noticePopup.type==="event"?"#E4A800":"#e05555"}}>
                  {noticePopup.type==="patch"?"🔧 패치노트":noticePopup.type==="event"?"🎉 이벤트":"📢 공지"}
                </span>
                <span style={{fontSize:11,color:T.sub}}>{noticePopup.date}</span>
              </div>
              <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:6}}>{noticePopup.title}</div>
              <div style={{fontSize:12,color:T.sub,lineHeight:1.7}}>{noticePopup.summary}</div>
            </div>
            {/* 버튼 */}
            <a href="/notice" target="_blank" rel="noopener noreferrer"
              onClick={()=>{
                if(noPopupCheck) localStorage.setItem("lastSeenNoticeId", noticePopup.id);
                setNoticePopup(null); setNoPopupCheck(false);
              }}
              style={{display:"block",background:"#534AB7",color:"#fff",borderRadius:10,
                padding:"12px 0",fontSize:13,fontWeight:700,textAlign:"center",
                textDecoration:"none",marginBottom:10}}>
              공지사항 전체 보기 →
            </a>
            {/* 다음 공지까지 보지 않기 */}
            <div style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",justifyContent:"center"}}
              onClick={()=>setNoPopupCheck(p=>!p)}>
              <div style={{width:16,height:16,borderRadius:4,border:`1.5px solid ${noPopupCheck?"#534AB7":T.sub}`,
                background:noPopupCheck?"#534AB7":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {noPopupCheck&&<span style={{color:"#fff",fontSize:10}}>✓</span>}
              </div>
              <span style={{fontSize:11,color:T.sub}}>다음 공지까지 보지 않기</span>
            </div>
          </div>
        </div>
      )}

      {detailRec&&(
        <Modal T={T} title={editMode?(detailRec.kind==="income"?"✏️ 수익 수정":"✏️ 지출 수정"):(detailRec.kind==="income"?"💰 수익 상세":"💸 지출 상세")} onClose={()=>{setDetailRec(null);setEditMode(false);setCharMoveMode(false);setCharMoveTarget("");}}>
          {!editMode?(
            <div>
              <div style={{background:T.charBg,borderRadius:10,padding:12,marginBottom:12}}>
                {[["캐릭터",detailRec.nickname||detailRec.job||"공통"],["날짜",detailRec.date],["유형",EXP_TYPE_MAP[detailRec.type]||detailRec.type],
                  ...(detailRec.type==="boss"&&isPatchTransitionDate(detailRec.date)&&phaseBadgeInfo(detailRec.pricePhase)
                    ?[["패치 시점", phaseBadgeInfo(detailRec.pricePhase).label + (detailRec.pricePhase==="mixed" ? " (보스마다 다름 — 아래 목록 참고)" : "")]]
                    :[]),
                  ...(detailRec.note?[["메모",detailRec.note]]:[])].map(([l,v])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                    <span style={{fontSize:12,color:T.sub}}>{l}</span><span style={{fontSize:13,fontWeight:500,color:T.text}}>{v}</span>
                  </div>
                ))}
              </div>
              {detailRec.kind==="income"?(
                <div>
                  {detailRec.bossList?.length>0&&(
                    <div style={{marginBottom:10}}>
                      <div style={{fontSize:12,color:T.sub,marginBottom:4}}>보스 결정석 내역</div>
                      {detailRec.bossList.map((b,i)=>{
                        const itemPb = b.phase ? phaseBadgeInfo(b.phase) : null;
                        return (
                          <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,padding:"3px 0"}}>
                            <span style={{color:T.text,display:"flex",alignItems:"center",gap:6}}>
                              {b.name}{b.party>1?` (${b.party}인)`:""}
                              {itemPb&&(
                                <span style={{fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:10,whiteSpace:"nowrap",
                                  background:itemPb.bg,color:itemPb.fg}}>
                                  {itemPb.short}
                                </span>
                              )}
                            </span>
                            <span style={{color:"#854F0B",fontWeight:500}}>{fmt(b.crystal)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {[["솔에르다 기운",detailRec.sol0,"개"],["솔조각 (이벤트)",detailRec.sol1,"개"],["솔조각 (일반)",detailRec.sol2,"개"],["솔조각 (사냥)",detailRec.sol3,"개"],["솔조각 (교환불가)",detailRec.sol4,"개"],["코어 잼스톤",detailRec.core,"개"],["메소 획득량",detailRec.meso,"메소"],["기타 수익",detailRec.etc,"메소"],["마일리지",detailRec.mile,""],["사냥 시간",detailRec.mins,"분"]].filter(([,v])=>v>0).map(([l,v,unit])=>(
                    <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`0.5px solid ${T.border}`,fontSize:13}}>
                      <span style={{color:T.sub}}>{l}</span>
                      <span style={{color:T.text}}>{unit==="메소"?fmt(v):(v||0).toLocaleString()}{unit==="메소"?"":unit}</span>
                    </div>
                  ))}
                  <div style={{background:T.prevBg,borderRadius:8,padding:"10px 12px",marginTop:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}><span style={{color:T.sub}}>솔 에르다 환산가</span><span style={{color:"#0F6E56",fontWeight:500}}>{fmt(detailRec.totalSol||0)}</span></div>
                    {(detailRec.dropIncome||0)>0&&(
                      <div style={{fontSize:12,marginBottom:4}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}><span style={{color:T.sub}}>드롭 아이템</span><span style={{color:"#534AB7",fontWeight:500}}>{fmt(detailRec.dropIncome)}</span></div>
                        {DROP_ITEMS.filter(d=>(detailRec.dropQty?.[d.key]||0)>0).map(d=>(
                          <div key={d.key} style={{display:"flex",justifyContent:"space-between",paddingLeft:8,fontSize:11,color:T.sub}}>
                            <span>{d.name} {detailRec.dropQty[d.key]}개</span>
                            <span>{fmt((detailRec.dropQty[d.key]||0)*d.price)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(detailRec.bossIncome||0)>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}><span style={{color:T.sub}}>보스 결정석</span><span style={{color:"#854F0B",fontWeight:500}}>{fmt(detailRec.bossIncome)}</span></div>}
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:14,fontWeight:500}}><span style={{color:"#534AB7"}}>총 수익</span><span style={{color:"#534AB7"}}>{fmt(calcIncome(detailRec,favorites))}</span></div>
                  </div>
                  {(()=>{/* 💎 관련 보스 드롭 인벤토리 */
                  const relatedItems = inventory.filter(it=>
                    !it.sold &&
                    it.nickname===detailRec.nickname &&
                    it.fromBoss
                  );
                  if(!relatedItems.length) return null;
                  return (
                    <div style={{marginTop:10,background:T.card,borderRadius:10,border:`0.5px solid ${T.border}`,padding:12}}>
                      <div style={{fontSize:12,fontWeight:600,color:"#534AB7",marginBottom:8}}>💎 보관함 — 보스 드롭 ({relatedItems.length}개)</div>
                      {relatedItems.map(it=>(
                        <div key={it.id} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 8px",
                          borderRadius:8,background:T.bg,border:`0.5px solid ${T.border}`,marginBottom:4}}>
                          <span style={{fontSize:12,flex:1,color:T.text}}>{it.name}</span>
                          {it.ringLevel&&<span style={{fontSize:10,color:"#534AB7",background:"#534AB720",padding:"1px 6px",borderRadius:8}}>{it.ringLevel}레벨</span>}
                          {it.ringName&&<span style={{fontSize:10,color:T.sub}}>{it.ringName}</span>}
                          <button onClick={()=>{
                            const newRingLevel = prompt(`${it.name} 반지 레벨 수정 (현재: ${it.ringLevel||"없음"})\n1/2/3/4 입력`, it.ringLevel||"");
                            if(newRingLevel===null) return;
                            const newRingName = prompt(`링 종류 수정 (현재: ${it.ringName||"없음"})`, it.ringName||"");
                            if(newRingName===null) return;
                            updateInventoryItem(user.uid, it.id, {
                              ringLevel: newRingLevel||null,
                              ringName: newRingName||null,
                            });
                            showToast("수정 완료!", "✅");
                          }} style={{background:"transparent",border:`0.5px solid ${T.border}`,
                            color:T.sub,borderRadius:6,padding:"2px 7px",fontSize:10,cursor:"pointer"}}>
                            ✏️
                          </button>
                          <button onClick={()=>{
                            if(!window.confirm(`"${it.name}" 삭제할까요?`)) return;
                            deleteInventoryItem(user.uid, it.id);
                          }} style={{background:"transparent",border:"none",color:"#A32D2D",fontSize:11,cursor:"pointer"}}>🗑️</button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                </div>
              ):(
                <div>
                  {/* 환전 기록: 3칸 모두 표시 */}
                  {detailRec.type==="exchange"&&(detailRec.exchMp>0||detailRec.exchRate>0)?(
                    <div style={{display:"flex",flexDirection:"column",gap:4}}>
                      {/* 거래 메소 */}
                      <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`0.5px solid ${T.border}`,fontSize:13}}>
                        <span style={{color:T.sub}}>{detailRec.exchDir==="buy"?"💰 획득 메소":"💸 지출 메소"}</span>
                        <span style={{color:detailRec.exchDir==="buy"?"#3B6D11":"#A32D2D",fontWeight:700}}>
                          {detailRec.exchDir==="buy"?"+":"-"}{fmt(detailRec.meso||0)}
                        </span>
                      </div>
                      {/* 실지불/수령 메포 */}
                      <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`0.5px solid ${T.border}`,fontSize:13}}>
                        <span style={{color:T.sub}}>{detailRec.exchDir==="buy"?"💸 지불 메포":"💰 수령 메포"}</span>
                        <span style={{color:detailRec.exchDir==="buy"?"#A32D2D":"#3B6D11",fontWeight:700}}>
                          {detailRec.exchDir==="buy"?"-":"+"}{(detailRec.exchMp||detailRec.cash||detailRec.mile||0).toLocaleString()} MP
                        </span>
                      </div>
                      {/* 실거래 단가 */}
                      {(detailRec.exchRealRate||detailRec.exchRate)>0&&(
                        <div style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:`0.5px solid ${T.border}`,fontSize:12}}>
                          <span style={{color:T.sub}}>📊 실거래 단가</span>
                          <span style={{color:"#534AB7",fontWeight:700}}>
                            {Math.round(detailRec.exchRealRate||detailRec.exchRate||0).toLocaleString()} MP/억
                          </span>
                        </div>
                      )}
                      {/* 시장가 */}
                      {detailRec.exchMarket>0&&(
                        <div style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:`0.5px solid ${T.border}`,fontSize:12}}>
                          <span style={{color:T.sub}}>🏪 당시 시장가</span>
                          <span style={{color:T.sub}}>{Math.round(detailRec.exchMarket).toLocaleString()} MP/억</span>
                        </div>
                      )}
                      {/* 시장가 대비 손익 */}
                      {detailRec.exchMarket>0&&detailRec.exchRealRate>0&&(()=>{
                        const diff = detailRec.exchMarket - detailRec.exchRealRate;
                        return (
                          <div style={{display:"flex",justifyContent:"space-between",padding:"5px 0",fontSize:12}}>
                            <span style={{color:T.sub}}>시장가 대비</span>
                            <span style={{color:diff>0?"#3B6D11":"#A32D2D",fontWeight:700}}>
                              {diff>0?"▼ ":"▲ "}{Math.abs(Math.round(diff)).toLocaleString()} MP/억 {diff>0?"이득":"손해"}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  ):(
                    <div style={{display:"flex",flexDirection:"column",gap:4}}>
                      {[["지출 메소",detailRec.meso,"메소"],["넥슨 캐시",detailRec.cash,"원"],["마일리지",detailRec.mile,""]].filter(([,v])=>v>0).map(([l,v,unit])=>(
                        <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`0.5px solid ${T.border}`,fontSize:13}}>
                          <span style={{color:T.sub}}>{l}</span><span style={{color:T.text}}>{unit==="메소"?fmt(v):(v||0).toLocaleString()}{unit==="메소"?"":unit}</span>
                        </div>
                      ))}
                      {detailRec.type==="market"&&detailRec.mp>0&&(
                        <>
                          <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`0.5px solid ${T.border}`,fontSize:13}}>
                            <span style={{color:T.sub}}>메이플포인트</span>
                            <span style={{color:"#A32D2D"}}>−{(detailRec.mp||0).toLocaleString()} MP</span>
                          </div>
                          {detailRec.mktBuyMpPerHundredM>0&&(
                            <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:12}}>
                              <span style={{color:T.sub}}>억당 메포</span>
                              <span style={{color:"#534AB7",fontWeight:600}}>{detailRec.mktBuyMpPerHundredM.toLocaleString()} MP/억</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div style={{display:"flex",gap:8,marginTop:14}}>
                <button onClick={()=>setEditMode(true)} style={{flex:2,background:"#534AB7",color:"#fff",border:"none",borderRadius:8,padding:12,fontSize:14,fontWeight:500,cursor:"pointer"}}>✏️ 수정하기</button>
                <button onClick={()=>{setCharMoveMode(m=>!m);setCharMoveTarget("");}} style={{flex:1,background:"transparent",color:"#534AB7",border:"1px solid #534AB7",borderRadius:8,padding:12,fontSize:12,cursor:"pointer"}}>👤 이동</button>
                <button onClick={()=>handleDelete(detailRec.id)} style={{flex:1,background:"transparent",color:"#A32D2D",border:"1px solid #A32D2D",borderRadius:8,padding:12,fontSize:13,cursor:"pointer"}}>🗑️ 삭제</button>
              </div>
              {charMoveMode&&(
                <div style={{marginTop:10,background:T.card,border:`1px solid #534AB7`,borderRadius:10,padding:12}}>
                  <div style={{fontSize:12,color:T.sub,marginBottom:8}}>👤 다른 캐릭터로 이동</div>
                  <div style={{fontSize:11,color:T.sub,marginBottom:6}}>현재: <span style={{color:T.text,fontWeight:600}}>{detailRec.nickname}</span></div>
                  <select value={charMoveTarget} onChange={e=>setCharMoveTarget(e.target.value)}
                    style={{...IS,marginBottom:8}}>
                    <option value="">캐릭터 선택</option>
                    {favorites.filter(f=>f.nickname!==detailRec.nickname).map(f=>(
                      <option key={f.nickname} value={f.nickname}>{f.nickname}</option>
                    ))}
                  </select>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={handleCharMove} disabled={!charMoveTarget}
                      style={{flex:1,background:charMoveTarget?"#534AB7":"#888",color:"#fff",border:"none",
                        borderRadius:8,padding:"8px 0",fontSize:12,fontWeight:600,cursor:charMoveTarget?"pointer":"default"}}>
                      ✅ 이동 확인
                    </button>
                    <button onClick={()=>{setCharMoveMode(false);setCharMoveTarget("");}}
                      style={{flex:1,background:"transparent",border:`0.5px solid ${T.border}`,color:T.sub,
                        borderRadius:8,padding:"8px 0",fontSize:12,cursor:"pointer"}}>
                      취소
                    </button>
                  </div>
                </div>
              )}
            </div>
          ):(
            <div>
              <div style={{fontSize:12,color:T.sub,marginBottom:10}}>수정할 항목을 변경하고 저장하세요</div>
              <div style={{marginBottom:12}}>
                <DateNavPicker value={editDate} onChange={setEditDate} T={T} label="날짜"/>
              </div>
              {detailRec.kind==="income"?(
                <div>
                  <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:8}}><label style={{fontSize:11,color:T.sub}}>수익 유형</label>
                    <select value={editType} onChange={e=>setEditType(e.target.value)} style={IS}><option value="hunt">사냥/일퀘</option><option value="boss">보스</option><option value="event">이벤트/기타</option><option value="drop">물욕템</option><option value="epic">에픽던전주화</option></select>
                  </div>
                  {editType==="boss"&&<BossSection T={T} isMobile={isMobile} checkedBosses={editCheckedBosses} setCheckedBosses={setEditCheckedBosses} bossParty={editBossParty} setBossParty={setEditBossParty} bossPartyMembers={editBossPartyMembers} setBossPartyMembers={setEditBossPartyMembers} bossDiffs={editBossDiffs} setBossDiffs={setEditBossDiffs} records={records} selChar={detailRec?{nickname:detailRec.nickname}:null} incDate={editDate} excludeRecordId={detailRec?.id} isEditMode={true} originalDate={detailRec?.date} onLimitToast={(msg)=>showToast(msg,"⚠️")} bossPricePhaseMap={editBossPricePhaseMap} setBossPricePhaseMap={setEditBossPricePhaseMap}/>}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
                    <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>솔에르다 기운</label><input type="number" value={editSol0} onChange={e=>setEditSol0(e.target.value)} style={IS}/></div>
                    <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>솔조각 (이벤트)</label><input type="number" value={editSol1} onChange={e=>setEditSol1(e.target.value)} style={IS}/></div>
                    <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>솔조각 (일반)</label><input type="number" value={editSol2} onChange={e=>setEditSol2(e.target.value)} style={IS}/></div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                    <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>솔조각 (사냥)</label><input type="number" value={editSol3} onChange={e=>setEditSol3(e.target.value)} style={IS}/></div>
                    <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>솔조각 (교환불가)</label><input type="number" value={editSol4} onChange={e=>setEditSol4(e.target.value)} style={IS_NOEX}/></div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                    <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>메소 획득량</label><input type="number" value={editMeso} onChange={e=>setEditMeso(e.target.value)} style={IS}/></div>
                    <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>코어 잼스톤</label><input type="number" value={editCore} onChange={e=>setEditCore(e.target.value)} style={IS}/></div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                    <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>기타 수익</label><input type="number" value={editEtc} onChange={e=>setEditEtc(e.target.value)} style={IS}/></div>
                    <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>마일리지</label><input type="number" value={editMile} onChange={e=>setEditMile(e.target.value)} style={IS}/></div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:8}}><label style={{fontSize:11,color:T.sub}}>사냥 시간(분)</label><input type="number" value={editMins} onChange={e=>setEditMins(e.target.value)} style={IS}/></div>
                  <div style={{background:T.prevBg,borderRadius:8,padding:"10px 12px",marginBottom:12}}>
                    {editType==="boss"&&(()=>{
                      // "오늘"이 아니라 editDate + 보스별 선택된 패치 전/후 기준으로 계산해야 정확함
                      const editBossSum = Object.entries(editCheckedBosses).filter(([,v])=>v).reduce((s,[k])=>{const parts=k.split("_");const sec=parts[0];const diff=parts[parts.length-1];const bname=parts.slice(1,-1).join("_");const {crystal}=resolveBossPrice(editDate,sec,bname,diff,editBossPricePhaseMap,k);const p=editBossParty[k]||1;return crystal!=null?s+Math.floor(crystal/p):s;},0);
                      return (
                        <>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"3px 0"}}><span style={{color:T.sub}}>보스 결정석</span><span style={{color:"#854F0B",fontWeight:500}}>{fmt(editBossSum)}</span></div>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:500,padding:"3px 0"}}><span style={{color:"#534AB7"}}>수정 후 총 수익</span><span style={{color:"#534AB7"}}>{fmt(((+editSol1||0)+(+editSol2||0)+(+editSol3||0))*SOL_PRICE+(+editMeso||0)+(+editEtc||0)+editBossSum)}</span></div>
                        </>
                      );
                    })()}
                    {editType!=="boss"&&<div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:500,padding:"3px 0"}}><span style={{color:"#534AB7"}}>수정 후 총 수익</span><span style={{color:"#534AB7"}}>{fmt(((+editSol1||0)+(+editSol2||0)+(+editSol3||0))*SOL_PRICE+(+editMeso||0)+(+editEtc||0))}</span></div>}
                  </div>
                </div>
              ):(
                <div>
                  <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:8}}><label style={{fontSize:11,color:T.sub}}>지출 유형</label>
                    <select value={editExpType} onChange={e=>{setEditExpType(e.target.value);setEditSymbolForce("");setEditSymbolArea("");}} style={IS}>
                      <option value="cube">큐브 강화</option><option value="star">스타포스</option><option value="spell">주문서</option>
                      <option value="symbol">심볼업</option><option value="boss-entry">보스 입장권</option>
                      <option value="cash">캐시 아이템</option><option value="market">경매장 구매</option><option value="etc">기타</option>
                    </select>
                  </div>
                  {editExpType==="symbol"&&(()=>{
                    const areaList2 = editSymbolForce==="arcane"?ARCANE_AREAS:editSymbolForce==="authentic"?AUTHENTIC_AREAS:editSymbolForce==="grand"?GRAND_AUTHENTIC_AREAS:[];
                    const maxLv2    = editSymbolForce==="arcane"?20:11;
                    const cur2=parseInt(editSymCurLv)||0, tgt2=parseInt(editSymTgtLv)||0;
                    const symRes2 = (editSymbolForce&&editSymbolArea&&cur2>=1&&tgt2>cur2&&tgt2<=maxLv2)
                      ? calcSymbolCost(editSymbolForce,cur2,tgt2,editSymbolArea,editDate) : null;
                    return(
                    <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:8}}>
                      {/* 포스 + 지역 */}
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                        <div style={{display:"flex",flexDirection:"column",gap:3}}>
                          <label style={{fontSize:11,color:T.sub}}>포스 종류</label>
                          <select value={editSymbolForce} onChange={e=>{setEditSymbolForce(e.target.value);setEditSymbolArea("");setEditSymCurLv("");setEditSymTgtLv("");}} style={IS}>
                            <option value="">-- 선택 --</option>
                            <option value="arcane">아케인포스</option>
                            <option value="authentic">어센틱포스</option>
                            <option value="grand">그랜드 어센틱</option>
                          </select>
                        </div>
                        {editSymbolForce&&(
                          <div style={{display:"flex",flexDirection:"column",gap:3}}>
                            <label style={{fontSize:11,color:T.sub}}>지역</label>
                            <select value={editSymbolArea} onChange={e=>{setEditSymbolArea(e.target.value);setEditSymCurLv("");setEditSymTgtLv("");}} style={IS}>
                              <option value="">-- 지역 선택 --</option>
                              {areaList2.map(v=><option key={v} value={v}>{v}</option>)}
                            </select>
                          </div>
                        )}
                      </div>
                      {/* 레벨 드롭다운 */}
                      {editSymbolForce&&editSymbolArea&&(
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                          <div style={{display:"flex",flexDirection:"column",gap:3}}>
                            <label style={{fontSize:11,color:T.sub}}>현재 레벨</label>
                            <select value={editSymCurLv} onChange={e=>{const v=e.target.value;const newTgt=parseInt(editSymTgtLv)<=parseInt(v)?"":editSymTgtLv;setEditSymCurLv(v);setEditSymTgtLv(newTgt);}} style={{...IS,fontSize:13,fontWeight:500}}>
                              <option value="">선택</option>
                              {[...Array(maxLv2-1)].map((_,i)=><option key={i+1} value={i+1}>Lv.{i+1}</option>)}
                            </select>
                          </div>
                          <div style={{display:"flex",flexDirection:"column",gap:3}}>
                            <label style={{fontSize:11,color:T.sub}}>목표 레벨</label>
                            <select value={editSymTgtLv} onChange={e=>setEditSymTgtLv(e.target.value)}
                              style={{...IS,fontSize:13,fontWeight:500,
                                borderColor:editSymTgtLv&&parseInt(editSymTgtLv)===maxLv2?"#E4A800":undefined,
                                color:editSymTgtLv&&parseInt(editSymTgtLv)===maxLv2?"#E4A800":undefined}}>
                              <option value="">선택</option>
                              {[...Array(maxLv2)].map((_,i)=>i+1).filter(v=>v>(cur2||0)).map(v=>(
                                <option key={v} value={v}>{v===maxLv2?`★ Lv.${v}`:`Lv.${v}`}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                      {/* 계산 결과 */}
                      {symRes2&&symRes2.meso>0&&(
                        <div style={{background:dark?"#1e1e2e":"#f0efff",borderRadius:9,padding:"10px 12px",
                          border:`1px solid ${dark?"#534AB740":"#534AB730"}`}}>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                            <span style={{fontSize:12,color:T.sub}}>💰 필요 메소</span>
                            <span style={{fontSize:14,fontWeight:700,color:"#534AB7"}}>{fmt(symRes2.meso)}</span>
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
                            <span style={{fontSize:12,color:T.sub}}>🧩 필요 조각</span>
                            <span style={{fontSize:13,fontWeight:600,color:"#059669"}}>{symRes2.pieces.toLocaleString()}개</span>
                          </div>
                          <button onClick={()=>setEditExpMeso(String(symRes2.meso))}
                            style={{width:"100%",background:"#534AB7",color:"#fff",border:"none",borderRadius:7,
                              padding:"8px 0",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                            ↙ 지출 메소에 자동 입력
                          </button>
                        </div>
                      )}
                    </div>
                    );
                  })()}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                    <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>지출 메소</label><input type="number" value={editExpMeso} onChange={e=>setEditExpMeso(e.target.value)} style={IS}/></div>
                    <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:11,color:T.sub}}>넥슨 캐시(원)</label><input type="number" value={editExpCash} onChange={e=>setEditExpCash(e.target.value)} style={IS}/></div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:8}}><label style={{fontSize:11,color:T.sub}}>마일리지 사용</label><input type="number" value={editExpMile} onChange={e=>setEditExpMile(e.target.value)} style={IS}/></div>
                </div>
              )}
              <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:12}}><label style={{fontSize:11,color:T.sub}}>메모</label><input type="text" value={editNote} onChange={e=>setEditNote(e.target.value)} placeholder="메모..." style={IS}/></div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setEditMode(false)} style={{flex:1,background:"transparent",border:`0.5px solid ${T.border}`,borderRadius:8,padding:11,fontSize:13,color:T.sub,cursor:"pointer"}}>← 상세보기</button>
                <button onClick={handleUpdateRecord} disabled={saving} style={{flex:2,background:"#534AB7",color:"#fff",border:"none",borderRadius:8,padding:11,fontSize:13,fontWeight:500,cursor:"pointer"}}>{saving?"저장 중...":"💾 수정 저장"}</button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {showInfo&&(
        <Modal T={T} title="📊 캐릭터 INFO" onClose={()=>{setShowInfo(false);setInfoChar(null);setInfoError("");}} wide>
          {infoLoading&&<div style={{textAlign:"center",padding:"40px 0",color:T.sub}}>🔄 조회 중...</div>}
          {infoError&&<div style={{color:"#A32D2D",padding:"16px 0",textAlign:"center"}}>{infoError}</div>}
          {infoChar&&(
            <div>
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10,padding:"0 0 14px",borderBottom:`0.5px solid ${T.border}`,marginBottom:14,textAlign:"center"}}>
                {infoChar.image&&<CharAvatar src={infoChar.image} alt={infoChar.nickname} size={150} mode="full" bg={T.charBg}/>}
                <div style={{width:"100%"}}>
                  <div style={{fontSize:20,fontWeight:500,color:T.text,marginBottom:6}}>{infoChar.nickname}</div>
                  <div style={{display:"flex",justifyContent:"center",gap:6,flexWrap:"wrap"}}>
                    {[infoChar.job,`Lv.${infoChar.level}`,infoChar.world,`유니온 ${infoChar.unionLevel?.toLocaleString()}`,infoChar.unionGrade,`길드: ${infoChar.guild}`].map(v=>(
                      <span key={v} style={{background:T.charBg,borderRadius:20,padding:"3px 10px",fontSize:12,color:T.text}}>{v}</span>
                    ))}
                  </div>
                </div>
              </div>
              {infoChar.stats&&Object.keys(infoChar.stats).length>0&&(
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:12,fontWeight:500,color:"#534AB7",marginBottom:8}}>📈 주요 스탯</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:5}}>
                    {MAIN_STATS.filter(s=>infoChar.stats[s]).map(s=>(
                      <div key={s} style={{display:"flex",justifyContent:"space-between",padding:"5px 8px",background:T.prevBg,borderRadius:6,fontSize:12}}>
                        <span style={{color:T.sub}}>{s}</span>
                        <span style={{color:T.text,fontWeight:500}}>{Number(infoChar.stats[s]).toLocaleString()}{s.includes("데미지")||s.includes("무시")||s.includes("스탠스")||s.includes("최종")?"%":""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {infoChar.equipment&&Object.keys(infoChar.equipment).length>0&&(
                <div>
                  <div style={{fontSize:12,fontWeight:500,color:"#534AB7",marginBottom:8}}>🛡️ 장비 아이템</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr",gap:5}}>
                    {Object.entries(infoChar.equipment).map(([slot,item])=>(
                      <div key={slot} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:T.prevBg,borderRadius:8,border:item.grade?`0.5px solid ${potColor(item.grade)}44`:`0.5px solid ${T.border}`}}>
                        {item.icon&&<img src={item.icon} alt={item.name} style={{width:36,height:36,borderRadius:4,background:T.card,flexShrink:0}}/>}
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                            <span style={{fontSize:11,color:T.sub}}>{slot}</span>
                            {item.starforce>0&&<span style={{fontSize:10,color:"#F59E0B",fontWeight:500}}>★{item.starforce}</span>}
                            {item.grade&&<span style={{fontSize:10,color:potColor(item.grade),fontWeight:500}}>{item.grade}</span>}
                          </div>
                          <div style={{fontSize:12,fontWeight:500,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.name}</div>
                          {item.potential1&&<div style={{fontSize:11,color:potColor(item.grade)||T.sub,marginTop:2}}>{item.potential1}{item.potential2?" / "+item.potential2:""}{item.potential3?" / "+item.potential3:""}</div>}
                          {item.additional1&&<div style={{fontSize:11,color:"#10B981",marginTop:1}}>에잠: {item.additional1}{item.additional2?" / "+item.additional2:""}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>
      )}

      {isMobile?(
        <div style={{flex:1,display:"flex",flexDirection:"column"}}>
          {SumCards}
          <div style={{flex:1,padding:"10px 12px 70px",overflowY:"auto"}}>{tabContent[tab]}</div>
          <div style={{position:"fixed",bottom:0,left:0,right:0,background:T.card,borderTop:`0.5px solid ${T.border}`,display:"flex",zIndex:100,height:56}}>
            {TABS.map(([k,icon,label])=>(
              <button key={k} onClick={()=>setTab(k)} style={{flex:1,background:"none",border:"none",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:2,color:tab===k?"#534AB7":T.sub,fontSize:9,fontWeight:tab===k?500:400}}>
                <span style={{fontSize:16}}>{icon}</span><span>{label}</span>
              </button>
            ))}
          </div>
        </div>
      ):(
        <div style={{display:"flex",flex:1,maxWidth:1440,margin:"0 auto",width:"100%",padding:"0 16px",gap:16,marginTop:16}}>
          <div style={{width:200,flexShrink:0}}>
            <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,overflow:"hidden",position:"sticky",top:70}}>
              {TABS.map(([k,icon,label])=>(
                <button key={k} onClick={()=>setTab(k)} style={{width:"100%",padding:"12px 16px",background:tab===k?"#534AB7":T.card,color:tab===k?"#fff":T.sub,border:"none",borderBottom:`0.5px solid ${T.border}`,cursor:"pointer",textAlign:"left",fontSize:13,fontWeight:tab===k?500:400,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:16}}>{icon}</span><span>{label}</span>
                </button>
              ))}
            </div>
            {favorites.length>0&&(
              <div style={{background:T.card,borderRadius:12,border:`0.5px solid ${T.border}`,padding:12,marginTop:12}}>
                <div style={{fontSize:12,fontWeight:500,color:T.sub,marginBottom:8}}>⭐ 즐겨찾기</div>
                {favorites.map(char=>(
                  <div key={char.nickname} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:`0.5px solid ${T.border}`}}>
                    <div onClick={()=>selectChar(char)} style={{display:"flex",alignItems:"center",gap:8,flex:1,cursor:"pointer",minWidth:0}}>
                      {char.image&&<CharAvatar src={char.image} alt={char.nickname} size={34}/>}
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:500,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{char.nickname}</div>
                        <div style={{fontSize:10,color:T.sub}}>{char.job}</div>
                      </div>
                    </div>
                    <button onClick={()=>handleLoadInfo(char.nickname)} style={{background:"transparent",border:"none",cursor:"pointer",color:"#534AB7",fontSize:12,padding:"2px 4px",flexShrink:0}}>📊</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{flex:1,minWidth:0,paddingBottom:40}}>
            {SumCards}
            <div style={{marginTop:12}}>{tabContent[tab]}</div>
          </div>
          <div style={{width:260,flexShrink:0}}>
            <div style={{position:"sticky",top:70}}>
              {PresetWidget}
            </div>
          </div>
        </div>
      )}

      {/* ── 하단 광고 슬롯 ── */}
      <div style={{width:"100%",textAlign:"center",padding:"8px 0",background:T.bg,borderTop:`0.5px solid ${T.border}`}}>
        <ins className="adsbygoogle"
          style={{display:"block"}}
          data-ad-client="ca-pub-4725606679376877"
          data-ad-slot="auto"
          data-ad-format="auto"
          data-full-width-responsive="true"/>
      </div>

      {/* ── 푸터 ── */}
      <div style={{borderTop:`0.5px solid ${T.border}`,padding:"20px 24px",textAlign:"center",color:T.sub,fontSize:11,lineHeight:1.8,background:T.bg}}>
        <p style={{marginBottom:8,fontSize:10,color:dark?"#444466":"#aaaacc",lineHeight:1.7}}>
          본 서비스는 넥슨(NEXON Co., Ltd.)의 메이플스토리 게임을 기반으로 한 비공식 팬 제작 유틸리티입니다.<br/>
          메이플스토리 및 관련 콘텐츠의 저작권은 넥슨에 있으며, 본 사이트는 넥슨과 공식 제휴 관계가 아닙니다.
        </p>
        <p style={{marginBottom:8,fontSize:10,color:dark?"#444466":"#aaaacc",lineHeight:1.7}}>
          This service uses <b>NEXON Open API</b>. MapleStory® is a registered trademark of NEXON Korea Corporation.<br/>
          This is an unofficial fan-made utility and is not affiliated with or endorsed by NEXON.
        </p>
        <p style={{marginBottom:6}}>
          <a href="/about" style={{color:"#534AB7",marginRight:12,textDecoration:"none"}}>서비스 소개</a>
          <a href="/privacy.html" style={{color:"#534AB7",marginRight:12,textDecoration:"none"}}>개인정보처리방침</a>
          <a href="/terms.html" style={{color:"#534AB7",textDecoration:"none"}}>이용약관</a>
        </p>
        <p style={{fontSize:10,color:dark?"#444466":"#bbbbcc"}}>© 2026 메이플 파밍 트래커 · All rights reserved.</p>
      </div>
    </div>
  );
}
